import React, { useState, useEffect } from "react";
import axios from "axios";
import { PieChart, Pie, Cell, Tooltip } from 'recharts';

const API = "https://app.cueportfolio.com";

// Bump whenever the beta agreement text changes materially — recorded per
// acceptance so we know which version a given user actually agreed to.
// TODO(Francisco): replace BETA_AGREEMENT_TEXT below with the real text from
// Cue_Beta_Agreement_Draft.md once it's available as a plain file (still a
// Google Docs shortcut as of 2026-08-28 — see chat).
const BETA_AGREEMENT_VERSION = "mvp-2tier-2026-08-28";
const BETA_AGREEMENT_TEXT = `Cue MVP Beta Agreement

Thanks for trying Cue. A few things to know before you start:

1. This is a beta. There is no payment, subscription, or billing of any kind during the MVP period.
2. Cue provides general, educational market information. This is not personalized investment advice, and Cue is not a registered investment adviser.
3. Neither tier connects to a real brokerage account. Portfolio Optimizer works from a snapshot you upload (CSV, Excel, or a photo) — Cue never has trading access to your money.
4. Uploaded files (CSV/Excel/photos) are processed in memory to extract your holdings and are not stored afterward.
5. You can stop using Cue and delete your account at any time from account settings.

By continuing, you accept these terms for the MVP beta.`;

function CueGlyph({ size = 16, color = "#F2B655" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <polygon points="78,50 38,28 38,72" fill={color} />
      <circle cx="20" cy="50" r="9" fill={color} />
    </svg>
  );
}

function FlintLogo({ size = "md" }) {
  const sizes = { sm: { text: 16, dot: 6 }, md: { text: 20, dot: 8 }, lg: { text: 28, dot: 10 } };
  const s = sizes[size];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <CueGlyph size={s.dot * 2.2} />
      <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: s.text, fontWeight: 400, color: "var(--text-primary)", letterSpacing: "-0.3px", lineHeight: 1 }}>
        Cue
      </span>
    </div>
  );
}

function CueRating({ rating, size = 14 }) {
  // Finviz's analyst scale runs 1 (Strong Buy) to 5 (Strong Sell) — lower is
  // more bullish. Filling more icons for a *lower* raw number would read as
  // "worse" to anyone used to star ratings, so this inverts it: 5 filled
  // glyphs means the strongest buy conviction, matching normal intuition.
  const r = parseFloat(rating);
  if (isNaN(r)) return <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>{rating}</span>;
  const filled = Math.max(0, Math.min(5, Math.round(6 - r)));
  return (
    <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
      {[0, 1, 2, 3, 4].map(i => (
        <CueGlyph key={i} size={size} color={i < filled ? "var(--accent)" : "var(--bg-border)"} />
      ))}
    </div>
  );
}

function AIBadge({ size = "md" }) {
  const fs = size === "sm" ? 9.5 : 10.5;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: fs, fontWeight: 600, letterSpacing: "1px", textTransform: "uppercase", color: "var(--accent)", background: "var(--accent-dim)", border: "1px solid rgba(232,160,48,0.3)", borderRadius: 20, padding: "3px 10px" }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--accent)", flexShrink: 0 }} />
      AI Portfolio Strategist
    </span>
  );
}

const SECTOR_COLORS = {
  "Technology": "#7B61FF", "Technology ETF": "#7B61FF", "Healthcare": "#22C97A",
  "Financials": "#4A9EFF", "Financial Services": "#4A9EFF", "Energy": "#F5A623",
  "Industrials": "#FF6B6B", "Consumer Discretionary": "#FFD93D", "Consumer Staples": "#98D8C8",
  "Broad Market ETF": "#9CA3AF", "International ETF": "#C77DFF", "Fixed Income ETF": "#4ECDC4",
  "Emerging Markets": "#FF9A9E", "Materials": "#A8E6CF", "Real Estate": "#FFB347",
  "Utilities": "#87CEEB", "Communication": "#DDA0DD", "Other": "#6B7280",
};

function PickCard({ pick, niceToHave }) {
  const [expanded, setExpanded] = React.useState(false);
  const [showDetail, setShowDetail] = React.useState(false);
  return (
    <div onClick={() => setExpanded(!expanded)}
      style={{
        flex: expanded ? "1 1 100%" : "0 0 260px",
        maxWidth: expanded ? "100%" : 260,
        background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: "var(--radius-lg)",
        padding: 20, cursor: "pointer", transition: "border-color 0.15s, box-shadow 0.15s",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--accent)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.18)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--bg-border)"; e.currentTarget.style.boxShadow = "none"; }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <StockLogo ticker={pick.ticker} size={28} />
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-primary)" }}>{pick.ticker}</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>{pick.name}</div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
          {niceToHave && <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 20, background: "var(--bg-elevated)", color: "var(--text-muted)", fontWeight: 600, whiteSpace: "nowrap" }}>NICE TO HAVE</span>}
          {pick.fills_gap && <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 20, background: "var(--accent-dim)", color: "var(--accent)", fontWeight: 600, whiteSpace: "nowrap" }}>{pick.gap_sector}</span>}
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: expanded ? 14 : 0 }}>
        {pick.upside_to_target > 0 && <span style={{ fontSize: 10, padding: "3px 9px", borderRadius: 20, background: pick.upside_to_target > 15 ? "var(--gain-bg)" : "var(--info-bg)", color: pick.upside_to_target > 15 ? "var(--gain)" : "var(--info)", fontWeight: 500 }}>+{Number(pick.upside_to_target).toFixed(1)}% upside</span>}
        {pick.earnings_quality && pick.earnings_quality !== "unknown" && <span style={{ fontSize: 10, padding: "3px 9px", borderRadius: 20, background: pick.earnings_quality === "strong" ? "var(--gain-bg)" : "var(--neutral-bg)", color: pick.earnings_quality === "strong" ? "var(--gain)" : "var(--text-secondary)", fontWeight: 500 }}>{pick.earnings_quality} earnings</span>}
        {pick.institution_trend === "accumulating" && <span style={{ fontSize: 10, padding: "3px 9px", borderRadius: 20, background: "var(--gain-bg)", color: "var(--gain)", fontWeight: 500 }}>Institutions buying</span>}
        {pick.earnings_soon && pick.days_to_earnings > 0 && <span style={{ fontSize: 10, padding: "3px 9px", borderRadius: 20, background: "var(--warning-bg)", color: "var(--warning)", fontWeight: 500 }}>Earnings in {pick.days_to_earnings}d</span>}
      </div>
      {expanded && (
        <div style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.7, borderTop: "1px solid var(--bg-border)", paddingTop: 14, marginTop: 4 }}>
          {pick.reason}
          {pick.detail && (
            <div style={{ marginTop: 10 }}>
              <button onClick={e => { e.stopPropagation(); setShowDetail(!showDetail); }}
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 12, color: "var(--accent)", fontWeight: 600 }}>
                {showDetail ? "Hide details ▲" : "Find out more ▼"}
              </button>
              {showDetail && (
                <div style={{ marginTop: 10 }} onClick={e => e.stopPropagation()}>
                  <PickDetailPanel detail={pick.detail} />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InsightsPanel({ strategy, portfolioData, recommendations }) {
  const perf = strategy?.recommended_portfolio?.performance_comparison;
  const startValue = (portfolioData?.net_liquidation || 0) + (portfolioData?.available_cash || 0) || 100000;
  const currentRate = (perf?.current_portfolio_return_pct || 8) / 100;
  const optimizedRate = (perf?.recommended_portfolio_return_pct || 14) / 100;
  const isFreshStart = !portfolioData?.portfolio?.length && portfolioData?.available_cash > 0;

  const years = [1, 3, 5];
  const dollarTable = years.map(y => ({
    years: y,
    today: Math.round(startValue * Math.pow(1 + currentRate, y)),
    optimized: Math.round(startValue * Math.pow(1 + optimizedRate, y)),
    diff: Math.round(startValue * Math.pow(1 + optimizedRate, y) - startValue * Math.pow(1 + currentRate, y)),
  }));
  const fiveYearExtra = dollarTable[2].diff;

  const sectorAlloc = strategy?.sector_allocation || {};
  const beforeData = Object.entries(sectorAlloc).map(([s, v]) => ({ name: s, value: parseFloat(v) || 0, color: SECTOR_COLORS[s] || SECTOR_COLORS["Other"] })).filter(d => d.value > 0.5);

  const afterAlloc = { ...sectorAlloc };
  (strategy?.recommended_portfolio?.recommended_positions || []).forEach(pos => {
    if (["SELL","REDUCE"].includes(pos.action)) {
      const sector = pos.sector || "Other";
      afterAlloc[sector] = Math.max(0, (parseFloat(afterAlloc[sector]) || 0) - (pos.current_pct - pos.recommended_pct));
    }
    if (["BUY","INCREASE"].includes(pos.action) && pos.gap_sector) {
      afterAlloc[pos.gap_sector] = (parseFloat(afterAlloc[pos.gap_sector]) || 0) + pos.recommended_pct;
    }
  });
  const afterData = Object.entries(afterAlloc).map(([s, v]) => ({ name: s, value: parseFloat(v) || 0, color: SECTOR_COLORS[s] || SECTOR_COLORS["Other"] })).filter(d => d.value > 0.5);

  const tooltipStyle = { background: "var(--bg-elevated)", border: "1px solid var(--bg-border)", borderRadius: 8, fontSize: 12, color: "var(--text-primary)" };

  return (
    <section style={{ marginBottom: 40 }}>
      <h2 style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 20 }}>
        The transformation
      </h2>

      <div style={{ display: "grid", gridTemplateColumns: !isFreshStart ? "1fr 1fr" : "1fr", gap: 20 }}>
        {!isFreshStart && beforeData.length > 0 && (
          <div style={{ background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: "var(--radius-lg)", padding: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-secondary)", marginBottom: 16 }}>Sector allocation</div>
            <div style={{ display: "flex", gap: 16, justifyContent: "space-around" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>Before</div>
                <PieChart width={140} height={140}>
                  <Pie data={beforeData} cx={70} cy={70} innerRadius={35} outerRadius={60} paddingAngle={2} dataKey="value">
                    {beforeData.map((e, i) => <Cell key={i} fill={e.color} opacity={0.85} />)}
                  </Pie>
                  <Tooltip formatter={(v) => [v.toFixed(1) + "%"]} contentStyle={tooltipStyle} />
                </PieChart>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>After</div>
                <PieChart width={140} height={140}>
                  <Pie data={afterData} cx={70} cy={70} innerRadius={35} outerRadius={60} paddingAngle={2} dataKey="value">
                    {afterData.map((e, i) => <Cell key={i} fill={e.color} opacity={0.85} />)}
                  </Pie>
                  <Tooltip formatter={(v) => [v.toFixed(1) + "%"]} contentStyle={tooltipStyle} />
                </PieChart>
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 10px", marginTop: 8 }}>
              {beforeData.map((d, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}><div style={{ width: 7, height: 7, borderRadius: "50%", background: d.color }} /><span style={{ fontSize: 10, color: "var(--text-muted)" }}>{d.name}</span></div>)}
            </div>
          </div>
        )}

        <div style={{ background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: "var(--radius-lg)", padding: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-secondary)", marginBottom: 16 }}>Dollar impact</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["", "Today", "Optimized", "Difference"].map((h, i) => (
                  <th key={i} style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textAlign: i === 0 ? "left" : "right", padding: "4px 0", paddingBottom: 10, borderBottom: "1px solid var(--bg-border)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dollarTable.map((row, i) => (
                <tr key={i}>
                  <td style={{ fontSize: 13, color: "var(--text-secondary)", padding: "10px 0", borderBottom: "1px solid var(--bg-border-subtle)" }}>{row.years}yr</td>
                  <td style={{ fontSize: 13, color: "var(--text-primary)", textAlign: "right", padding: "10px 0", borderBottom: "1px solid var(--bg-border-subtle)", fontVariantNumeric: "tabular-nums" }}>${row.today.toLocaleString("en-US", { maximumFractionDigits: 0 })}</td>
                  <td style={{ fontSize: 13, fontWeight: 600, color: "var(--gain)", textAlign: "right", padding: "10px 0", borderBottom: "1px solid var(--bg-border-subtle)", fontVariantNumeric: "tabular-nums" }}>${row.optimized.toLocaleString("en-US", { maximumFractionDigits: 0 })}</td>
                  <td style={{ fontSize: 13, fontWeight: 600, color: "var(--accent)", textAlign: "right", padding: "10px 0", borderBottom: "1px solid var(--bg-border-subtle)", fontVariantNumeric: "tabular-nums" }}>+${row.diff.toLocaleString("en-US", { maximumFractionDigits: 0 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 20, textAlign: "center" }}>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 6 }}>Over 5 years, optimization could put an extra</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "var(--gain)", fontVariantNumeric: "tabular-nums" }}>+${fiveYearExtra.toLocaleString("en-US", { maximumFractionDigits: 0 })}</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>{isFreshStart ? "Your cash has a plan now." : "in your pocket."}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

const StockLogo = ({ ticker, size = 20 }) => {
  if (!ticker) return null;
  return (
    <img
      src={`https://financialmodelingprep.com/image-stock/${ticker}.png`}
      alt=""
      onError={e => { e.target.style.display = "none"; }}
      style={{ width: size, height: size, borderRadius: 4, background: "var(--bg-elevated)", objectFit: "contain", flexShrink: 0 }}
    />
  );
};

const Badge = ({ text, type }) => {
  const colors = {
    buy: { background: "var(--gain-bg)", color: "var(--gain)" },
    sell: { background: "var(--loss-bg)", color: "var(--loss)" },
    hold: { background: "var(--info-bg)", color: "var(--accent-hover)" },
    high: { background: "var(--gain-bg)", color: "var(--gain)" },
    medium: { background: "var(--warning-bg)", color: "var(--warning)" },
    low: { background: "var(--loss-bg)", color: "var(--loss)" },
    core: { background: "var(--accent-glow)", color: "var(--accent)" },
    satellite: { background: "var(--bg-elevated)", color: "var(--text-primary)" },
    risk: { background: "var(--warning-bg)", color: "var(--warning)" },
    default: { background: "var(--bg-elevated)", color: "var(--text-primary)" },
  };
  const style = colors[text?.toLowerCase()] || colors[type] || colors.default;
  return (
    <span style={{
      ...style, fontSize: 13, padding: "3px 8px", borderRadius: 20,
      fontWeight: 500, display: "inline-block", whiteSpace: "nowrap"
    }}>{text}</span>
  );
};

const Card = ({ title, subtitle, children }) => (
  <div style={{
    background: "var(--bg-surface)", border: "1px solid var(--bg-border)",
    borderRadius: 12, padding: 14, marginBottom: 12
  }}>
    {title && <div style={{ fontSize: 15, fontWeight: 500, marginBottom: subtitle ? 4 : 12 }}>{title}</div>}
    {subtitle && <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>{subtitle}</div>}
    {children}
  </div>
);

function FlintOfTheDayCard({ pick, onTestInOptimizer, onExpand }) {
  // No more expand/collapse — the full explanation is always visible, so
  // treat a render of this card as the "expanded" analytics moment.
  React.useEffect(() => { if (onExpand) onExpand(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={{
      background: "var(--bg-surface)", border: "1px solid var(--bg-border)",
      borderRadius: "var(--radius-lg)", padding: 20, marginBottom: 16
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 8 }}>
        Cue of the Day
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img
            src={`https://financialmodelingprep.com/image-stock/${pick.ticker}.png`}
            alt=""
            onError={e => { e.target.style.display = "none"; }}
            style={{ width: 38, height: 38, borderRadius: 8, background: "var(--bg-elevated)", objectFit: "contain", flexShrink: 0 }}
          />
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)" }}>{pick.ticker}</div>
              {pick.company_name && pick.company_name !== pick.ticker && (
                <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{pick.company_name}</div>
              )}
            </div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2 }}>{pick.headline}</div>
          </div>
        </div>
        {pick.upside_pct !== null && pick.upside_pct !== undefined
          ? <Badge text={`${pick.upside_pct >= 0 ? "+" : ""}${pick.upside_pct}% to target`} type="buy" />
          : <Badge text={pick.risk_flag} type="risk" />}
      </div>
      <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.7, marginBottom: 14 }}>
        {pick.why_today}
      </div>
      {onTestInOptimizer && (
        <button onClick={onTestInOptimizer} style={{ fontSize: 13, padding: "6px 12px", borderRadius: 8, border: "1px solid var(--bg-border)", background: "var(--bg-elevated)", color: "var(--text-primary)", cursor: "pointer", fontWeight: 500 }}>
          Test this in my portfolio
        </button>
      )}
    </div>
  );
}

function DigestSection({ digestContent, marketInsight, onViewInOptimizer }) {
  const alerts = digestContent?.alerts || [];
  const actionItems = digestContent?.action_items || [];
  const hasActionItems = actionItems.length > 0;
  if (!marketInsight && alerts.length === 0 && !hasActionItems) return null;
  return (
    <div style={{ background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: "var(--radius-lg)", padding: 20, marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 10 }}>
        Today's Digest
      </div>
      {marketInsight && (
        <div style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.7, marginBottom: hasActionItems || alerts.length > 0 ? 14 : 0 }}>
          {marketInsight}
        </div>
      )}
      {hasActionItems && (
        <div style={{ background: "var(--gain-bg)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: "var(--radius-md)", padding: "10px 14px", marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--gain)", marginBottom: 4 }}>Your portfolio needs a decision</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.7 }}>
            {actionItems.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
          {onViewInOptimizer && (
            <button onClick={onViewInOptimizer} style={{ marginTop: 8, fontSize: 12, padding: "5px 10px", borderRadius: 6, border: "1px solid var(--bg-border)", background: "var(--bg-elevated)", color: "var(--text-secondary)", cursor: "pointer" }}>
              Run analysis & review trades →
            </button>
          )}
        </div>
      )}
      {alerts.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.7 }}>
          {alerts.map((a, i) => <li key={i}><strong>{a.ticker}</strong>: {a.plain_english_summary}</li>)}
        </ul>
      )}
    </div>
  );
}

function SimpleLoadingBar({ label }) {
  const [pct, setPct] = React.useState(8);
  React.useEffect(() => {
    const t = setInterval(() => setPct(p => (p < 90 ? p + (90 - p) * 0.12 : p)), 300);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ textAlign: "center", padding: 60, maxWidth: 360, margin: "0 auto" }}>
      <div style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 14 }}>{label}</div>
      <div style={{ background: "var(--bg-elevated)", borderRadius: 8, height: 6, overflow: "hidden" }}>
        <div style={{ height: 6, borderRadius: 8, background: "linear-gradient(90deg, var(--accent), var(--info))", width: `${pct}%`, transition: "width 0.3s ease" }} />
      </div>
    </div>
  );
}

function UpgradeLockedPanel({ headline, body, bullets, onUpgrade }) {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px", maxWidth: 480, margin: "0 auto" }}>
      <div style={{ fontSize: 36, marginBottom: 14 }}>🔒</div>
      <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 10 }}>{headline}</div>
      <div style={{ fontSize: 14, color: "var(--text-muted)", lineHeight: 1.7, marginBottom: 20 }}>{body}</div>
      {bullets && bullets.length > 0 && (
        <ul style={{ textAlign: "left", listStyle: "none", padding: 0, margin: "0 0 26px", display: "inline-block" }}>
          {bullets.map((b, i) => (
            <li key={i} style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 8, display: "flex", gap: 8, alignItems: "flex-start" }}>
              <span style={{ color: "var(--accent)", fontWeight: 700, flexShrink: 0 }}>✓</span>{b}
            </li>
          ))}
        </ul>
      )}
      <div>
        <button onClick={onUpgrade} style={{ padding: "12px 28px", borderRadius: 10, border: "none", background: "var(--accent)", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
          Upgrade to Plus
        </button>
      </div>
    </div>
  );
}

function TickerSearchBar() {
  const [query, setQuery] = React.useState("");
  const [result, setResult] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [suggestions, setSuggestions] = React.useState([]);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [expandedNews, setExpandedNews] = React.useState(null);
  const suggestTimer = React.useRef(null);
  const boxRef = React.useRef(null);

  const runSearch = async (t) => {
    if (!t) return;
    setLoading(true);
    setError("");
    setResult(null);
    setExpandedNews(null);
    setShowSuggestions(false);
    try {
      const r = await axios.get(`${API}/api/ticker-search/${encodeURIComponent(t)}`);
      if (r.data.success) setResult(r.data);
      else setError(r.data.error || "Ticker not found");
    } catch (e) {
      setError("Search failed. Please try again.");
    }
    setLoading(false);
  };

  const onQueryChange = (val) => {
    const upper = val.toUpperCase();
    setQuery(upper);
    clearTimeout(suggestTimer.current);
    if (!upper.trim()) { setSuggestions([]); setShowSuggestions(false); return; }
    suggestTimer.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/ticker-suggest`, { params: { q: upper.trim() } });
        setSuggestions(r.data.results || []);
        setShowSuggestions(true);
      } catch (e) { /* suggestions are best-effort */ }
    }, 250);
  };

  React.useEffect(() => {
    const onClickOutside = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  return (
    <div ref={boxRef} style={{ background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: "var(--radius-lg)", padding: 20, marginBottom: 20, position: "relative" }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 12 }}>Look up any ticker</div>
      <div style={{ display: "flex", gap: 8, position: "relative" }}>
        <input value={query} onChange={e => onQueryChange(e.target.value)}
          onFocus={() => query.trim() && suggestions.length > 0 && setShowSuggestions(true)}
          onKeyDown={e => e.key === "Enter" && runSearch(query.trim())}
          placeholder="e.g. AAPL, TSLA, ARKK"
          style={{ flex: 1, padding: "10px 14px", borderRadius: 8, border: "1px solid var(--bg-border)", background: "var(--bg-elevated)", color: "var(--text-primary)", fontSize: 14 }} />
        <button onClick={() => runSearch(query.trim())} disabled={loading || !query.trim()}
          style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: "var(--accent)", color: "#fff", fontSize: 14, fontWeight: 600, cursor: loading ? "default" : "pointer", opacity: loading ? 0.7 : 1 }}>
          {loading ? "..." : "Search"}
        </button>
        {showSuggestions && suggestions.length > 0 && (
          <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 88, background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.3)", zIndex: 300, overflow: "hidden" }}>
            {suggestions.map((s, i) => (
              <div key={i} onClick={() => { setQuery(s.ticker); runSearch(s.ticker); }}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", cursor: "pointer", borderBottom: i < suggestions.length - 1 ? "1px solid var(--bg-border-subtle)" : "none" }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--bg-elevated)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <StockLogo ticker={s.ticker} size={20} />
                <span style={{ fontSize: 14, fontWeight: 600, minWidth: 60 }}>{s.ticker}</span>
                <span style={{ fontSize: 13, color: "var(--text-muted)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</span>
                {s.is_etf && <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 10, background: "var(--bg-elevated)", color: "var(--text-muted)", flexShrink: 0 }}>ETF</span>}
                <span style={{ fontSize: 11, color: "var(--text-muted)", flexShrink: 0 }}>{s.exchange}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {error && <div style={{ marginTop: 12, fontSize: 13, color: "var(--loss)" }}>{error}</div>}
      {result && (
        <div style={{ marginTop: 16, padding: 16, borderRadius: "var(--radius-md)", background: "var(--bg-elevated)", border: "1px solid var(--bg-border)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <StockLogo ticker={result.ticker} size={32} />
            <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontSize: 18, fontWeight: 700 }}>{result.ticker}</span>
                <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{result.name}</span>
                {result.is_etf && <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 10, background: "var(--bg-surface)", color: "var(--text-muted)" }}>ETF</span>}
              </div>
              {result.sector && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{result.sector}</div>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: result.news?.length ? 12 : 0 }}>
            <div>
              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Price</div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>${result.current_price}
                {result.day_change_pct !== null && result.day_change_pct !== undefined && (
                  <span style={{ fontSize: 13, fontWeight: 600, marginLeft: 6, color: result.day_change_pct >= 0 ? "var(--gain)" : "var(--loss)" }}>
                    {result.day_change_pct >= 0 ? "+" : ""}{result.day_change_pct}%
                  </span>
                )}
              </div>
            </div>
            {result.is_etf ? (
              <>
                {result.momentum_1mo !== undefined && result.momentum_1mo !== null && (
                  <div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>1mo momentum</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: result.momentum_1mo >= 0 ? "var(--gain)" : "var(--loss)" }}>{result.momentum_1mo >= 0 ? "+" : ""}{result.momentum_1mo}%</div>
                  </div>
                )}
                {result.momentum_3mo !== undefined && result.momentum_3mo !== null && (
                  <div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>3mo momentum</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: result.momentum_3mo >= 0 ? "var(--gain)" : "var(--loss)" }}>{result.momentum_3mo >= 0 ? "+" : ""}{result.momentum_3mo}%</div>
                  </div>
                )}
              </>
            ) : (
              <>
                {result.target_price && (
                  <div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Analyst target</div>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>${result.target_price}
                      {result.upside_pct !== null && result.upside_pct !== undefined && (
                        <span style={{ fontSize: 13, fontWeight: 600, marginLeft: 6, color: result.upside_pct >= 0 ? "var(--gain)" : "var(--loss)" }}>
                          {result.upside_pct >= 0 ? "+" : ""}{result.upside_pct}%
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {result.analyst_rating && (
                  <div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Analyst rating</div>
                    <div style={{ marginTop: 3 }}><CueRating rating={result.analyst_rating} /></div>
                  </div>
                )}
              </>
            )}
            {result.fifty_two_week_low && result.fifty_two_week_high && (
              <div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>52-week range</div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>${result.fifty_two_week_low} – ${result.fifty_two_week_high}</div>
              </div>
            )}
          </div>
          {result.news && result.news.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 6 }}>Recent news</div>
              {result.news.map((n, i) => (
                <div key={i} style={{ padding: "6px 0", borderBottom: i < result.news.length - 1 ? "1px solid var(--bg-border-subtle)" : "none" }}>
                  <div onClick={() => setExpandedNews(expandedNews === i ? null : i)}
                    style={{ fontSize: 13, color: "var(--text-secondary)", cursor: "pointer", display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span>{n.title}</span>
                    <span style={{ color: "var(--text-muted)", flexShrink: 0 }}>{expandedNews === i ? "▲" : "▼"}</span>
                  </div>
                  {expandedNews === i && (
                    <div style={{ marginTop: 6, fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6 }}>
                      {n.description || "No summary available."}
                      {n.url && (
                        <div style={{ marginTop: 6 }}>
                          <a href={n.url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)", fontSize: 12 }}>Read full article →</a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MarketFlintTab({ featureFlags, flintPick, digestContent, onTestInOptimizer, onFlintExpand }) {
  const [marketData, setMarketData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/market-flint`).then(r => setMarketData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const fg = marketData?.fear_greed;
  const sr = marketData?.sector_rotation || {};
  const sectorData = sr.sectors || {};
  const allSectors = Object.entries(sectorData)
    .map(([name, sd]) => ({ name, ...sd }))
    .sort((a, b) => (b.momentum_1mo || 0) - (a.momentum_1mo || 0));
  const maxAbsMomentum = Math.max(1, ...allSectors.map(s => Math.abs(s.momentum_1mo || 0)));
  const headlines = marketData?.market_headlines || [];

  const showFlint = featureFlags?.flint_of_day && flintPick;
  const showDigest = featureFlags?.daily_digest && (digestContent || marketData?.market_summary);

  return (
    <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 4px" }}>
      <div style={{ padding: "16px 16px 0" }}>
        <TickerSearchBar />
      </div>

      {loading && <SimpleLoadingBar label="Loading market overview..." />}

      {!loading && showDigest && (
        <div style={{ padding: "0 16px" }}>
          <DigestSection
            digestContent={digestContent}
            marketInsight={marketData?.market_summary}
            onViewInOptimizer={onTestInOptimizer}
          />
        </div>
      )}

      {!loading && marketData && (
        <div style={{ padding: "0 16px" }}>
          {fg && (
            <div style={{ background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: "var(--radius-lg)", padding: 24, marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Fear &amp; Greed Index</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{fg.trend === "rising" ? "↑ rising" : "↓ falling"} vs last week</div>
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 40, fontWeight: 700, color: fg.score <= 40 ? "var(--loss)" : fg.score >= 60 ? "var(--gain)" : "var(--warning)" }}>{fg.score}</div>
                <div style={{ fontSize: 17, color: "var(--text-secondary)" }}>{fg.rating}</div>
              </div>
              <div style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.6 }}>{fg.market_timing}</div>
            </div>
          )}
        </div>
      )}

      {showFlint && (
        <div style={{ padding: "0 16px" }}>
          <FlintOfTheDayCard pick={flintPick} onTestInOptimizer={onTestInOptimizer} onExpand={onFlintExpand} />
        </div>
      )}

      {!loading && marketData && (
        <div style={{ padding: "0 16px 16px" }}>
          {allSectors.length > 0 && (
            <div style={{ background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: "var(--radius-lg)", padding: 24, marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 16 }}>Sector momentum (1mo)</div>
              {allSectors.map(s => {
                const m = s.momentum_1mo || 0;
                const positive = m >= 0;
                const barPct = Math.min(100, Math.abs(m) / maxAbsMomentum * 100);
                return (
                  <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                    <div style={{ width: 170, fontSize: 13, color: "var(--text-secondary)", flexShrink: 0 }}>{s.name}</div>
                    <div style={{ flex: 1, height: 8, background: "var(--bg-elevated)", borderRadius: 4, position: "relative" }}>
                      <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: 1, background: "var(--bg-border)" }} />
                      <div style={{
                        position: "absolute", top: 0, bottom: 0,
                        [positive ? "left" : "right"]: "50%",
                        width: `${barPct / 2}%`,
                        background: positive ? "var(--gain)" : "var(--loss)",
                        borderRadius: 4,
                      }} />
                    </div>
                    <div style={{ width: 56, textAlign: "right", fontSize: 13, fontWeight: 600, color: positive ? "var(--gain)" : "var(--loss)", flexShrink: 0 }}>
                      {positive ? "+" : ""}{m.toFixed(1)}%
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {headlines.length > 0 && (
            <div style={{ background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: "var(--radius-lg)", padding: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 12 }}>Market headlines</div>
              {headlines.map((h, i) => (
                <a key={i} href={h.url} target="_blank" rel="noopener noreferrer"
                  style={{ display: "block", padding: "10px 0", borderBottom: i < headlines.length - 1 ? "1px solid var(--bg-border-subtle)" : "none", textDecoration: "none" }}
                  onMouseEnter={e => e.currentTarget.querySelector(".hl-title").style.color = "var(--accent)"}
                  onMouseLeave={e => e.currentTarget.querySelector(".hl-title").style.color = "var(--text-primary)"}>
                  <div className="hl-title" style={{ fontSize: 14, color: "var(--text-primary)", lineHeight: 1.5, marginBottom: 4, transition: "color 0.15s" }}>{h.title}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{h.source}{h.published ? ` · ${h.published}` : ""}</div>
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const TIER_COLORS = {
  breaking: { background: "var(--loss-bg)", color: "var(--loss)" },
  digest: { background: "var(--warning-bg)", color: "var(--warning)" },
  fyi: { background: "var(--bg-elevated)", color: "var(--text-muted)" },
};

function NotificationBell({ alerts, unreadCount, onOpen, onMarkRead }) {
  const [open, setOpen] = React.useState(false);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && onOpen) onOpen();
  };
  return (
    <div style={{ position: "relative" }}>
      <button onClick={toggle} style={{ position: "relative", background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: 8, width: 32, height: 32, cursor: "pointer", fontSize: 15, color: "var(--text-primary)" }}>
        🔔
        {unreadCount > 0 && (
          <span style={{ position: "absolute", top: -4, right: -4, background: "var(--loss)", color: "#fff", fontSize: 10, fontWeight: 700, borderRadius: 20, minWidth: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px" }}>
            {unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div style={{ position: "absolute", top: 38, right: 0, width: 320, maxHeight: 400, overflowY: "auto", background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.3)", zIndex: 200 }}>
          {(!alerts || alerts.length === 0) && (
            <div style={{ padding: 16, fontSize: 13, color: "var(--text-muted)" }}>No alerts yet.</div>
          )}
          {alerts && alerts.map(a => (
            <div key={a.id} onClick={() => !a.read && onMarkRead(a.id)}
              style={{ padding: 12, borderBottom: "1px solid var(--bg-border-subtle)", cursor: a.read ? "default" : "pointer", opacity: a.read ? 0.6 : 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{a.ticker}</span>
                <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 20, fontWeight: 600, ...(TIER_COLORS[a.tier] || TIER_COLORS.fyi) }}>{a.tier}</span>
              </div>
              <div style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5 }}>{a.plain_english_summary}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WatchlistPanel({ tickers, onAdd, onRemove }) {
  const [open, setOpen] = React.useState(false);
  const [input, setInput] = React.useState("");
  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setOpen(!open)} style={{ fontSize: 13, padding: "4px 10px", borderRadius: 6, border: "1px solid var(--bg-border)", background: "var(--bg-surface)", cursor: "pointer", color: "var(--text-secondary)" }}>
        Watchlist {tickers?.length > 0 ? `(${tickers.length})` : ""}
      </button>
      {open && (
        <div style={{ position: "absolute", top: 32, right: 0, width: 240, background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.3)", zIndex: 200, padding: 12 }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            <input value={input} onChange={e => setInput(e.target.value.toUpperCase())}
              onKeyDown={e => { if (e.key === "Enter" && input.trim()) { onAdd(input.trim()); setInput(""); } }}
              placeholder="Ticker" style={{ flex: 1, fontSize: 13, padding: "6px 8px", borderRadius: 6, border: "1px solid var(--bg-border)", background: "var(--bg-elevated)", color: "var(--text-primary)" }} />
            <button onClick={() => { if (input.trim()) { onAdd(input.trim()); setInput(""); } }}
              style={{ fontSize: 13, padding: "6px 10px", borderRadius: 6, border: "none", background: "var(--accent)", color: "#fff", cursor: "pointer" }}>Add</button>
          </div>
          {(!tickers || tickers.length === 0) && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>No tickers watched yet.</div>}
          {tickers && tickers.map(t => (
            <div key={t} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", fontSize: 13 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}><StockLogo ticker={t} size={16} />{t}</span>
              <button onClick={() => onRemove(t)} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 13 }}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FeatureOnboardingModal({ onDismiss }) {
  const [dontShowAgain, setDontShowAgain] = React.useState(true);
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 28, width: 440, boxShadow: "0 8px 40px rgba(0,0,0,0.2)" }}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>New in your header</div>
        <div style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 20, lineHeight: 1.6 }}>
          Two new tools just showed up to help you stay on top of tickers you care about.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 24 }}>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ fontSize: 20 }}>👁️</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>Watchlist</div>
              <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5 }}>Add any ticker you want to keep an eye on — even ones you don't hold. We monitor it in the background.</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ fontSize: 20 }}>🔔</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>Notification bell</div>
              <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5 }}>Get alerted here when a watched ticker sees an analyst rating change, unusual volume, upcoming earnings, or breaking news.</div>
            </div>
          </div>
        </div>
        <button onClick={() => onDismiss(dontShowAgain)} style={{ width: "100%", padding: "11px", borderRadius: 8, border: "none", background: "var(--accent)", color: "#000", fontSize: 14, fontWeight: 600, cursor: "pointer", marginBottom: 12 }}>
          Got it
        </button>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-muted)", cursor: "pointer" }}>
          <input type="checkbox" checked={dontShowAgain} onChange={e => setDontShowAgain(e.target.checked)} />
          Don't show this again
        </label>
      </div>
    </div>
  );
}

function SignalAgreement({ signals, bullishCount, bearishCount }) {
  if (!signals) return null;
  const overall = bullishCount > bearishCount ? "bullish" : bearishCount > bullishCount ? "bearish" : "mixed";
  const overallColor = overall === "bullish" ? "var(--gain)" : overall === "bearish" ? "var(--loss)" : "var(--warning)";
  const overallBg = overall === "bullish" ? "var(--gain-bg)" : overall === "bearish" ? "var(--loss-bg)" : "var(--warning-bg)";
  const signalOrder = ["analysts", "momentum", "news", "insiders", "trends"];
  const signalLabels = { analysts: "Analysts", momentum: "Momentum", news: "News", insiders: "Insiders", trends: "Trends" };

  return (
    <div style={{ marginTop: 8, padding: "8px 10px", background: "var(--bg-surface)", borderRadius: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>Signal agreement</div>
        <span style={{ fontSize: 12, padding: "1px 7px", borderRadius: 10, background: overallBg, color: overallColor, fontWeight: 500 }}>
          {bullishCount}/5 bullish
        </span>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        {signalOrder.map(key => {
          const s = signals[key];
          if (!s) return null;
          const dir = s.direction;
          const dotColor = dir === "bullish" ? "var(--gain)" : dir === "bearish" ? "var(--loss)" : "#ccc";
          const bgColor = dir === "bullish" ? "var(--gain-bg)" : dir === "bearish" ? "var(--loss-bg)" : "var(--bg-primary)";
          const textColor = dir === "bullish" ? "var(--gain)" : dir === "bearish" ? "var(--loss)" : "#999";
          return (
            <div key={key} style={{ flex: 1, textAlign: "center" }} title={s.detail}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: dotColor, margin: "0 auto 3px" }} />
              <div style={{ fontSize: 11, color: textColor, background: bgColor, borderRadius: 4, padding: "1px 3px", lineHeight: 1.4 }}>
                {signalLabels[key]}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const PickRow = ({ ticker, name, reason, meta, analystRating, upsideToTarget, newsSentiment, momentumScore, fillsGap, gapSector, addsToOverweight, actionRecommendation, signalData }) => {
  const sentimentColor = {
    positive: { background: "var(--gain-bg)", color: "var(--gain)" },
    neutral: { background: "var(--bg-elevated)", color: "var(--text-primary)" },
    negative: { background: "var(--loss-bg)", color: "var(--loss)" }
  };
  const sentStyle = sentimentColor[newsSentiment] || sentimentColor.neutral;
  const isReplace = actionRecommendation?.toLowerCase().startsWith("consider replacing");
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 0", borderBottom: "1px solid var(--bg-border)" }}>
      <div style={{ minWidth: 48 }}>
        <div style={{ fontSize: 15, fontWeight: 500 }}>{ticker}</div>
        <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 1 }}>{name}</div>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 6 }}>{reason}</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
          {analystRating && (() => {
            const r = parseFloat(analystRating);
            const label = r <= 1.5 ? "Strong Buy" : r <= 2.0 ? "Buy" : r <= 2.5 ? "Hold" : r <= 3.5 ? "Underperform" : "Sell";
            const style = r <= 1.5 ? { background: "var(--gain-bg)", color: "var(--gain)" } :
              r <= 2.0 ? { background: "var(--info-bg)", color: "var(--accent-hover)" } :
              r <= 2.5 ? { background: "var(--bg-elevated)", color: "var(--text-primary)" } :
              { background: "var(--loss-bg)", color: "var(--loss)" };
            return <span style={{ fontSize: 12, ...style, padding: "2px 6px", borderRadius: 4 }}>analysts: {label} ({analystRating})</span>;
          })()}
          {upsideToTarget !== undefined && upsideToTarget !== null && (
            <span style={{
              fontSize: 12,
              background: upsideToTarget > 15 ? "var(--gain-bg)" : upsideToTarget > 5 ? "var(--info-bg)" : upsideToTarget > 0 ? "var(--warning-bg)" : "var(--loss-bg)",
              color: upsideToTarget > 15 ? "var(--gain)" : upsideToTarget > 5 ? "var(--accent-hover)" : upsideToTarget > 0 ? "var(--warning)" : "var(--loss)",
              padding: "2px 6px", borderRadius: 4, fontWeight: 500
            }}>{upsideToTarget > 0 ? "+" : ""}{upsideToTarget}% to target</span>
          )}
          {newsSentiment && <span style={{ fontSize: 12, ...sentStyle, padding: "2px 6px", borderRadius: 4 }}>news: {newsSentiment}</span>}
          {fillsGap && gapSector && <span style={{ fontSize: 12, background: "var(--accent-glow)", color: "var(--accent)", padding: "2px 6px", borderRadius: 4, fontWeight: 500 }}>fills gap: {gapSector}</span>}
          {addsToOverweight && <span style={{ fontSize: 12, background: "var(--warning-bg)", color: "var(--warning)", padding: "2px 6px", borderRadius: 4 }}>adds to overweight</span>}
        </div>
        {momentumScore !== undefined && momentumScore !== null && (
          <div style={{ marginBottom: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ fontSize: 12, color: "var(--text-muted)", minWidth: 60 }}>Momentum</div>
              <div style={{ flex: 1, height: 4, background: "var(--bg-primary)", borderRadius: 2 }}>
                <div style={{ height: 4, borderRadius: 2, width: `${momentumScore}%`, background: momentumScore >= 70 ? "var(--gain)" : momentumScore >= 50 ? "var(--warning)" : "var(--loss)" }} />
              </div>
              <div style={{ fontSize: 12, fontWeight: 500, minWidth: 28, textAlign: "right", color: momentumScore >= 70 ? "var(--gain)" : momentumScore >= 50 ? "var(--warning)" : "var(--loss)" }}>{momentumScore}/100</div>
            </div>
          </div>
        )}
        {signalData && (
          <SignalAgreement
            signals={signalData.signals}
            bullishCount={signalData.bullish_count}
            bearishCount={signalData.bearish_count}
          />
        )}
        {actionRecommendation && (
          <div style={{ marginTop: 6, padding: "6px 8px", borderRadius: 6, fontSize: 13, lineHeight: 1.5, background: isReplace ? "var(--loss-bg)" : "var(--gain-bg)", color: isReplace ? "var(--loss)" : "var(--gain)" }}>
            {actionRecommendation}
          </div>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
        {meta.map((m, i) => <Badge key={i} text={m} />)}
      </div>
    </div>
  );
};

function TradeButton({ ticker, action, price, currentShares, suggestedShares, suggestionReason, onExecute, compact }) {
  const [expanded, setExpanded] = useState(false);
  const isSell = ["SELL", "REDUCE"].includes(action);
  const maxShares = isSell ? Math.floor(currentShares || 0) : 99999;
  const defaultShares = isSell ? Math.floor(currentShares || 0) : Math.max(1, Math.floor(suggestedShares || 1));
  const [shares, setShares] = useState(defaultShares);
  const total = shares * (price || 0);

  if (!expanded) return (
    <button onClick={() => setExpanded(true)} style={{
      marginTop: compact ? 4 : 8, marginBottom: compact ? 0 : 4,
      display: compact ? "block" : "inline-block",
      width: compact ? "100%" : "auto",
      padding: compact ? "3px 0" : "4px 14px",
      borderRadius: compact ? 4 : 6, border: "none",
      background: isSell ? "var(--loss)" : "var(--accent)",
      color: "#fff", fontSize: compact ? 10 : 11,
      cursor: "pointer", fontWeight: 500
    }}>
      {isSell ? "Execute" : "Buy"}
    </button>
  );

  return (
    <div style={{ marginTop: compact ? 6 : 8, marginBottom: compact ? 0 : 4, padding: compact ? 8 : 12, background: "var(--bg-surface)", borderRadius: compact ? 6 : 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <div>
          <div style={{ fontSize: compact ? 10 : 11, fontWeight: 500, color: "var(--text-primary)", marginBottom: 1 }}>
            {isSell ? `Sell ${ticker}` : `Buy ${ticker}`}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            ${price > 0 ? price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"} per share
            {isSell && currentShares > 0 && <span style={{ color: "var(--loss)" }}> · {Math.floor(currentShares)} shares held</span>}
          </div>
        </div>
        <button onClick={() => setExpanded(false)} style={{ padding: "2px 6px", borderRadius: 4, border: "1px solid var(--bg-border)", background: "var(--bg-surface)", fontSize: 12, cursor: "pointer", color: "var(--text-muted)" }}>✕</button>
      </div>
      {!isSell && suggestionReason && (
        <div style={{ fontSize: 12, color: "var(--accent)", background: "var(--accent-glow)", padding: "5px 8px", borderRadius: 6, marginBottom: 8, lineHeight: 1.5 }}>
          💡 {suggestionReason}
        </div>
      )}
      {isSell && (
        <div style={{ fontSize: 12, color: "var(--loss)", background: "var(--loss-bg)", padding: "5px 8px", borderRadius: 6, marginBottom: 8, lineHeight: 1.5 }}>
          ⚠️ This will sell your entire position unless you reduce the share count below.
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: compact ? 4 : 8, marginBottom: 8 }}>
        <div style={{ fontSize: compact ? 10 : 11, color: "var(--text-secondary)" }}>Shares:</div>
        <button onClick={() => setShares(s => Math.max(1, s - 1))} style={{ width: compact ? 22 : 28, height: compact ? 22 : 28, borderRadius: 6, border: "1px solid var(--bg-border)", background: "var(--bg-surface)", cursor: "pointer", fontSize: 16, color: "var(--text-primary)", display: "flex", alignItems: "center", justifyContent: "center" }}>−</button>
        <input type="number" value={shares} min={1} max={maxShares}
          onChange={e => setShares(Math.min(maxShares, Math.max(1, Math.floor(Number(e.target.value)))))}
          style={{ width: compact ? 44 : 60, padding: compact ? "3px 4px" : "5px 8px", borderRadius: 6, textAlign: "center", border: "1px solid var(--bg-border)", fontSize: compact ? 11 : 13, fontWeight: 500, outline: "none", background: "var(--bg-surface)" }}
        />
        <button onClick={() => setShares(s => Math.min(maxShares, s + 1))} style={{ width: compact ? 22 : 28, height: compact ? 22 : 28, borderRadius: 6, border: "1px solid var(--bg-border)", background: "var(--bg-surface)", cursor: "pointer", fontSize: 16, color: "var(--text-primary)", display: "flex", alignItems: "center", justifyContent: "center" }}>+</button>
        <div style={{ fontSize: compact ? 10 : 12, color: isSell ? "var(--loss)" : "var(--accent)", fontWeight: 500 }}>
          {isSell ? "+" : "−"}${total.toLocaleString("en-US", { maximumFractionDigits: 0 })}
        </div>
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        <button onClick={() => setExpanded(false)} style={{ flex: 1, padding: compact ? "3px 0" : "6px 0", borderRadius: 6, border: "1px solid var(--bg-border)", background: "var(--bg-surface)", fontSize: compact ? 10 : 11, cursor: "pointer", color: "var(--text-muted)" }}>Cancel</button>
        <button onClick={() => onExecute(ticker, action, total, price)} style={{ flex: 2, padding: compact ? "3px 0" : "6px 0", borderRadius: 6, border: "none", background: isSell ? "var(--loss)" : "var(--accent)", color: "#fff", fontSize: compact ? 10 : 11, cursor: "pointer", fontWeight: 500 }}>
          Preview — {shares} share{shares !== 1 ? "s" : ""}
        </button>
      </div>
    </div>
  );
}

function MarketTab({ data, loading, onRefresh, hasPortfolioCache, onBuy }) {
  if (loading) return (
    <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>
      Fetching market data and running AI analysis...<br />
      <span style={{ fontSize: 14 }}>This takes about 4-5 minutes including portfolio gap analysis</span>
    </div>
  );
  if (!data) return (
    <div style={{ textAlign: "center", padding: 60 }}>
      {!hasPortfolioCache && (
        <div style={{ marginBottom: 16, padding: "12px 16px", background: "var(--warning-bg)", borderRadius: 8, fontSize: 14, color: "var(--warning)", maxWidth: 420, margin: "0 auto 16px" }}>
          For fully aligned recommendations, run <strong>Portfolio Strategy</strong> tab first.
        </div>
      )}
      <button onClick={() => onRefresh(false)} style={{ padding: "10px 24px", borderRadius: 8, border: "1px solid var(--bg-border)", cursor: "pointer", fontSize: 16, background: "var(--bg-surface)" }}>
        Run today's analysis
      </button>
    </div>
  );
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
        <Card title="Today's stock picks">
          {data.stock_picks?.map((p, i) => (
            <div key={i}>
              <PickRow
                ticker={p.ticker} name={p.name} reason={p.reason}
                meta={["BUY", `confidence: ${p.confidence}`, `horizon: ${p.time_horizon}`]}
                analystRating={p.analyst_rating} upsideToTarget={p.upside_to_target}
                newsSentiment={p.news_sentiment} momentumScore={p.momentum_score}
                fillsGap={p.fills_gap}
                gapSector={p.gap_sector} addsToOverweight={p.adds_to_overweight}
                actionRecommendation={p.action_recommendation}
                signalData={p.signal_convergence} />
              {onBuy && (
                <TradeButton
                  ticker={p.ticker} action="BUY"
                  price={p.current_price || 0}
                  suggestedShares={p.current_price > 0 ? Math.max(1, Math.floor((p.suggested_investment || 5000) / p.current_price)) : 1}
                  suggestionReason={p.suggestion_reason}
                  onExecute={onBuy} compact={false} />
              )}
            </div>
          ))}
        </Card>
        <div>
          <Card title="ETF picks">
            {data.etf_picks?.map((p, i) => (
              <PickRow key={i} ticker={p.ticker} name={p.name} reason={p.reason}
                meta={[p.portfolio_role === "core" ? "Core" : "Satellite"]}
                fillsGap={p.fills_gap} gapSector={p.gap_sector}
                actionRecommendation={p.action_recommendation} />
            ))}
          </Card>
          <Card title="Sectors to watch">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {data.sectors_to_watch?.map((s, i) => <Badge key={i} text={s} />)}
            </div>
          </Card>
        </div>
      </div>
      {data.portfolio_alignment && (
        <Card title="Portfolio alignment">
          <div style={{ background: "var(--accent-glow)", borderRadius: 8, padding: 10, fontSize: 14, color: "var(--accent)", lineHeight: 1.7 }}>{data.portfolio_alignment}</div>
        </Card>
      )}
      <Card title="Market summary">
        <div style={{ background: "var(--bg-surface)", borderRadius: 8, padding: 10, fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.7 }}>{data.market_summary}</div>
      </Card>
    </div>
  );
}

function SectorBar({ label, actual, target }) {
  const over = actual > target + 5;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
      <div style={{ fontSize: 13, color: "var(--text-secondary)", minWidth: 140 }}>{label}</div>
      <div style={{ flex: 1, position: "relative", height: 14 }}>
        <div style={{ position: "absolute", top: 4, left: 0, right: 0, height: 6, background: "var(--bg-primary)", borderRadius: 3 }} />
        <div style={{ position: "absolute", top: 4, left: 0, width: `${Math.min(actual, 100)}%`, height: 6, background: over ? "var(--loss)" : "var(--info)", borderRadius: 3 }} />
        {target > 0 && <div style={{ position: "absolute", top: 0, left: `${target}%`, width: 2, height: 14, background: "var(--text-muted)", borderRadius: 1 }} />}
      </div>
      <div style={{ fontSize: 13, minWidth: 72, textAlign: "right", color: over ? "var(--loss)" : "#555" }}>{actual}% / {target}%</div>
    </div>
  );
}

function OrderModal({ order, onConfirm, onCancel, placing, instruction }) {
  if (!order) return null;
  const isSell = order.ibkr_action === "SELL";

  // Instruction mode — order is queued, user opens IBKR to confirm
  if (instruction) {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 24, width: 420, boxShadow: "0 8px 40px rgba(0,0,0,0.2)" }}>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>✓</div>
            <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 6 }}>Order instruction created</div>
            <div style={{ fontSize: 14, color: "var(--text-muted)", lineHeight: 1.6 }}>
              Your {order.ibkr_action} for <strong>{order.shares} share{order.shares !== 1 ? "s" : ""} of {order.ticker}</strong> is ready.
              Open IBKR to review and confirm — nothing executes until you approve it there.
            </div>
          </div>
          <a href={instruction.confirm_url} target="_blank" rel="noopener noreferrer"
            style={{ display: "block", textAlign: "center", padding: "13px", borderRadius: 8, background: "var(--accent)", color: "#fff", fontSize: 15, fontWeight: 600, textDecoration: "none", marginBottom: 10 }}>
            Open in IBKR to confirm →
          </a>
          <button onClick={onCancel} style={{ width: "100%", padding: "10px", borderRadius: 8, border: "1px solid var(--bg-border)", background: "var(--bg-surface)", cursor: "pointer", fontSize: 14, color: "var(--text-secondary)" }}>
            Done
          </button>
        </div>
      </div>
    );
  }

  // Preview mode — user reviews before sending to IBKR
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 24, width: 420, boxShadow: "0 8px 40px rgba(0,0,0,0.2)" }}>
        <div style={{ fontSize: 17, fontWeight: 500, marginBottom: 4 }}>Review order</div>
        <div style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 20 }}>You'll confirm this in IBKR before it executes</div>
        <div style={{ background: "var(--bg-surface)", borderRadius: 10, padding: 14, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 500 }}>{order.ticker}</div>
              <div style={{ fontSize: 14, color: "var(--text-muted)" }}>Market order · DAY</div>
            </div>
            <span style={{ fontSize: 15, fontWeight: 500, padding: "4px 12px", borderRadius: 6, background: isSell ? "var(--loss-bg)" : "var(--gain-bg)", color: isSell ? "var(--loss)" : "var(--gain)", alignSelf: "flex-start" }}>{order.ibkr_action}</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div style={{ background: "var(--bg-surface)", borderRadius: 8, padding: 10 }}>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 2 }}>Shares</div>
              <div style={{ fontSize: 18, fontWeight: 500 }}>{order.shares}</div>
            </div>
            <div style={{ background: "var(--bg-surface)", borderRadius: 8, padding: 10 }}>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 2 }}>Current price</div>
              <div style={{ fontSize: 18, fontWeight: 500 }}>${order.current_price}</div>
            </div>
            <div style={{ background: "var(--bg-surface)", borderRadius: 8, padding: 10 }}>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 2 }}>Est. {isSell ? "proceeds" : "cost"}</div>
              <div style={{ fontSize: 18, fontWeight: 500, color: isSell ? "var(--gain)" : "var(--accent)" }}>
                {isSell ? "+" : "-"}${order.estimated_value.toLocaleString("en-US", { maximumFractionDigits: 0 })}
              </div>
            </div>
            <div style={{ background: "var(--bg-surface)", borderRadius: 8, padding: 10 }}>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 2 }}>Confirmation</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: "var(--accent)" }}>Via IBKR app</div>
            </div>
          </div>
        </div>
        <div style={{ background: "var(--info-bg)", borderRadius: 8, padding: 10, marginBottom: 16, fontSize: 13, color: "var(--accent-hover)", lineHeight: 1.6 }}>
          This sends an instruction to IBKR. You'll get a link to open in the IBKR app or web to confirm before it executes.
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onCancel} disabled={placing} style={{ flex: 1, padding: "10px", borderRadius: 8, border: "1px solid var(--bg-border)", background: "var(--bg-surface)", cursor: "pointer", fontSize: 15 }}>Cancel</button>
          <button onClick={onConfirm} disabled={placing} style={{ flex: 1, padding: "10px", borderRadius: 8, border: "none", background: placing ? "#ccc" : isSell ? "var(--loss)" : "var(--accent)", color: "#fff", cursor: placing ? "default" : "pointer", fontSize: 15, fontWeight: 500 }}>
            {placing ? "Sending to IBKR..." : `Send ${order.ibkr_action} instruction`}
          </button>
        </div>
      </div>
    </div>
  );
}

function StrategyTab({ strategy, loading, onFetch, onExecute }) {
  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>Analyzing your portfolio...<br /><span style={{ fontSize: 14 }}>Fetching positions, checking signals, generating recommendations</span></div>;
  if (!strategy) return (
    <div style={{ textAlign: "center", padding: 60 }}>
      <div style={{ marginBottom: 12, fontSize: 14, color: "var(--text-muted)" }}>Run this first to align all recommendations across the platform.</div>
      <button onClick={onFetch} style={{ padding: "10px 24px", borderRadius: 8, border: "1px solid var(--bg-border)", cursor: "pointer", fontSize: 16, background: "var(--bg-surface)" }}>Analyze my portfolio</button>
    </div>
  );
  if (!strategy.connected) return <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>IBKR TWS is not connected.</div>;

  const deteriorationSignals = strategy.deterioration_signals || [];
  const priceSignals = strategy.price_target_signals || [];
  const consolidation = strategy.consolidation_opportunities || [];
  const recommended = strategy.recommended_portfolio;
  const perf = recommended?.performance_comparison;

  const positionPrices = {};
  const positionShares = {};
  (strategy.portfolio || []).forEach(p => {
    positionPrices[p.ticker] = p.market_price || (p.position > 0 ? p.market_value / p.position : 0);
    positionShares[p.ticker] = p.position || 0;
  });

  const actionColor = {
    "SELL":     { bg: "var(--loss-bg)", color: "var(--loss)" },
    "REDUCE":   { bg: "var(--warning-bg)", color: "var(--warning)" },
    "KEEP":     { bg: "var(--bg-elevated)", color: "var(--text-primary)" },
    "INCREASE": { bg: "var(--info-bg)", color: "var(--accent-hover)" },
    "BUY":      { bg: "var(--gain-bg)", color: "var(--gain)" },
  };

  const positions = recommended?.recommended_positions || [];
  const sells = positions.filter(p => ["SELL", "REDUCE"].includes(p.action));
  const keeps = positions.filter(p => p.action === "KEEP");
  const buys = positions.filter(p => ["BUY", "INCREASE"].includes(p.action));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button onClick={onFetch} style={{ padding: "6px 16px", borderRadius: 8, border: "1px solid var(--bg-border)", cursor: "pointer", fontSize: 14, background: "var(--bg-surface)", color: "var(--text-secondary)" }}>Re-run analysis</button>
      </div>

      {deteriorationSignals.length > 0 && (
        <Card title="⚠️ Deterioration alerts">
          {deteriorationSignals.map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 0", borderBottom: i < deteriorationSignals.length - 1 ? "1px solid var(--bg-border)" : "none" }}>
              <div style={{ minWidth: 48 }}>
                <div style={{ fontSize: 15, fontWeight: 500, color: "var(--loss)" }}>{s.ticker}</div>
                <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{s.name}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", gap: 12, marginBottom: 4 }}>
                  <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>Rating: <span style={{ color: "var(--loss)", fontWeight: 500 }}>{s.rating_change}</span></div>
                  <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>Target: <span style={{ color: "var(--loss)", fontWeight: 500 }}>{s.target_change}</span></div>
                </div>
                <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>{s.action}</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                <Badge text="DETERIORATING" type="sell" />
                <Badge text={s.urgency} type={s.urgency === "high" ? "sell" : "medium"} />
              </div>
            </div>
          ))}
        </Card>
      )}

      {priceSignals.length > 0 && (
        <Card title="Price target signals">
          {priceSignals.map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: i < priceSignals.length - 1 ? "1px solid var(--bg-border)" : "none" }}>
              <div style={{ minWidth: 48 }}>
                <div style={{ fontSize: 15, fontWeight: 500, color: "var(--loss)" }}>{s.ticker}</div>
                <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{s.name}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 4 }}>Current: ${s.current_price} · Analyst target: ${s.target_price}</div>
                <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>{s.action}</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                <Badge text="TARGET REACHED" type="sell" />
                <span style={{ fontSize: 13, color: "var(--gain)" }}>+${Number(s.unrealized_pnl).toLocaleString("en-US", { maximumFractionDigits: 0 })} gain</span>
              </div>
            </div>
          ))}
        </Card>
      )}

      {consolidation.length > 0 && (
        <Card title="ETF consolidation">
          {consolidation.map((c, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: i < consolidation.length - 1 ? "1px solid var(--bg-border)" : "none" }}>
              <div style={{ minWidth: 120 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{c.group}</div>
                <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2 }}>{c.held.join(" + ")}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 3 }}>{c.reason}</div>
                <div style={{ fontSize: 13, color: "var(--gain)", fontWeight: 500 }}>Saves ${c.annual_savings.toFixed(2)}/yr</div>
              </div>
              <Badge text={c.recommendation} type="hold" />
            </div>
          ))}
        </Card>
      )}

      {perf && (
        <Card title="Expected performance: current vs recommended">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div style={{ background: "var(--bg-surface)", borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 4 }}>Today</div>
              <div style={{ fontSize: 26, fontWeight: 500, color: perf.current_portfolio_return_pct >= 18 ? "var(--gain)" : "var(--loss)" }}>~{perf.current_portfolio_return_pct}%</div>
              <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>estimated 12-month return</div>
              <div style={{ fontSize: 13, marginTop: 6, color: perf.current_portfolio_return_pct >= 18 ? "var(--gain)" : "var(--loss)", fontWeight: 500 }}>{perf.current_vs_target}</div>
            </div>
            <div style={{ background: "var(--gain-bg)", borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 4 }}>Recommended portfolio</div>
              <div style={{ fontSize: 26, fontWeight: 500, color: "var(--gain)" }}>~{perf.recommended_portfolio_return_pct}%</div>
              <div style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 4 }}>estimated 12-month return</div>
              <div style={{ fontSize: 13, marginTop: 6, color: "var(--gain)", fontWeight: 500 }}>{perf.recommended_vs_target}</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div style={{ fontSize: 14, color: "var(--text-secondary)" }}>Improvement:</div>
            <div style={{ fontSize: 16, fontWeight: 500, color: "var(--gain)" }}>+{perf.return_improvement_pct}% estimated annual return</div>
          </div>
          {perf.key_drivers?.length > 0 && (
            <div>
              <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 6 }}>Key drivers:</div>
              {perf.key_drivers.map((d, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 4 }}>
                  <div style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--gain)", marginTop: 5, flexShrink: 0 }} />
                  <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>{d}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {recommended && (
        <Card title="Recommended portfolio allocation">
          {recommended.execution_summary && (
            <div style={{ background: "var(--accent-glow)", borderRadius: 8, padding: 10, fontSize: 14, color: "var(--accent)", lineHeight: 1.7, marginBottom: 14 }}>
              {recommended.execution_summary}
            </div>
          )}
          {sells.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Sell / Reduce</div>
              {sells.map((p, i) => (
                <div key={i} style={{ padding: "8px 0", borderBottom: "1px solid var(--bg-border)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ minWidth: 48 }}>
                      <div style={{ fontSize: 15, fontWeight: 500 }}>{p.ticker}</div>
                      <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{p.name}</div>
                    </div>
                    <div style={{ flex: 1, fontSize: 13, color: "var(--text-secondary)" }}>{p.rationale}</div>
                    <div style={{ textAlign: "right", minWidth: 140 }}>
                      <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{p.current_pct}% → {p.recommended_pct}%</div>
                      <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>${Number(p.current_value).toLocaleString("en-US", { maximumFractionDigits: 0 })} → ${Number(p.recommended_value).toLocaleString("en-US", { maximumFractionDigits: 0 })}</div>
                    </div>
                    <div style={{ minWidth: 70, textAlign: "right" }}>
                      <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 4, fontWeight: 500, background: actionColor[p.action]?.bg, color: actionColor[p.action]?.color }}>{p.action}</span>
                      {p.priority === "high" && <div style={{ fontSize: 12, color: "var(--loss)", marginTop: 2 }}>urgent</div>}
                    </div>
                  </div>
                  {onExecute && <TradeButton ticker={p.ticker} action={p.action} price={positionPrices[p.ticker] || 0} currentShares={positionShares[p.ticker] || 0} onExecute={onExecute} compact={true} />}
                </div>
              ))}
            </div>
          )}
          {keeps.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Keep</div>
              {keeps.map((p, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--bg-border)" }}>
                  <div style={{ minWidth: 48 }}>
                    <div style={{ fontSize: 15, fontWeight: 500 }}>{p.ticker}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{p.name}</div>
                  </div>
                  <div style={{ flex: 1, fontSize: 13, color: "var(--text-secondary)" }}>{p.rationale}</div>
                  <div style={{ textAlign: "right", minWidth: 140 }}>
                    <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{p.recommended_pct}% of portfolio</div>
                    <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>${Number(p.recommended_value).toLocaleString("en-US", { maximumFractionDigits: 0 })}</div>
                  </div>
                  <div style={{ minWidth: 70, textAlign: "right" }}>
                    <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 4, fontWeight: 500, background: actionColor["KEEP"]?.bg, color: actionColor["KEEP"]?.color }}>KEEP</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {buys.length > 0 && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Buy / Increase</div>
              {buys.map((p, i) => (
                <div key={i} style={{ padding: "8px 0", borderBottom: "1px solid var(--bg-border)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ minWidth: 48 }}>
                      <div style={{ fontSize: 15, fontWeight: 500 }}>{p.ticker}</div>
                      <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{p.name}</div>
                    </div>
                    <div style={{ flex: 1, fontSize: 13, color: "var(--text-secondary)" }}>{p.rationale}</div>
                    <div style={{ textAlign: "right", minWidth: 140 }}>
                      <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{p.current_pct > 0 ? `${p.current_pct}% → ` : "new → "}{p.recommended_pct}%</div>
                      <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>{p.current_value > 0 ? `$${Number(p.current_value).toLocaleString("en-US", { maximumFractionDigits: 0 })} → ` : ""}${Number(p.recommended_value).toLocaleString("en-US", { maximumFractionDigits: 0 })}</div>
                    </div>
                    <div style={{ minWidth: 70, textAlign: "right" }}>
                      <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 4, fontWeight: 500, background: actionColor[p.action]?.bg, color: actionColor[p.action]?.color }}>{p.action}</span>
                    </div>
                  </div>
                  {onExecute && <TradeButton ticker={p.ticker} action={p.action} price={positionPrices[p.ticker] || 0} currentShares={positionShares[p.ticker] || 0} suggestedShares={positionPrices[p.ticker] > 0 ? Math.max(1, Math.floor(p.recommended_value / positionPrices[p.ticker])) : 1} onExecute={onExecute} compact={true} />}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function PerformanceTab({ performance, loading, onFetch, strategy, portfolioData }) {
  if (loading) return <SimpleLoadingBar label="Loading performance data..." />;

  if (!performance || !performance.connected) return (
    <div style={{ textAlign: "center", padding: 60 }}>
      <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>Portfolio Performance</div>
      <div style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 24, maxWidth: 360, margin: "0 auto 24px" }}>
        {performance?.error || "Load your portfolio to see performance data."}
      </div>
      <button onClick={onFetch} style={{ padding: "10px 24px", borderRadius: 8, border: "1px solid var(--bg-border)", cursor: "pointer", fontSize: 16, background: "var(--bg-surface)" }}>
        Load Performance
      </button>
    </div>
  );

  const positions = performance.positions || [];
  const summary = performance.summary || {};
  const perf = strategy?.recommended_portfolio?.performance_comparison;

  // Color helpers
  const pnlColor = (v) => v > 0 ? "var(--gain)" : v < 0 ? "var(--loss)" : "#555";
  const pnlBg = (v) => v > 0 ? "var(--gain-bg)" : v < 0 ? "var(--loss-bg)" : "var(--bg-surface)";

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
        <div style={{ background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 4 }}>Total value</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>${Number((summary.net_liquidation || 0) + (portfolioData?.available_cash || 0)).toLocaleString("en-US", { maximumFractionDigits: 0 })}</div>
          {portfolioData?.available_cash > 0 && (
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4, display: "flex", flexDirection: "column", gap: 1 }}>
              <span>Invested: ${Number(summary.net_liquidation || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}</span>
              <span style={{ color: "var(--accent)" }}>+ Cash: ${Number(portfolioData.available_cash).toLocaleString("en-US", { maximumFractionDigits: 0 })}</span>
            </div>
          )}
        </div>
        <div style={{ background: summary.cost_data_available ? pnlBg(summary.total_unrealized_pnl) : "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 4 }}>Total gain / loss</div>
          {summary.cost_data_available ? (
            <>
              <div style={{ fontSize: 24, fontWeight: 700, color: pnlColor(summary.total_unrealized_pnl) }}>
                {summary.total_unrealized_pnl >= 0 ? "+" : ""}${Number(summary.total_unrealized_pnl || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}
              </div>
              <div style={{ fontSize: 13, color: pnlColor(summary.total_pnl_pct) }}>{summary.total_pnl_pct >= 0 ? "+" : ""}{summary.total_pnl_pct}% total return</div>
            </>
          ) : (
            <div style={{ fontSize: 14, color: "var(--text-muted)", marginTop: 4 }}>Cost basis data unavailable from this sync</div>
          )}
        </div>
        <div style={{ background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 4 }}>Positions</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{summary.position_count}</div>
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{summary.cost_data_available ? `Avg cost basis: $${Number(summary.total_cost_basis || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}` : "Cost basis unavailable"}</div>
        </div>
      </div>

      {/* Before/After optimization */}
      {!perf && (
        <div style={{ background: "var(--accent-glow)", border: "0.5px solid var(--accent)33", borderRadius: 12, padding: 14, marginBottom: 16, fontSize: 14, color: "var(--accent)" }}>
          💡 Run <strong>Portfolio</strong> to see how the recommended changes would improve your returns.
        </div>
      )}

      {/* Position detail table */}
      <div style={{ background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: 12, padding: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 14 }}>Position Performance</div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--bg-border)" }}>
                {["Ticker", "Name", "Weight", "Value", "P&L", "Return", "1mo", "3mo", "YTD"].map(h => (
                  <th key={h} style={{ textAlign: h === "Ticker" || h === "Name" ? "left" : "right", padding: "6px 10px", color: "var(--text-muted)", fontWeight: 500, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {positions.map((p, i) => (
                <tr key={i} style={{ borderBottom: "1px solid var(--bg-border)" }}>
                  <td style={{ padding: "10px", fontWeight: 600 }}>{p.ticker}</td>
                  <td style={{ padding: "10px", color: "var(--text-secondary)", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</td>
                  <td style={{ padding: "10px", textAlign: "right" }}>
                    <div style={{ display: "inline-block", background: "var(--bg-surface)", borderRadius: 4, padding: "2px 6px" }}>{p.weight}%</div>
                  </td>
                  <td style={{ padding: "10px", textAlign: "right", fontWeight: 500 }}>${Number(p.market_value).toLocaleString("en-US", { maximumFractionDigits: 0 })}</td>
                  <td style={{ padding: "10px", textAlign: "right", color: pnlColor(p.unrealized_pnl) }}>
                    {p.unrealized_pnl != null ? `${p.unrealized_pnl >= 0 ? "+" : ""}$${Number(p.unrealized_pnl).toLocaleString("en-US", { maximumFractionDigits: 0 })}` : "—"}
                  </td>
                  <td style={{ padding: "10px", textAlign: "right" }}>
                    <span style={{ background: pnlBg(p.pnl_pct), color: pnlColor(p.pnl_pct), padding: "2px 6px", borderRadius: 4, fontWeight: 500 }}>
                      {p.pnl_pct != null ? `${p.pnl_pct >= 0 ? "+" : ""}${p.pnl_pct}%` : "—"}
                    </span>
                  </td>
                  <td style={{ padding: "10px", textAlign: "right", color: pnlColor(p.momentum_1mo) }}>{p.momentum_1mo != null ? `${p.momentum_1mo >= 0 ? "+" : ""}${p.momentum_1mo}%` : "—"}</td>
                  <td style={{ padding: "10px", textAlign: "right", color: pnlColor(p.momentum_3mo) }}>{p.momentum_3mo != null ? `${p.momentum_3mo >= 0 ? "+" : ""}${p.momentum_3mo}%` : "—"}</td>
                  <td style={{ padding: "10px", textAlign: "right", color: pnlColor(p.ytd) }}>{p.ytd != null ? `${p.ytd >= 0 ? "+" : ""}${p.ytd}%` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button onClick={onFetch} style={{ marginTop: 12, padding: "6px 16px", borderRadius: 8, border: "1px solid var(--bg-border)", cursor: "pointer", fontSize: 14, background: "var(--bg-surface)", color: "var(--text-secondary)" }}>
          Refresh
        </button>
      </div>
    </div>
  );
}


function AIChat({ portfolioData, recommendationsData, strategyData, performanceData }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const messagesEndRef = React.useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open]);

  const buildContext = () => {
    let ctx = "You are Cue's AI assistant with two roles:\n";
    ctx += "1. **INVESTMENT ADVISOR**: Answer portfolio questions using the user's live data — reference actual tickers, dollar amounts, and percentages.\n";
    ctx += "2. **PLATFORM SUPPORT**: Help users understand how to use Cue. Answer questions about placing orders, connecting brokers, uploading portfolios, interpreting recommendations, etc.\n\n";
    ctx += "Be concise and specific. Format responses with line breaks. Use **bold** for key terms and numbers.\n\n";

    ctx += "PLATFORM KNOWLEDGE BASE:\n";
    ctx += "- **Placing orders**: Click 'Execute Portfolio Optimization' in the Rebalancing Plan section. Review each order (dollar amount shown). Toggle orders on/off. Click 'Send orders to IBKR' — each creates an IBKR instruction. You receive a link to open in IBKR to confirm before anything executes. Nothing runs automatically.\n";
    ctx += "- **Connecting IBKR (Premium)**: Click 'Connect IBKR' in the header. Cue auto-generates your 2FA code and connects (~35–40 seconds). Or from the onboarding 'Connect your investment account' screen.\n";
    ctx += "- **Other brokers**: Upload a screenshot or CSV export from Schwab, Fidelity, Robinhood, E*TRADE, TD Ameritrade, or any broker. The 'Connect your investment account' screen walks you through broker-specific export steps.\n";
    ctx += "- **Running analysis**: Click 'Run Portfolio Optimization' on the Portfolio tab. Takes 3–5 minutes — Cue scans 13 live data sources and runs AI analysis tailored to your holdings.\n";
    ctx += "- **Tiers**: Free = today's market picks only. Basic = portfolio analysis + CSV/screenshot upload. Premium = IBKR live sync + order execution.\n";
    ctx += "- **Investor profile**: 8-question risk questionnaire taken during onboarding. Personalizes recommendations to your risk tolerance and return target. Can be retaken from the tier select screen.\n";
    ctx += "- **BUY orders**: Use dollar-based cashQty — IBKR fills the exact number of shares (including fractional) at market price.\n";
    ctx += "- **SELL orders**: Full sells use exact share count. Partial reduces use dollar-based cashQty.\n";
    ctx += "- **Performance tab**: Shows position P&L, 1mo/3mo/YTD momentum, and projected return improvement from optimization.\n";
    ctx += "- **Balance score**: 0–100 diversification score. Penalizes ETF overlaps and sector overweights. Shown in the top stats bar.\n";
    ctx += "- **Deterioration alerts**: Auto-detected when analyst ratings and price targets both decline. These override other recommendations with a SELL.\n\n";

    ctx += "INVESTOR PROFILE:\n- Portfolio size: ~$135,000\n- Annual growth target: 18%\n- Risk tolerance: aggressive\n- Based in Argentina\n\n";
    if (portfolioData?.portfolio) {
      ctx += "LIVE PORTFOLIO POSITIONS:\n";
      portfolioData.portfolio.forEach(p => {
        ctx += `- ${p.ticker} (${p.name}): $${Number(p.market_value).toLocaleString("en-US", { maximumFractionDigits: 0 })}, return ${p.pnl_pct?.toFixed(1)}%, analyst: ${p.analyst_label || "N/A"}, upside: ${p.upside_pct !== null ? p.upside_pct + "%" : "N/A"}\n`;
      });
      ctx += `Total: $${Number(portfolioData.net_liquidation).toLocaleString("en-US", { maximumFractionDigits: 0 })}\n\n`;
    }
    if (recommendationsData?.stock_picks) {
      ctx += "TODAY'S STOCK PICKS:\n";
      recommendationsData.stock_picks.forEach(p => {
        ctx += `- ${p.ticker} (${p.name}): momentum ${p.momentum_score}/100, ${p.upside_to_target ? p.upside_to_target + "% upside, " : ""}analyst ${p.analyst_rating}, ${p.action_recommendation}\n`;
      });
      ctx += "\nETF PICKS:\n";
      recommendationsData.etf_picks?.forEach(p => {
        ctx += `- ${p.ticker} (${p.name}): ${p.portfolio_role}, ${p.action_recommendation || ""}\n`;
      });
      if (recommendationsData.portfolio_alignment) ctx += `\nALIGNMENT: ${recommendationsData.portfolio_alignment}\n`;
      ctx += "\n";
    }
    if (strategyData?.recommended_portfolio) {
      ctx += "PORTFOLIO STRATEGY:\n";
      const positions = strategyData.recommended_portfolio.recommended_positions || [];
      positions.forEach(p => {
        ctx += `- ${p.ticker}: ${p.action} — ${p.rationale} (target ${p.recommended_pct}%, $${Number(p.recommended_value).toLocaleString("en-US", { maximumFractionDigits: 0 })})\n`;
      });
      if (strategyData.recommended_portfolio.performance_comparison) {
        const perf = strategyData.recommended_portfolio.performance_comparison;
        ctx += `\nToday expected return: ~${perf.current_portfolio_return_pct}%\n`;
        ctx += `Recommended portfolio expected return: ~${perf.recommended_portfolio_return_pct}%\n`;
        ctx += `Key drivers: ${perf.key_drivers?.join(", ")}\n`;
      }
      ctx += "\n";
    }
    if (performanceData?.summary) {
      const s = performanceData.summary;
      ctx += "PERFORMANCE:\n";
      ctx += `- TWR since inception: +${s.twr_total_pct}%\n`;
      ctx += `- Annualized: ${s.twr_annualized_pct}% vs 18% target\n`;
      ctx += `- YTD: ${s.ytd_return_pct}% vs ${s.ytd_target_pct}% pace\n`;
      ctx += `- Vs target: ${s.pace_vs_target > 0 ? "+" : ""}${s.pace_vs_target}%\n\n`;
    }
    if (strategyData?.deterioration_signals?.length > 0) {
      ctx += "DETERIORATION ALERTS:\n";
      strategyData.deterioration_signals.forEach(s => {
        ctx += `- ${s.ticker}: ${s.action}, rating: ${s.rating_change}, target: ${s.target_change}\n`;
      });
      ctx += "\n";
    }
    return ctx;
  };

  const renderMessage = (content) => {
    return content.split("\n").map((line, li) => {
      if (!line.trim()) return <div key={li} style={{ height: 6 }} />;
      const parts = [];
      let remaining = line;
      let keyIdx = 0;
      while (remaining.length > 0) {
        const boldStart = remaining.indexOf("**");
        if (boldStart === -1) { parts.push(<span key={keyIdx++}>{remaining}</span>); break; }
        if (boldStart > 0) parts.push(<span key={keyIdx++}>{remaining.slice(0, boldStart)}</span>);
        const boldEnd = remaining.indexOf("**", boldStart + 2);
        if (boldEnd === -1) { parts.push(<span key={keyIdx++}>{remaining.slice(boldStart)}</span>); break; }
        parts.push(<strong key={keyIdx++} style={{ fontWeight: 600 }}>{remaining.slice(boldStart + 2, boldEnd)}</strong>);
        remaining = remaining.slice(boldEnd + 2);
      }
      const isBullet = line.trim().startsWith("- ") || line.trim().startsWith("• ");
      if (isBullet) return (
        <div key={li} style={{ display: "flex", gap: 6, marginBottom: 4 }}>
          <span style={{ color: "var(--accent)", fontWeight: 700, flexShrink: 0 }}>·</span>
          <span>{parts}</span>
        </div>
      );
      return <div key={li} style={{ marginBottom: 4 }}>{parts}</div>;
    });
  };

  const sendMessage = async () => {
    if (!input.trim() || thinking) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setThinking(true);
    try {
      const context = buildContext();
      const history = messages.map(m => ({ role: m.role, content: m.content }));
      const response = await axios.post(`${API}/api/chat`, {
        context,
        messages: [...history, { role: "user", content: userMsg }]
      });
      const reply = response.data.error === "upgrade_required"
        ? (response.data.message || "Upgrade to Portfolio Optimizer to unlock the AI Portfolio Agent.")
        : response.data.reply;
      setMessages(prev => [...prev, { role: "assistant", content: reply }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, something went wrong. Please try again." }]);
    }
    setThinking(false);
  };

  return (
    <>
      <button onClick={() => setOpen(o => !o)} style={{
        position: "fixed", bottom: 24, right: 24, zIndex: 1000,
        width: 52, height: 52, borderRadius: "50%",
        background: "var(--accent)", border: "none", cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 4px 16px rgba(0,0,0,0.18)"
      }} title="Ask AI">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {open ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></> : <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>}
        </svg>
      </button>
      {open && (
        <div style={{ position: "fixed", bottom: 88, right: 24, zIndex: 999, width: 400, height: 540, background: "var(--bg-surface)", borderRadius: 16, border: "1px solid var(--bg-border)", boxShadow: "var(--shadow-elevated)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--bg-border)", background: "var(--accent)" }}>
            <div style={{ fontSize: 15, fontWeight: 500, color: "#fff" }}>Cue Assistant</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>Ask about your portfolio, or how to use the platform</div>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            {messages.length === 0 && (
              <div style={{ textAlign: "center", padding: "20px 10px" }}>
                <div style={{ fontSize: 14, color: "var(--text-muted)", lineHeight: 1.6, marginBottom: 16 }}>Ask about your portfolio, recommendations, or how to use Cue — I can answer both investment and platform questions.</div>
                {["How do I push a buy/sell recommendation to my broker?", "Why sell Nike?", "How am I tracking vs my 18% target?", "How do I connect my broker?"].map((q, i) => (
                  <button key={i} onClick={() => setInput(q)} style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", marginBottom: 6, borderRadius: 8, border: "1px solid var(--bg-border)", background: "var(--bg-surface)", cursor: "pointer", fontSize: 13, color: "var(--text-secondary)" }}>{q}</button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
                <div style={{ maxWidth: "85%", padding: "10px 14px", borderRadius: 12, fontSize: 14, lineHeight: 1.8, background: m.role === "user" ? "var(--accent)" : "var(--bg-surface)", color: m.role === "user" ? "#fff" : "#333", borderBottomRightRadius: m.role === "user" ? 4 : 12, borderBottomLeftRadius: m.role === "assistant" ? 4 : 12 }}>
                  {m.role === "assistant" ? renderMessage(m.content) : m.content}
                </div>
              </div>
            ))}
            {thinking && (
              <div style={{ display: "flex", justifyContent: "flex-start" }}>
                <div style={{ padding: "10px 14px", borderRadius: 12, background: "var(--bg-surface)", fontSize: 14, color: "var(--text-muted)" }}>Thinking...</div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <div style={{ padding: "10px 12px", borderTop: "1px solid var(--bg-border)", display: "flex", gap: 8, alignItems: "center" }}>
            <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()} placeholder="Ask about your portfolio..." style={{ flex: 1, padding: "8px 12px", borderRadius: 8, border: "1px solid var(--bg-border)", fontSize: 14, outline: "none", background: "var(--bg-surface)" }} />
            <button onClick={sendMessage} disabled={thinking || !input.trim()} style={{ padding: "8px 14px", borderRadius: 8, background: thinking || !input.trim() ? "#ccc" : "var(--accent)", border: "none", cursor: thinking || !input.trim() ? "default" : "pointer", color: "#fff", fontSize: 14, fontWeight: 500 }}>Send</button>
          </div>
        </div>
      )}
    </>
  );
}


function IbkrConnectModal({ onConnect, onClose }) {
  const [phase, setPhase] = React.useState("idle"); // idle | connecting | connected | error
  const [error, setError] = React.useState("");
  const [elapsed, setElapsed] = React.useState(0);

  React.useEffect(() => {
    let t;
    if (phase === "connecting") {
      t = setInterval(() => setElapsed(e => e + 1), 1000);
    }
    return () => clearInterval(t);
  }, [phase]);

  const handleConnect = async () => {
    setPhase("connecting");
    setError("");
    setElapsed(0);
    try {
      const r = await axios.post(`${API}/api/ibkr/auto-connect`, {}, { timeout: 150000 });
      if (r.data.success) {
        setPhase("connected");
        setTimeout(() => onConnect(true), 1200);
      } else {
        setError(r.data.error || "Connection failed");
        setPhase("error");
      }
    } catch (e) {
      setError("Connection error. Please try again.");
      setPhase("error");
    }
  };

  const steps = [
    { label: "Generating 2FA code", done: elapsed >= 2 },
    { label: "Starting IBKR gateway", done: elapsed >= 10 },
    { label: "Authenticating session", done: elapsed >= 25 },
    { label: "Verifying connection", done: elapsed >= 35 },
  ];

  return (
    <div style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 36, width: 360, boxShadow: "var(--shadow-elevated)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <svg width={20} height={20} viewBox="0 0 20 20" fill="none">
            <rect width={20} height={20} rx={5} fill="#E8A030" opacity={0.15} />
            <path d="M10 4C10 4 13 7 13 9.5C13 11.2 11.8 12.5 10.5 13.2C10.8 11.8 10 11 9 10.7C9.3 11.8 9 13.2 8 14.2C7.3 13.2 7 11.8 7 10.5C7 7.5 10 4 10 4Z" fill="#E8A030"/>
          </svg>
          <span style={{ fontSize: 16, fontWeight: 600, color: "var(--text-primary)" }}>Connect IBKR</span>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text-muted)", fontSize: 18, cursor: "pointer", lineHeight: 1 }}>×</button>
      </div>

      {phase === "idle" && (
        <>
          <div style={{ background: "var(--accent-dim)", border: "1px solid rgba(232,160,48,0.2)", borderRadius: 10, padding: 16, marginBottom: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--accent)", marginBottom: 6 }}>Secure auto-connect</div>
            <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>
              Cue connects to your IBKR account using pre-authorized credentials. No manual entry required — your 2FA code is generated automatically.
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
            {[
              "Live portfolio sync",
              "Real-time position values",
              "One-click order execution",
            ].map((f, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-secondary)" }}>
                <span style={{ color: "var(--gain)", fontSize: 15 }}>✓</span> {f}
              </div>
            ))}
          </div>
          <button onClick={handleConnect}
            style={{ width: "100%", padding: "12px", borderRadius: 8, border: "none", background: "var(--accent)", color: "#000", fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
            Connect to IBKR
          </button>
        </>
      )}

      {phase === "connecting" && (
        <div style={{ padding: "8px 0" }}>
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 20 }}>
            This takes about 35–40 seconds. Do not close this window.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 24 }}>
            {steps.map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0,
                  background: s.done ? "var(--gain-bg)" : "var(--bg-border)",
                  color: s.done ? "var(--gain)" : "var(--text-muted)",
                  border: s.done ? "1px solid var(--gain-border)" : "1px solid var(--bg-border)"
                }}>
                  {s.done ? "✓" : "·"}
                </div>
                <span style={{ fontSize: 13, color: s.done ? "var(--text-primary)" : "var(--text-muted)" }}>{s.label}</span>
              </div>
            ))}
          </div>
          <div style={{ height: 3, background: "var(--bg-primary)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ height: 3, background: "var(--accent)", borderRadius: 2, transition: "width 1s linear",
              width: `${Math.min(95, (elapsed / 40) * 100)}%`
            }} />
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 8, textAlign: "right" }}>{elapsed}s</div>
        </div>
      )}

      {phase === "connected" && (
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>✓</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "var(--gain)", marginBottom: 6 }}>Connected to IBKR</div>
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Live portfolio sync is active.</div>
        </div>
      )}

      {phase === "error" && (
        <>
          <div style={{ background: "var(--loss-bg)", border: "1px solid var(--loss-border)", borderRadius: 8, padding: 14, marginBottom: 20, fontSize: 13, color: "var(--loss)" }}>
            {error}
          </div>
          <button onClick={handleConnect}
            style={{ width: "100%", padding: "11px", borderRadius: 8, border: "none", background: "var(--accent)", color: "#000", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            Try again
          </button>
        </>
      )}
    </div>
  );
}


function LoadingOptimization({ stage }) {
  const [elapsed, setElapsed] = React.useState(0);
  const [currentDetail, setCurrentDetail] = React.useState(0);

  React.useEffect(() => {
    const timer = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const stages = [
    {
      key: "portfolio", label: "Reading your positions", pct: 5,
      details: [
        "Parsing portfolio positions...",
        "Calculating sector allocations...",
        "Identifying overlap patterns...",
      ]
    },
    {
      key: "market", label: "Scanning 80+ stocks", pct: 20,
      details: [
        "Running Finviz momentum screen (80+ candidates)...",
        "Filtering by analyst ratings & institutional ownership...",
        "Targeting your portfolio gap sectors...",
        "Fetching sector ETF momentum data...",
      ]
    },
    {
      key: "signals", label: "Pulling live market data", pct: 50,
      details: [
        "Fetching analyst price targets from Finviz & FMP...",
        "Getting earnings history & EPS surprises from yfinance...",
        "Checking institutional ownership trends...",
        "Pulling DCF intrinsic value & ROIC from Financial Modeling Prep...",
        "Fetching Yahoo Finance news sentiment...",
        "Checking SEC insider trading filings...",
        "Getting macro context (VIX, yields, FRED)...",
        "Calculating sector rotation signals...",
      ]
    },
    {
      key: "ai", label: "Running AI analysis", pct: 80,
      details: [
        "Scoring 25+ candidates across 8 signal dimensions...",
        "Cross-referencing with your existing holdings...",
        "Filtering out indirect ETF exposure...",
        "Applying signal quality thresholds...",
        "Generating portfolio-specific recommendations...",
      ]
    },
    {
      key: "done", label: "Building your plan", pct: 95,
      details: [
        "Calculating capital allocation from redundant positions...",
        "Sizing new positions to maximize diversification...",
        "Computing projected return improvement...",
        "Generating diversification score projection...",
        "Preparing executive summary...",
      ]
    },
  ];

  const currentStage = stages.find(s => s.key === stage) || stages[0];
  const stageIdx = stages.findIndex(s => s.key === stage);

  React.useEffect(() => {
    const details = currentStage.details;
    const interval = setInterval(() => {
      setCurrentDetail(d => (d + 1) % details.length);
    }, 2200);
    return () => clearInterval(interval);
  }, [stage]);

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  const timeStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div style={{ width: "100%", maxWidth: 520 }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 6 }}>Analyzing your portfolio</div>
          <div style={{ fontSize: 14, color: "var(--text-muted)" }}>
            13 live data sources · AI-powered · {timeStr} elapsed
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ background: "var(--bg-elevated)", borderRadius: 8, height: 6, marginBottom: 8, overflow: "hidden" }}>
          <div style={{ height: 6, borderRadius: 8, background: "linear-gradient(90deg, var(--accent), var(--info))", width: currentStage.pct + "%", transition: "width 1.5s ease" }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--text-muted)", marginBottom: 28 }}>
          <span style={{ color: "var(--accent)", fontWeight: 500 }}>{currentStage.label}...</span>
          <span>{currentStage.pct}%</span>
        </div>

        {/* Current activity detail */}
        <div style={{ background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: 10, padding: "12px 16px", marginBottom: 20, minHeight: 40, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--accent)", flexShrink: 0, animation: "pulse 1.5s infinite" }} />
          <div style={{ fontSize: 14, color: "var(--text-secondary)" }}>{currentStage.details[currentDetail % currentStage.details.length]}</div>
        </div>

        {/* Stage list */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {stages.map((s, i) => {
            const isDone = i < stageIdx;
            const isCurrent = i === stageIdx;
            return (
              <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                  background: isDone ? "var(--gain)" : isCurrent ? "var(--accent)" : "var(--bg-elevated)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, color: "#fff", fontWeight: 700
                }}>
                  {isDone ? "✓" : isCurrent ? "●" : ""}
                </div>
                <div style={{ fontSize: 14, color: isDone ? "var(--gain)" : isCurrent ? "var(--accent)" : "#aaa", fontWeight: isCurrent ? 500 : 400 }}>
                  {s.label}
                </div>
                {isCurrent && (
                  <div style={{ marginLeft: "auto", fontSize: 12, color: "var(--accent)", background: "var(--info-bg)", padding: "2px 8px", borderRadius: 10 }}>
                    live
                  </div>
                )}
                {isDone && (
                  <div style={{ marginLeft: "auto", fontSize: 12, color: "var(--gain)" }}>done</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Data sources footer */}
        <div style={{ marginTop: 28, padding: "10px 14px", background: "var(--bg-surface)", borderRadius: 8, fontSize: 13, color: "var(--text-muted)", lineHeight: 1.7 }}>
          <div style={{ fontWeight: 500, color: "var(--text-secondary)", marginBottom: 4 }}>Live data feeds:</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
            {["Finviz", "yfinance", "Yahoo Finance RSS", "Financial Modeling Prep", "SEC EDGAR", "Fear & Greed", "FRED Macro", "Damodaran Valuations"].map(src => (
              <span key={src} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ color: "var(--accent)" }}>●</span> {src}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


function TimingBadge({ horizon }) {
  const h = (horizon || "").toLowerCase();
  const map = {
    short: { label: "< 1 year", bg: "var(--gain-bg)", color: "var(--gain)" },
    medium: { label: "1–2 years", bg: "var(--info-bg)", color: "var(--accent-hover)" },
    long: { label: "2+ years", bg: "var(--accent-glow)", color: "var(--accent)" },
  };
  const key = h.includes("short") ? "short" : h.includes("long") ? "long" : "medium";
  const s = map[key];
  return <span style={{ fontSize: 12, padding: "2px 7px", borderRadius: 10, fontWeight: 500, background: s.bg, color: s.color }}>{s.label}</span>;
}

const DETAIL_SECTIONS = [
  { key: "growth_outlook", label: "Growth outlook" },
  { key: "moat_quality", label: "Moat & quality" },
  { key: "valuation", label: "Valuation" },
  { key: "risks", label: "Risks" },
];

const DETAIL_FIELD_LABELS = {
  revenue_growth_yoy_pct: "Revenue growth (YoY)", earnings_growth_yoy_pct: "Earnings growth (YoY)",
  momentum_1mo_pct: "1mo momentum", momentum_3mo_pct: "3mo momentum", analyst_rating: "Analyst rating",
  upside_to_target_pct: "Upside to target", target_price: "Target price", revision_trend: "Analyst revisions",
  revision_detail: "Revision detail", roic_pct: "ROIC", roe_pct: "ROE", profit_margin_pct: "Profit margin",
  debt_to_equity: "Debt / equity", current_ratio: "Current ratio", earnings_quality: "Earnings quality",
  avg_eps_surprise_pct: "Avg. EPS surprise", consecutive_beats: "Consecutive beats",
  institutional_ownership_pct: "Institutional ownership", institution_trend: "Institutional trend",
  pe_ratio: "P/E ratio", forward_pe: "Forward P/E", peg_ratio: "PEG ratio", fcf_yield_pct: "FCF yield",
  dcf_discount_pct: "DCF discount to fair value", valuation_signal: "Valuation vs. sector",
  pe_discount_vs_sector_pct: "P/E discount vs. sector", short_float_pct: "Short interest", rsi: "RSI",
  position_in_52w_range_pct: "Position in 52w range", earnings_soon: "Earnings coming up",
  days_to_earnings: "Days to earnings", insider_signal: "Insider signal", insider_summary: "Insider activity",
  news_sentiment: "News sentiment",
};

function PickDetailPanel({ detail }) {
  const sections = DETAIL_SECTIONS.map(s => ({ ...s, fields: detail[s.key] || {} })).filter(s => Object.keys(s.fields).length > 0);
  if (sections.length === 0) return <div style={{ fontSize: 12, color: "var(--text-muted)" }}>No additional signal detail available for this pick.</div>;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
      {sections.map(s => (
        <div key={s.key}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>{s.label}</div>
          {Object.entries(s.fields).map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12, padding: "3px 0", color: "var(--text-secondary)" }}>
              <span style={{ color: "var(--text-muted)" }}>{DETAIL_FIELD_LABELS[k] || k}</span>
              <span style={{ textAlign: "right" }}>{typeof v === "number" ? (Math.abs(v) < 10 ? v.toFixed(2) : v.toFixed(1)) : String(v)}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function OptimizationTab({ strategy, recommendations, loading, loadingStage, onFetch, onExecuteAll, portfolioData, error }) {
  const [expandedTickers, setExpandedTickers] = React.useState(new Set());
  const [growthOutlookTab, setGrowthOutlookTab] = React.useState("stocks");
  const toggleDetail = (ticker) => setExpandedTickers(prev => {
    const next = new Set(prev);
    next.has(ticker) ? next.delete(ticker) : next.add(ticker);
    return next;
  });

  if (loading) return <LoadingOptimization stage={loadingStage} />;
  const hasData = strategy || recommendations;
  if (!hasData) return (
    <div style={{ textAlign: "center", padding: 80 }}>
      <div style={{ fontSize: 16, fontWeight: 500, color: "var(--text-primary)", marginBottom: 8 }}>Analyze your portfolio</div>
      <div style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 28, maxWidth: 380, margin: "0 auto 28px" }}>
        Get AI-powered stock picks, rebalancing recommendations, and a full optimization plan.
      </div>
      {error && (
        <div style={{ marginBottom: 20, padding: "10px 16px", borderRadius: 8, background: "var(--loss-bg, #2a1a1a)", color: "var(--loss, #f87171)", fontSize: 13, maxWidth: 420, margin: "0 auto 20px" }}>
          {error}
        </div>
      )}
      <button onClick={() => onFetch(false)} style={{ padding: "12px 32px", borderRadius: "var(--radius-md)", border: "none", background: "var(--accent)", color: "#000", fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
        Run Portfolio Optimization
      </button>
    </div>
  );

  const stockPicks = recommendations?.stock_picks || [];
  const etfPicks = recommendations?.etf_picks || [];
  const positions = strategy?.recommended_portfolio?.recommended_positions || [];
  const sells = positions.filter(p => ["SELL","REDUCE"].includes(p.action))
    .sort((a, b) => (b.current_value || 0) - (a.current_value || 0));
  const increases = positions.filter(p => ["BUY","INCREASE"].includes(p.action))
    .sort((a, b) => (b.recommended_value || 0) - (a.recommended_value || 0));
  const keeps = positions.filter(p => p.action === "KEEP")
    .sort((a, b) => (b.recommended_value || 0) - (a.recommended_value || 0));
  const fullyOptimized = strategy?.fully_optimized || false;
  const nextReviewDate = strategy?.next_review_date || "";
  const perf = strategy?.recommended_portfolio?.performance_comparison;
  const positionPrices = {};
  const positionShares = {};
  (strategy?.portfolio || []).forEach(p => {
    positionPrices[p.ticker] = p.market_price || (p.position > 0 ? p.market_value / p.position : 0);
    positionShares[p.ticker] = p.position || 0;
  });
  const actionColor = {
    "SELL": { bg: "var(--loss-bg)", color: "var(--loss)" }, "REDUCE": { bg: "var(--warning-bg)", color: "var(--warning)" },
    "KEEP": { bg: "var(--bg-elevated)", color: "var(--text-primary)" }, "INCREASE": { bg: "var(--info-bg)", color: "var(--accent-hover)" },
    "BUY": { bg: "var(--gain-bg)", color: "var(--gain)" },
  };

  const sectionHeader = (title, right) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 20 }}>
      <h2 style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>{title}</h2>
      {right && <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>{right}</span>}
    </div>
  );

  const computedAt = strategy?.cache_meta?.computed_at;
  const updatedLabel = computedAt
    ? new Date(computedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : null;

  return (
    <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 4px" }}>
      {/* Re-run button */}
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginBottom: 24 }}>
        {updatedLabel && (
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            {strategy?.cache_meta?.cached ? `Computed today at ${updatedLabel}` : `Updated ${updatedLabel}`}
          </span>
        )}
        <button onClick={() => onFetch(true)} style={{ padding: "7px 16px", borderRadius: "var(--radius-sm)", border: "1px solid var(--bg-border)", background: "transparent", color: "var(--text-muted)", fontSize: 12, cursor: "pointer" }}>Re-run analysis</button>
      </div>

      {/* Portfolio Growth Outlook — surfaced above "what to buy" since a reached
          target is a decision point, not just a market idea. Stocks show
          analyst target-price upside; ETFs have no analyst coverage to draw
          a target from, so that subtab shows trailing momentum instead —
          real data, clearly a look backward rather than a forecast. */}
      {((strategy?.position_targets || []).length > 0 || (strategy?.etf_outlook || []).length > 0) && (() => {
        const stockTargets = (strategy.position_targets || []).filter(pt => !pt.is_etf);
        const etfOutlook = strategy.etf_outlook || [];
        return (
          <section style={{ marginBottom: fullyOptimized ? 12 : 40 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 16 }}>
              <h2 style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Portfolio Growth Outlook</h2>
              <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>{stockTargets.length + etfOutlook.length} tracked</span>
            </div>
            <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
              {[["stocks", `Stocks (${stockTargets.length})`], ["etf", `ETF (${etfOutlook.length})`]].map(([key, label]) => (
                <button key={key} onClick={() => setGrowthOutlookTab(key)}
                  style={{
                    fontSize: 13, fontWeight: 600, padding: "6px 14px", borderRadius: 20,
                    border: "1px solid var(--bg-border)", cursor: "pointer",
                    background: growthOutlookTab === key ? "var(--accent)" : "var(--bg-surface)",
                    color: growthOutlookTab === key ? "#14100a" : "var(--text-secondary)",
                  }}>
                  {label}
                </button>
              ))}
            </div>
            {growthOutlookTab === "etf" ? (
              etfOutlook.length === 0 ? (
                <div style={{ padding: "16px 0", fontSize: 13, color: "var(--text-muted)" }}>No ETF momentum data available yet.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 2 }}>Trailing performance — ETFs have no analyst price target, so this shows recent momentum instead.</div>
                  {etfOutlook.map((e, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderRadius: "var(--radius-md)", background: "var(--bg-elevated)", border: "1px solid var(--bg-border)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <StockLogo ticker={e.ticker} size={22} />
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{e.ticker}</span>
                        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{e.name}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 16, fontSize: 13 }}>
                        <span style={{ color: "var(--text-secondary)" }}>${Number(e.current_price).toFixed(2)}</span>
                        <span style={{ fontWeight: 600, color: e.momentum_1mo >= 0 ? "var(--gain)" : "var(--loss)" }}>{e.momentum_1mo >= 0 ? "+" : ""}{e.momentum_1mo}% (1mo)</span>
                        <span style={{ fontWeight: 600, color: e.momentum_3mo >= 0 ? "var(--gain)" : "var(--loss)" }}>{e.momentum_3mo >= 0 ? "+" : ""}{e.momentum_3mo}% (3mo)</span>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              stockTargets.length === 0 ? (
                <div style={{ padding: "16px 0", fontSize: 13, color: "var(--text-muted)" }}>No stock target prices tracked yet.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {stockTargets.map((pt, i) => (
                    <div key={i} style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "12px 16px", borderRadius: "var(--radius-md)",
                      background: pt.target_reached ? "var(--gain-bg)" : "var(--bg-elevated)",
                      border: pt.target_reached ? "1px solid rgba(34,197,94,0.3)" : "1px solid var(--bg-border)",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <StockLogo ticker={pt.ticker} size={22} />
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{pt.ticker}</span>
                        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{pt.name}</span>
                        {pt.target_reached && (
                          <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 20, background: "var(--gain)", color: "#04120a", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                            Target reached
                          </span>
                        )}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 16, fontSize: 13 }}>
                        <span style={{ color: "var(--text-secondary)" }}>${Number(pt.current_price).toFixed(2)}</span>
                        <span style={{ color: "var(--text-muted)" }}>target ${Number(pt.target_price).toFixed(2)}</span>
                        <span style={{ fontWeight: 600, color: pt.upside_pct >= 0 ? "var(--gain)" : "var(--loss)" }}>
                          {pt.upside_pct >= 0 ? "+" : ""}{pt.upside_pct}% to target
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </section>
        );
      })()}

      {fullyOptimized && (
        <div style={{ background: "var(--gain-bg)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 13, color: "var(--gain)", lineHeight: 1.6, marginBottom: 40 }}>
          <strong>Good news</strong> — your portfolio is already well-optimized for your goals and current market conditions. The picks below are worth keeping an eye on, but there isn't enough deployable capital to fund them right now. Refresh once conditions shift or you have more funds to allocate.
        </div>
      )}

      {/* ACT 2: What to buy */}
      {(stockPicks.length > 0 || etfPicks.length > 0) && (
        <section style={{ marginBottom: 40 }}>
          {sectionHeader(fullyOptimized ? "What to buy (nice to have)" : "What to buy", `${stockPicks.length + etfPicks.length} picks`)}
          {stockPicks.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 12 }}>Stocks</div>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 16 }}>
                {stockPicks.map((p, i) => <PickCard key={i} pick={p} niceToHave={fullyOptimized} />)}
              </div>
            </div>
          )}
          {etfPicks.length > 0 && (
            <div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 12 }}>ETFs</div>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 16 }}>
                {etfPicks.map((p, i) => <PickCard key={i} pick={p} niceToHave={fullyOptimized} />)}
              </div>
            </div>
          )}
          {(stockPicks.length > 0 || etfPicks.length > 0) && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 10 }}>Investment timing</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {[
                  { label: "Short-term (< 1yr)", key: "short", color: "var(--gain)", bg: "var(--gain-bg)" },
                  { label: "Medium (1–2yr)", key: "medium", color: "var(--accent)", bg: "var(--accent-dim)" },
                  { label: "Long-term (2yr+)", key: "long", color: "var(--info)", bg: "var(--info-bg)" },
                ].map(({ label, key, color, bg }) => {
                  const items = [...stockPicks, ...etfPicks].filter(p => {
                    const h = (p.time_horizon || "medium").toLowerCase();
                    if (key === "short") return h.includes("short");
                    if (key === "long") return h.includes("long");
                    return !h.includes("short") && !h.includes("long");
                  });
                  if (!items.length) return null;
                  return (
                    <div key={key} style={{ background: bg, borderRadius: "var(--radius-md)", padding: "8px 14px" }}>
                      <div style={{ fontSize: 11, color, fontWeight: 600, marginBottom: 4 }}>{label}</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {items.map((p, i) => (
                          <span key={i} style={{ fontSize: 13, fontWeight: 700, color }}>{p.ticker}{p.upside_to_target ? ` +${Number(p.upside_to_target).toFixed(1)}%` : ""}</span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      )}

      {/* ACT 3: Why these picks for you */}
      {recommendations?.portfolio_alignment && (
        <section style={{ marginBottom: 40 }}>
          {sectionHeader("Why these picks for you")}
          <div style={{ background: "var(--accent-dim)", border: "1px solid rgba(232,160,48,0.15)", borderRadius: "var(--radius-lg)", padding: "16px 20px", fontSize: 14, color: "var(--accent)", lineHeight: 1.8 }}>
            {recommendations.portfolio_alignment}
          </div>
        </section>
      )}

      {/* ACT 4: Portfolio Today — hidden when fully optimized since nothing will actually execute */}
      {perf && !fullyOptimized && (
        <section style={{ marginBottom: 40 }}>
          {sectionHeader("Your portfolio today vs optimization")}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div style={{ background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: "var(--radius-lg)", padding: 24 }}>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Current trajectory</div>
              <div style={{ fontSize: 36, fontWeight: 700, color: perf.current_portfolio_return_pct >= 18 ? "var(--gain)" : "var(--loss)", lineHeight: 1 }}>~{perf.current_portfolio_return_pct}%</div>
              <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 8 }}>projected 12-month return</div>
              {perf.current_vs_target && <div style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 6, lineHeight: 1.5 }}>{perf.current_vs_target}</div>}
              {(strategy?.strengths?.length > 0 || strategy?.weaknesses?.length > 0) && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--bg-border)" }}>
                  {strategy?.strengths?.length > 0 && strategy.strengths.slice(0,2).map((s, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, fontSize: 12, color: "var(--gain)" }}>
                      <span>+</span><span>{s}</span>
                    </div>
                  ))}
                  {strategy?.weaknesses?.length > 0 && strategy.weaknesses.slice(0,2).map((w, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, fontSize: 12, color: "var(--text-muted)" }}>
                      <span>−</span><span>{w}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ background: "var(--bg-surface)", border: "1px solid var(--gain-border)", borderRadius: "var(--radius-lg)", padding: 24, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg, var(--gain), rgba(34,201,122,0.3))" }} />
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Optimized target</div>
              <div style={{ fontSize: 36, fontWeight: 700, color: "var(--gain)", lineHeight: 1 }}>~{perf.recommended_portfolio_return_pct}%</div>
              <div style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 8 }}>projected 12-month return</div>
              <div style={{ fontSize: 13, color: "var(--gain)", fontWeight: 600, marginTop: 6 }}>+{perf.return_improvement_pct}% improvement</div>
              {perf.capital_freed > 0 && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--gain-border)" }}>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 6 }}>Capital to redeploy</div>
                  <div style={{ display: "flex", gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--accent)" }}>${Number(perf.capital_freed).toLocaleString("en-US", { maximumFractionDigits: 0 })}</div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>freed</div>
                    </div>
                    <div style={{ color: "var(--text-muted)", fontSize: 18, alignSelf: "center" }}>→</div>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--gain)" }}>${Number(perf.capital_deployed).toLocaleString("en-US", { maximumFractionDigits: 0 })}</div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>redeployed</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {perf.current_diversification_score !== null && perf.current_diversification_score !== undefined && perf.projected_diversification_score != null && (
            <div style={{ background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: "var(--radius-lg)", padding: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>Balance score today</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: perf.current_diversification_score >= 70 ? "var(--gain)" : perf.current_diversification_score >= 50 ? "var(--warning)" : "var(--loss)" }}>
                    {perf.current_diversification_score}<span style={{ fontSize: 14, fontWeight: 400, color: "var(--text-muted)" }}>/100</span>
                  </div>
                </div>
                <div style={{ fontSize: 22, color: "var(--text-muted)" }}>→</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>After optimization</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: "var(--gain)" }}>
                    {perf.projected_diversification_score}<span style={{ fontSize: 14, fontWeight: 400, color: "var(--text-muted)" }}>/100</span>
                  </div>
                  {perf.diversification_improvement > 0 && <div style={{ fontSize: 12, color: "var(--gain)", fontWeight: 600 }}>+{perf.diversification_improvement} pts</div>}
                </div>
                <div style={{ flex: 2 }}>
                  {(() => {
                    const allPicks = [...(recommendations?.stock_picks||[]), ...(recommendations?.etf_picks||[])];
                    const gapPicks = allPicks.filter(p => p.fills_gap);
                    return gapPicks.slice(0,3).map((p, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, marginBottom: 4 }}>
                        <span style={{ background: "var(--gain-bg)", color: "var(--gain)", padding: "1px 6px", borderRadius: 4, fontWeight: 600 }}>{p.ticker}</span>
                        <span style={{ color: "var(--text-muted)" }}>adds {p.gap_sector || p.sector}</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* ACT 5: Transformation — hidden when fully optimized since nothing will actually execute */}
      {(strategy || recommendations) && !fullyOptimized && (
        <InsightsPanel strategy={strategy} portfolioData={portfolioData} recommendations={recommendations} />
      )}

      {/* ACT 5.5: Fully optimized banner */}
      {fullyOptimized && (
        <section style={{ marginBottom: 40 }}>
          <div style={{ background: "var(--gain-bg)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: "var(--radius-lg)", padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--gain)", marginBottom: 6 }}>✓ Your portfolio is fully optimized</div>
            <div style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.7 }}>
              There isn't enough deployable capital right now to fund a meaningful new position, so no buys are recommended.
              {nextReviewDate && <> Check back around <strong>{nextReviewDate}</strong> to see if market conditions have opened up new opportunities worth acting on.</>}
            </div>
          </div>
        </section>
      )}

      {/* ACT 6: Rebalancing plan — hidden when fully optimized: a reduce whose whole
          rationale is "frees capital to fund higher-conviction picks" is misleading
          when nothing is actually deployable to fund those picks right now. */}
      {(sells.length > 0 || increases.length > 0 || keeps.length > 0) && !fullyOptimized && (
        <section style={{ marginBottom: 40 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 20 }}>
            <h2 style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Rebalancing plan</h2>
            {onExecuteAll && (sells.length > 0 || increases.length > 0) && (
              <button onClick={onExecuteAll} style={{ padding: "10px 20px", borderRadius: "var(--radius-md)", border: "none", background: "var(--accent)", color: "#000", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                Execute optimization
              </button>
            )}
          </div>
          {strategy?.recommended_portfolio?.execution_summary && (
            <div style={{ background: "var(--accent-dim)", border: "1px solid rgba(232,160,48,0.15)", borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 13, color: "var(--accent)", lineHeight: 1.7, marginBottom: 16 }}>
              {strategy.recommended_portfolio.execution_summary}
            </div>
          )}
          <div style={{ background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: "var(--radius-lg)", overflow: "hidden" }}>
            {sells.length > 0 && (
              <div style={{ padding: "16px 20px", borderBottom: keeps.length > 0 || increases.length > 0 ? "1px solid var(--bg-border)" : "none" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--loss)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 12 }}>Sell / Reduce</div>
                {sells.map((p, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < sells.length - 1 ? "1px solid var(--bg-border-subtle)" : "none" }}>
                    <StockLogo ticker={p.ticker} size={24} />
                    <div style={{ minWidth: 52 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{p.ticker}</div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{p.name}</div>
                    </div>
                    <div style={{ flex: 1, fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5 }}>{p.rationale}</div>
                    <div style={{ textAlign: "right", minWidth: 130 }}>
                      <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{p.current_pct}% → {p.recommended_pct}%</div>
                      <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>${Number(p.current_value).toLocaleString("en-US",{maximumFractionDigits:0})} → ${Number(p.recommended_value).toLocaleString("en-US",{maximumFractionDigits:0})}</div>
                    </div>
                    <div style={{ minWidth: 60, textAlign: "right" }}>
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 600, background: actionColor[p.action]?.bg, color: actionColor[p.action]?.color }}>{p.action}</span>
                      {p.priority === "high" && <div style={{ fontSize: 11, color: "var(--loss)", marginTop: 2, fontWeight: 500 }}>urgent</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {increases.length > 0 && (
              <div style={{ padding: "16px 20px", borderBottom: keeps.length > 0 ? "1px solid var(--bg-border)" : "none" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--gain)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 12 }}>Buy / Increase</div>
                {increases.map((p, i) => {
                  const detailKey = p.ticker + i;
                  const isOpen = expandedTickers.has(detailKey);
                  return (
                    <div key={i} style={{ borderBottom: i < increases.length - 1 ? "1px solid var(--bg-border-subtle)" : "none" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0" }}>
                        <StockLogo ticker={p.ticker} size={24} />
                        <div style={{ minWidth: 52 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{p.ticker}</div>
                          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{p.name}</div>
                        </div>
                        <div style={{ flex: 1, fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5 }}>
                          {p.rationale}
                          {p.detail && (
                            <div>
                              <button onClick={() => toggleDetail(detailKey)} style={{ background: "none", border: "none", padding: 0, marginTop: 4, cursor: "pointer", fontSize: 12, color: "var(--accent)", fontWeight: 600 }}>
                                {isOpen ? "Hide details ▲" : "Find out more ▼"}
                              </button>
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: "right", minWidth: 130 }}>
                          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{p.current_pct > 0 ? p.current_pct+"% → " : "new → "}{p.recommended_pct}%</div>
                          <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>{p.current_value > 0 ? "$"+Number(p.current_value).toLocaleString("en-US",{maximumFractionDigits:0})+" → " : ""}${Number(p.recommended_value).toLocaleString("en-US",{maximumFractionDigits:0})}</div>
                        </div>
                        <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 600, background: actionColor[p.action]?.bg, color: actionColor[p.action]?.color, minWidth: 60, textAlign: "center" }}>{p.action}</span>
                      </div>
                      {isOpen && p.detail && (
                        <div style={{ padding: "0 0 16px 64px" }}>
                          <PickDetailPanel detail={p.detail} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {keeps.length > 0 && (
              <div style={{ padding: "16px 20px" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 12 }}>Keep</div>
                {keeps.map((p, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < keeps.length - 1 ? "1px solid var(--bg-border-subtle)" : "none" }}>
                    <StockLogo ticker={p.ticker} size={24} />
                    <div style={{ minWidth: 52 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{p.ticker}</div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{p.name}</div>
                    </div>
                    <div style={{ flex: 1, fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5 }}>{p.rationale}</div>
                    <div style={{ textAlign: "right", minWidth: 130 }}>
                      <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{p.recommended_pct}% of portfolio</div>
                      <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>${Number(p.recommended_value).toLocaleString("en-US",{maximumFractionDigits:0})}</div>
                    </div>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 600, background: actionColor["KEEP"]?.bg, color: actionColor["KEEP"]?.color, minWidth: 60, textAlign: "center" }}>KEEP</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ACT 7: Execute CTA */}
      {onExecuteAll && (sells.length > 0 || increases.length > 0) && !fullyOptimized && (
        <section style={{ marginBottom: 40 }}>
          <div style={{ background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: "var(--radius-lg)", padding: 28, textAlign: "center" }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-primary)", marginBottom: 8 }}>Ready to execute?</div>
            <div style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 24, maxWidth: 400, margin: "0 auto 24px" }}>
              {sells.length} sell{sells.length !== 1 ? "s" : ""} + {increases.length} buy{increases.length !== 1 ? "s" : ""} — each order goes to IBKR as an instruction. Nothing executes until you confirm.
            </div>
            <button onClick={onExecuteAll} style={{ padding: "14px 40px", borderRadius: "var(--radius-md)", border: "none", background: "var(--accent)", color: "#000", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
              Execute Portfolio Optimization
            </button>
          </div>
        </section>
      )}
    </div>
  );
}


function PortfolioInputScreen({ onPortfolioLoaded, onSkipToMarketData }) {
  const [method, setMethod] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [preview, setPreview] = React.useState(null);
  const [manualPositions, setManualPositions] = React.useState([
    { ticker: "", shares: "", avg_cost: "" }
  ]);
  const [cashSource, setCashSource] = React.useState("none");
  const [cashAmount, setCashAmount] = React.useState("");

  const handleScreenshot = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true); setError(""); setStatus("Analyzing portfolio screenshot with AI...");
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const b64 = ev.target.result.split(",")[1];
        const r = await axios.post(`${API}/api/portfolio/upload-screenshot`,
          { image: b64, media_type: file.type || "image/jpeg" }, { timeout: 60000 });
        if (r.data.connected && r.data.portfolio?.length > 0) { setPreview(r.data); }
        else setError(r.data.error || "Could not extract portfolio. Try a clearer screenshot.");
      } catch(e) { setError("Analysis failed. Please try again."); }
      setLoading(false); setStatus("");
    };
    reader.readAsDataURL(file);
  };

  const handleCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true); setError(""); setStatus("Parsing portfolio file...");
    try {
      const isExcel = /\.xlsx$/i.test(file.name);
      let r;
      if (isExcel) {
        const buf = await file.arrayBuffer();
        const b64 = btoa(new Uint8Array(buf).reduce((s, b) => s + String.fromCharCode(b), ""));
        r = await axios.post(`${API}/api/portfolio/upload-excel`, { file: b64 }, { timeout: 30000 });
      } else {
        const text = await file.text();
        r = await axios.post(`${API}/api/portfolio/upload-csv`, { csv_content: text }, { timeout: 30000 });
      }
      if (r.data.connected && r.data.portfolio?.length > 0) setPreview(r.data);
      else setError(r.data.error || "Could not parse file. Check the format.");
    } catch(e) { setError("Upload failed."); }
    setLoading(false); setStatus("");
  };

  const handleManual = async () => {
    const valid = manualPositions.filter(p => p.ticker && p.shares);
    if (!valid.length) { setError("Add at least one position."); return; }
    setLoading(true); setError(""); setStatus("Fetching current prices...");
    try {
      const r = await axios.post(`${API}/api/portfolio/manual`,
        { positions: valid.map(p => ({ ticker: p.ticker.toUpperCase(), shares: parseFloat(p.shares), avg_cost: parseFloat(p.avg_cost) || 0 })) },
        { timeout: 30000 });
      if (r.data.connected) setPreview(r.data);
      else setError(r.data.error || "Failed.");
    } catch(e) { setError("Failed. Try again."); }
    setLoading(false); setStatus("");
  };

  const addRow = () => setManualPositions(p => [...p, { ticker: "", shares: "", avg_cost: "" }]);
  const updateRow = (i, f, v) => setManualPositions(p => p.map((r, idx) => idx === i ? { ...r, [f]: v } : r));
  const removeRow = (i) => setManualPositions(p => p.filter((_, idx) => idx !== i));

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 16, color: "var(--accent)", fontWeight: 500, marginBottom: 8 }}>{status}</div>
        <div style={{ fontSize: 14, color: "var(--text-muted)" }}>Please wait...</div>
      </div>
    </div>
  );

  if (preview) return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", fontFamily: "'DM Sans', system-ui, sans-serif", padding: 20 }}>
      <div style={{ maxWidth: 700, margin: "0 auto" }}>
        <div style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 24, marginBottom: 16, boxShadow: "var(--shadow-card)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 600 }}>Portfolio detected</div>
              <div style={{ fontSize: 14, color: "var(--text-muted)", marginTop: 2 }}>
                {preview.portfolio.length} positions · ${Number(preview.net_liquidation).toLocaleString("en-US", { maximumFractionDigits: 0 })} total
                {preview.broker && preview.broker !== "unknown" && ` · ${preview.broker}`}
                {preview.source === "screenshot" && preview.confidence && ` · ${preview.confidence} confidence`}
              </div>
            </div>
            <button onClick={() => { setPreview(null); setMethod(null); }} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid var(--bg-border)", background: "var(--bg-surface)", cursor: "pointer", fontSize: 14, color: "var(--text-secondary)" }}>Start over</button>
          </div>
          <div style={{ maxHeight: 320, overflowY: "auto", overflowX: "auto" }}>
            <table style={{ width: "100%", minWidth: 480, borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--bg-border)" }}>
                  {["Ticker", "Name", "Shares", "Price", "Value", "P&L"].map(h => (
                    <th key={h} style={{ textAlign: h === "Ticker" || h === "Name" ? "left" : "right", padding: "6px 8px", color: "var(--text-muted)", fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.portfolio.map((p, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid var(--bg-border)" }}>
                    <td style={{ padding: "8px", fontWeight: 600 }}>{p.ticker}</td>
                    <td style={{ padding: "8px", color: "var(--text-secondary)", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</td>
                    <td style={{ padding: "8px", textAlign: "right" }}>{p.position}</td>
                    <td style={{ padding: "8px", textAlign: "right" }}>${(p.market_price || 0).toFixed(2)}</td>
                    <td style={{ padding: "8px", textAlign: "right", fontWeight: 500 }}>${Number(p.market_value).toLocaleString("en-US", { maximumFractionDigits: 0 })}</td>
                    <td style={{ padding: "8px", textAlign: "right", color: (p.pnl_pct || 0) >= 0 ? "var(--gain)" : "var(--loss)" }}>{(p.pnl_pct || 0) >= 0 ? "+" : ""}{(p.pnl_pct || 0).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {error && <div style={{ background: "var(--loss-bg)", color: "var(--loss)", padding: "10px 14px", borderRadius: 8, fontSize: 14, marginBottom: 12 }}>{error}</div>}
                {/* Cash available question */}
        <div style={{ background: "var(--bg-surface)", borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Cash ready to deploy?</div>
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>We'll put it to work alongside any capital freed from rebalancing.</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            {[{ id: "none", label: "No, just optimize existing portfolio" }, { id: "broker", label: "Yes, in my brokerage" }, { id: "outside", label: "Yes, outside broker" }].map(opt => (
              <button key={opt.id} onClick={() => setCashSource(opt.id)}
                style={{ padding: "7px 12px", borderRadius: 8, border: cashSource === opt.id ? "1.5px solid var(--accent)" : "1px solid var(--bg-border)", background: cashSource === opt.id ? "var(--accent-dim)" : "var(--bg-elevated)", cursor: "pointer", fontSize: 13, fontWeight: cashSource === opt.id ? 600 : 400, color: cashSource === opt.id ? "var(--accent)" : "var(--text-secondary)" }}>
                {opt.label}
              </button>
            ))}
          </div>
          {(cashSource === "broker" || cashSource === "outside") && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 16, color: "var(--text-secondary)", fontWeight: 500 }}>$</span>
              <input type="number" value={cashAmount} onChange={e => setCashAmount(e.target.value)}
                placeholder="Enter amount" min="0"
                style={{ flex: 1, padding: "8px 12px", borderRadius: 8, border: "1px solid var(--bg-border)", fontSize: 16, outline: "none", background: "var(--bg-surface)", color: "var(--text-primary)" }} />
            </div>
          )}
        </div>
        <button onClick={() => {
          const cash = (cashSource === "broker" || cashSource === "outside") ? parseFloat(cashAmount) || 0 : 0;
          onPortfolioLoaded({ ...preview, available_cash: cash, cash_source: cashSource || "none" });
        }} style={{ width: "100%", padding: "14px", borderRadius: 10, border: "none", background: "var(--accent)", color: "#fff", fontSize: 17, fontWeight: 600, cursor: "pointer" }}>
          Analyze my portfolio →
        </button>
      </div>
    </div>
  );

  if (!method) return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', system-ui, sans-serif", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 600 }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}><FlintLogo size="lg" /></div>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}><AIBadge /></div>
          <div style={{ fontSize: 15, color: "var(--text-secondary)" }}>Your portfolio. Optimized by AI.</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
          {[
            { id: "screenshot", icon: "📸", title: "Screenshot", desc: "Snap a photo of your holdings from any broker app", badge: "Quickest" },
            { id: "csv", icon: "📄", title: "CSV / Excel", desc: "Upload a CSV or Excel export from your broker", badge: null },
            { id: "manual", icon: "✏️", title: "Manual entry", desc: "Enter your positions manually", badge: null },
            { id: "cash", icon: "💵", title: "Cash only", desc: "Start fresh — tell me how much you want to invest", badge: null },
          ].map(opt => (
            <button key={opt.id} onClick={() => setMethod(opt.id)} style={{ background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: 14, padding: 20, cursor: "pointer", textAlign: "left", position: "relative", color: "var(--text-primary)" }}>
              {opt.badge && <div style={{ position: "absolute", top: 10, right: 10, background: "var(--accent)", color: "#fff", fontSize: 11, padding: "2px 6px", borderRadius: 10, fontWeight: 600 }}>{opt.badge}</div>}
              <div style={{ fontSize: 28, marginBottom: 10 }}>{opt.icon}</div>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6, color: "var(--text-primary)" }}>{opt.title}</div>
              <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5 }}>{opt.desc}</div>
            </button>
          ))}
        </div>
        {onSkipToMarketData && (
          <div style={{ textAlign: "center", marginTop: 24 }}>
            <button onClick={onSkipToMarketData} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: 13, textDecoration: "underline" }}>
              Not ready to upload — switch to Market Data instead
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (method === "screenshot") return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', system-ui, sans-serif", padding: 20 }}>
      <div style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 32, width: "100%", maxWidth: 480, boxShadow: "var(--shadow-elevated)" }}>
        <button onClick={() => setMethod(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: 14, marginBottom: 20, padding: 0 }}>← Back</button>
        <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 6 }}>Upload portfolio screenshot</div>
        <div style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 24, lineHeight: 1.6 }}>Take a screenshot of your portfolio from any broker — Robinhood, Schwab, Fidelity, IBKR, or any other. AI will extract your positions automatically.</div>
        {error && <div style={{ background: "var(--loss-bg)", color: "var(--loss)", padding: "10px 14px", borderRadius: 8, fontSize: 14, marginBottom: 16 }}>{error}</div>}
        <label style={{ display: "block", border: "2px dashed var(--bg-border)", borderRadius: 12, padding: 32, textAlign: "center", cursor: "pointer", background: "var(--bg-surface)" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📸</div>
          <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>Click to upload or drag & drop</div>
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>JPG, PNG, WebP, HEIC supported</div>
          <input type="file" accept="image/*" onChange={handleScreenshot} style={{ display: "none" }} />
        </label>
        <div style={{ marginTop: 16, fontSize: 13, color: "var(--text-muted)", textAlign: "center" }}>Your image is processed securely and never stored.</div>
      </div>
    </div>
  );

  if (method === "csv") return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', system-ui, sans-serif", padding: 20 }}>
      <div style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 32, width: "100%", maxWidth: 480, boxShadow: "var(--shadow-elevated)" }}>
        <button onClick={() => setMethod(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: 14, marginBottom: 20, padding: 0 }}>← Back</button>
        <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 6 }}>Upload portfolio file</div>
        <div style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 20 }}>Export your portfolio as CSV from your broker and upload it here.</div>
        <div style={{ background: "var(--bg-surface)", borderRadius: 10, padding: 14, marginBottom: 20, fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.8 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>How to export:</div>
          <div>· <strong>IBKR:</strong> Portfolio → Export → CSV</div>
          <div>· <strong>Schwab:</strong> Positions → Export</div>
          <div>· <strong>Fidelity:</strong> Positions → Download CSV</div>
          <div>· <strong>Robinhood:</strong> Account → Statements → CSV</div>
        </div>
        {error && <div style={{ background: "var(--loss-bg)", color: "var(--loss)", padding: "10px 14px", borderRadius: 8, fontSize: 14, marginBottom: 16 }}>{error}</div>}
        <label style={{ display: "block", border: "2px dashed var(--bg-border)", borderRadius: 12, padding: 32, textAlign: "center", cursor: "pointer", background: "var(--bg-surface)" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
          <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>Click to upload CSV or Excel</div>
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>.csv and .xlsx supported</div>
          <input type="file" accept=".csv,.xlsx,text/csv" onChange={handleCSV} style={{ display: "none" }} />
        </label>
      </div>
    </div>
  );

  if (method === "cash") return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', system-ui, sans-serif", padding: 20 }}>
      <div style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 32, width: "100%", maxWidth: 440, boxShadow: "var(--shadow-elevated)" }}>
        <button onClick={() => setMethod(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: 14, marginBottom: 20, padding: 0 }}>← Back</button>
        <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 6 }}>How much do you want to invest?</div>
        <div style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 24, lineHeight: 1.6 }}>We'll recommend the best portfolio to build from scratch with your available capital.</div>
        {error && <div style={{ background: "var(--loss-bg)", color: "var(--loss)", padding: "10px 14px", borderRadius: 8, fontSize: 14, marginBottom: 16 }}>{error}</div>}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 6, fontWeight: 500 }}>Investment amount</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18, color: "var(--text-secondary)", fontWeight: 500 }}>$</span>
            <input type="number" value={cashAmount} onChange={e => setCashAmount(e.target.value)}
              placeholder="10,000" min="1000"
              style={{ flex: 1, padding: "10px 12px", borderRadius: 8, border: "1px solid var(--bg-border)", fontSize: 18, outline: "none", background: "var(--bg-surface)", color: "var(--text-primary)" }} />
          </div>
        </div>
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 8, fontWeight: 500 }}>Where is this cash?</div>
          {["In my brokerage account", "Outside my broker (will transfer)", "Just exploring options"].map((opt, i) => (
            <label key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", cursor: "pointer" }}>
              <input type="radio" name="cashLoc" value={opt} checked={cashSource === opt} onChange={() => setCashSource(opt)}
                style={{ accentColor: "var(--accent)" }} />
              <span style={{ fontSize: 15, color: "var(--text-secondary)" }}>{opt}</span>
            </label>
          ))}
        </div>
        <button onClick={() => {
          const amt = parseFloat(cashAmount);
          if (!amt || amt < 100) { setError("Please enter a valid amount (min $100)"); return; }
          onPortfolioLoaded({
            connected: true,
            portfolio: [],
            net_liquidation: amt,
            available_cash: amt,
            cash_source: cashSource || "In my brokerage account",
            source: "cash"
          });
        }} style={{ width: "100%", padding: "12px", borderRadius: 10, border: "none", background: "var(--accent)", color: "#fff", fontSize: 16, fontWeight: 600, cursor: "pointer" }}>
          Build my portfolio →
        </button>
      </div>
    </div>
  );

  if (method === "manual") return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", fontFamily: "'DM Sans', system-ui, sans-serif", padding: 20 }}>
      <div style={{ maxWidth: 600, margin: "0 auto" }}>
        <button onClick={() => setMethod(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: 14, marginBottom: 20, padding: 0 }}>← Back</button>
        <div style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 24, boxShadow: "var(--shadow-elevated)" }}>
          <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>Enter your positions</div>
          <div style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 20 }}>Current prices fetched automatically.</div>
          {error && <div style={{ background: "var(--loss-bg)", color: "var(--loss)", padding: "10px 14px", borderRadius: 8, fontSize: 14, marginBottom: 16 }}>{error}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 28px", gap: 8, marginBottom: 8 }}>
            {["Ticker", "Shares", "Avg cost (opt.)", ""].map(h => (
              <div key={h} style={{ fontSize: 13, color: "var(--text-muted)", fontWeight: 500, padding: "0 4px" }}>{h}</div>
            ))}
          </div>
          {manualPositions.map((pos, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 28px", gap: 8, marginBottom: 8 }}>
              <input value={pos.ticker} onChange={e => updateRow(i, "ticker", e.target.value.toUpperCase())} placeholder="AAPL"
                style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid var(--bg-border)", fontSize: 15, outline: "none", background: "var(--bg-surface)" }} />
              <input value={pos.shares} onChange={e => updateRow(i, "shares", e.target.value)} placeholder="10" type="number"
                style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid var(--bg-border)", fontSize: 15, outline: "none", background: "var(--bg-surface)" }} />
              <input value={pos.avg_cost} onChange={e => updateRow(i, "avg_cost", e.target.value)} placeholder="150.00" type="number"
                style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid var(--bg-border)", fontSize: 15, outline: "none", background: "var(--bg-surface)" }} />
              <button onClick={() => removeRow(i)} style={{ padding: 0, border: "none", background: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: 20 }}>×</button>
            </div>
          ))}
          <button onClick={addRow} style={{ marginTop: 4, marginBottom: 20, padding: "8px 14px", borderRadius: 8, border: "1px solid var(--bg-border)", background: "var(--bg-surface)", cursor: "pointer", fontSize: 14, color: "var(--text-secondary)" }}>+ Add position</button>
          <button onClick={handleManual} style={{ width: "100%", padding: "12px", borderRadius: 10, border: "none", background: "var(--accent)", color: "#fff", fontSize: 16, fontWeight: 600, cursor: "pointer" }}>Load portfolio</button>
        </div>
      </div>
    </div>
  );

  return null;
}

// ── AUTH / ONBOARDING SCREENS ─────────────────────────────────────────────────

function AuthScreen({ onAuth }) {
  const [mode, setMode] = React.useState("login");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [name, setName] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const googleBtnRef = React.useRef(null);
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

  React.useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (!window.google) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
      });
      if (googleBtnRef.current) {
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: "outline", size: "large", width: 340,
        });
      }
    };
    document.body.appendChild(script);
    return () => { try { document.body.removeChild(script); } catch (e) {} };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogleResponse = async (response) => {
    setLoading(true);
    setError("");
    try {
      const r = await axios.post(`${API}/api/auth/google`, { credential: response.credential });
      if (r.data.token) onAuth(r.data);
      else setError(r.data.error || "Google sign in failed");
    } catch (e) {
      setError("Google sign in failed. Please try again.");
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const endpoint = mode === "login" ? "/api/auth/login" : "/api/auth/register";
      const payload = mode === "login" ? { email, password } : { email, password, name };
      const r = await axios.post(`${API}${endpoint}`, payload);
      if (r.data.token) onAuth(r.data);
      else setError(r.data.error || "Authentication failed");
    } catch (e) {
      setError(e.response?.data?.error || "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const inputStyle = { width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--bg-border)", fontSize: 16, boxSizing: "border-box", outline: "none", background: "var(--bg-surface)", color: "var(--text-primary)" };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 40, width: 380, boxShadow: "var(--shadow-elevated)" }}>
        <div style={{ marginBottom: 10 }}><FlintLogo size="lg" /></div>
        <div style={{ marginBottom: 20 }}><AIBadge /></div>
        <div style={{ fontSize: 15, color: "var(--text-muted)", marginBottom: 28 }}>{mode === "login" ? "Sign in to your account" : "Create your account"}</div>

        {GOOGLE_CLIENT_ID && (
          <>
            <div ref={googleBtnRef} style={{ marginBottom: 16 }} />
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
              <div style={{ flex: 1, height: 1, background: "var(--bg-border)" }} />
              <span style={{ fontSize: 14, color: "var(--text-muted)" }}>or</span>
              <div style={{ flex: 1, height: 1, background: "var(--bg-border)" }} />
            </div>
          </>
        )}

        <form onSubmit={handleSubmit}>
          {mode === "register" && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 4 }}>Name</div>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" style={inputStyle} />
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 4 }}>Email</div>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required style={inputStyle} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 4 }}>Password</div>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required style={inputStyle} />
          </div>
          {error && (
            <div style={{ color: "var(--loss)", fontSize: 14, marginBottom: 12, padding: "8px 12px", background: "var(--loss-bg)", borderRadius: 6 }}>{error}</div>
          )}
          <button type="submit" disabled={loading}
            style={{ width: "100%", padding: 12, borderRadius: 10, border: "none", background: "var(--accent)", color: "#fff", fontSize: 16, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}>
            {loading ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>

        <div style={{ textAlign: "center", marginTop: 16, fontSize: 15, color: "var(--text-muted)" }}>
          {mode === "login" ? (
            <>Don't have an account?{" "}
              <button onClick={() => { setMode("register"); setError(""); }} style={{ background: "none", border: "none", color: "var(--accent)", cursor: "pointer", fontSize: 15, padding: 0 }}>Create one</button>
            </>
          ) : (
            <>Already have an account?{" "}
              <button onClick={() => { setMode("login"); setError(""); }} style={{ background: "none", border: "none", color: "var(--accent)", cursor: "pointer", fontSize: 15, padding: 0 }}>Sign in</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const QUESTIONS = [
  { id: "q1", text: "How old are you?", options: ["Under 30", "30–45", "45–60", "Over 60"] },
  { id: "q2", text: "What is your primary investment goal?", options: ["Preserve capital (protect what I have)", "Generate income (regular dividends/returns)", "Grow my wealth (long-term appreciation)", "Maximize returns (aggressive growth)"] },
  { id: "q3", text: "When do you plan to use this money?", options: ["Within 2 years", "2–5 years", "5–10 years", "10+ years"] },
  { id: "q4", text: "Imagine your portfolio drops 25% in 3 months. What do you do?", options: ["Sell everything immediately", "Sell some to reduce risk", "Hold and wait for recovery", "Buy more — great opportunity"] },
  { id: "q5", text: "What is your investment experience?", options: ["Beginner (< 2 years)", "Intermediate (2–5 years)", "Experienced (5–10 years)", "Expert (10+ years)"] },
  { id: "q6", text: "What % of your investable assets does this portfolio represent?", options: ["Less than 20%", "20–50%", "50–80%", "More than 80%"] },
  { id: "q7", text: "How would you describe your income stability?", options: ["Unstable / freelance", "Somewhat stable", "Stable salary", "Multiple income sources"] },
  { id: "q8", text: "What annual return would satisfy you?", options: ["5–8% (market average)", "10–14% (above average)", "15–20% (high growth)", "20%+ (maximum growth, accept high risk)"] },
];

function QuestionnaireScreen({ onComplete }) {
  const [step, setStep] = React.useState(0);
  const [answers, setAnswers] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const q = QUESTIONS[step];
  const isLast = step === QUESTIONS.length - 1;

  const next = async () => {
    if (answers[q.id] === undefined) return;
    if (!isLast) { setStep(s => s + 1); return; }
    setLoading(true);
    setError("");
    try {
      const r = await axios.post(`${API}/api/investor-profile/submit`, { answers });
      if (r.data.profile_type) onComplete(answers, r.data);
      else setError(r.data.error || "Failed to save profile");
    } catch (e) {
      setError("Failed to save profile. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 40, width: 480, boxShadow: "var(--shadow-elevated)" }}>
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 15, color: "var(--text-muted)" }}>Question {step + 1} of {QUESTIONS.length}</div>
            <div style={{ fontSize: 15, color: "var(--text-muted)" }}>{Math.round((step / QUESTIONS.length) * 100)}%</div>
          </div>
          <div style={{ height: 4, background: "var(--bg-primary)", borderRadius: 2 }}>
            <div style={{ height: "100%", width: `${(step / QUESTIONS.length) * 100}%`, background: "var(--accent)", borderRadius: 2, transition: "width 0.3s" }} />
          </div>
        </div>

        <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 24, lineHeight: 1.4 }}>{q.text}</div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
          {q.options.map((opt, i) => {
            const selected = answers[q.id] === i + 1;
            return (
              <button key={i} onClick={() => setAnswers(prev => ({ ...prev, [q.id]: i + 1 }))}
                style={{ padding: "14px 18px", borderRadius: 10, border: `1.5px solid ${selected ? "var(--accent)" : "var(--bg-border)"}`, background: selected ? "var(--accent-dim)" : "var(--bg-elevated)", cursor: "pointer", fontSize: 16, textAlign: "left", color: selected ? "var(--accent)" : "var(--text-secondary)", fontWeight: selected ? 600 : 400, transition: "all 0.15s" }}>
                {opt}
              </button>
            );
          })}
        </div>

        {error && <div style={{ color: "var(--loss)", fontSize: 14, marginBottom: 12, padding: "8px 12px", background: "var(--loss-bg)", borderRadius: 6 }}>{error}</div>}

        <div style={{ display: "flex", gap: 10 }}>
          {step > 0 && (
            <button onClick={() => setStep(s => s - 1)}
              style={{ flex: 1, padding: 12, borderRadius: 10, border: "1px solid var(--bg-border)", background: "var(--bg-surface)", color: "var(--text-secondary)", fontSize: 16, cursor: "pointer" }}>
              Back
            </button>
          )}
          <button onClick={next} disabled={answers[q.id] === undefined || loading}
            style={{ flex: 2, padding: 12, borderRadius: 10, border: "none", background: answers[q.id] !== undefined ? "var(--accent)" : "#ccc", color: "#fff", fontSize: 16, fontWeight: 600, cursor: answers[q.id] !== undefined ? "pointer" : "not-allowed", opacity: loading ? 0.7 : 1 }}>
            {loading ? "Saving..." : isLast ? "See my profile" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProfileReportScreen({ profile, onAccept, onRetake }) {
  const typeColors = { conservative: "var(--accent)", moderate: "var(--gain)", balanced_growth: "var(--warning)", aggressive: "var(--accent)", very_aggressive: "var(--loss)" };
  const color = typeColors[profile?.profile_type] || "var(--accent)";
  const rows = [
    ["Target annual return", `${profile?.target_return}%`],
    ["Risk tolerance", profile?.risk_label],
    ["Max acceptable loss", `-${profile?.max_drawdown}%`],
    ["Investment horizon", profile?.horizon],
    ["Style", profile?.style],
  ];
  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 40, width: 440, boxShadow: "var(--shadow-elevated)" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: 15, color: "var(--text-muted)", marginBottom: 8 }}>Your Investor Profile</div>
          <div style={{ fontSize: 24, fontWeight: 700, color }}>{profile?.label}</div>
        </div>
        <div style={{ background: "var(--bg-surface)", borderRadius: 12, padding: 20, marginBottom: 20 }}>
          {rows.map(([label, value]) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: "1px solid var(--bg-border)" }}>
              <span style={{ fontSize: 15, color: "var(--text-muted)" }}>{label}</span>
              <span style={{ fontSize: 15, fontWeight: 500, color: "var(--text-primary)" }}>{value}</span>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 15, color: "var(--text-secondary)", lineHeight: 1.7, marginBottom: 24, padding: "14px 16px", background: "var(--bg-primary)", borderRadius: 10 }}>
          <div style={{ fontWeight: 500, marginBottom: 6, color: "var(--text-primary)" }}>What this means for you</div>
          {profile?.description}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onRetake} style={{ flex: 1, padding: 12, borderRadius: 10, border: "1px solid var(--bg-border)", background: "var(--bg-surface)", color: "var(--text-secondary)", fontSize: 16, cursor: "pointer" }}>
            Retake questionnaire
          </button>
          <button onClick={onAccept} style={{ flex: 2, padding: 12, borderRadius: 10, border: "none", background: "var(--accent)", color: "#fff", fontSize: 16, fontWeight: 600, cursor: "pointer" }}>
            This looks right →
          </button>
        </div>
      </div>
    </div>
  );
}

function DisclaimerScreen({ onAccept }) {
  const [agreed, setAgreed] = React.useState(false);
  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 40, width: 480, boxShadow: "var(--shadow-elevated)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <span style={{ fontSize: 24 }}>⚠️</span>
          <div style={{ fontSize: 18, fontWeight: 600 }}>Important Notice Before You Begin</div>
        </div>
        <div style={{ fontSize: 15, color: "var(--text-secondary)", lineHeight: 1.75, marginBottom: 20 }}>
          <p style={{ marginTop: 0 }}>This platform is an advisory tool designed to help you explore portfolio optimization strategies based on publicly available market data and analytical models.</p>
          <p><strong style={{ color: "var(--text-primary)" }}>All investment decisions remain entirely yours.</strong> We provide analysis and suggestions — you decide what to do with your money.</p>
          <ul style={{ paddingLeft: 18, margin: "12px 0" }}>
            <li>Past performance does not guarantee future results</li>
            <li>Market conditions can change rapidly and unexpectedly</li>
            <li>This tool does not constitute financial advice</li>
            <li>Always consult a licensed financial advisor before making significant investment decisions</li>
          </ul>
          <p style={{ marginBottom: 0 }}>By continuing, you acknowledge that you understand the risks involved in investing and that this platform is a research and planning aid, not a financial advisor.</p>
        </div>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginBottom: 20 }}>
          <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ marginTop: 2, width: 16, height: 16, cursor: "pointer" }} />
          <span style={{ fontSize: 15, color: "var(--text-secondary)" }}>I understand and agree to continue</span>
        </label>
        <button onClick={onAccept} disabled={!agreed}
          style={{ width: "100%", padding: 13, borderRadius: 10, border: "none", background: agreed ? "var(--accent)" : "#ccc", color: "#fff", fontSize: 16, fontWeight: 600, cursor: agreed ? "pointer" : "not-allowed" }}>
          Let's get started →
        </button>
      </div>
    </div>
  );
}

function BetaAgreementScreen({ onAccept }) {
  const [checked, setChecked] = React.useState(false);
  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', system-ui, sans-serif", padding: "40px 16px" }}>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}><FlintLogo size="lg" /></div>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}><AIBadge /></div>
      <div style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 28, maxWidth: 560, width: "100%", boxShadow: "var(--shadow-elevated)" }}>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Before you start</div>
        <div style={{ maxHeight: 320, overflowY: "auto", padding: "4px 4px 4px 0", marginBottom: 20, fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
          {BETA_AGREEMENT_TEXT}
        </div>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 20, fontSize: 14, color: "var(--text-secondary)", cursor: "pointer" }}>
          <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} style={{ marginTop: 3 }} />
          I've read and accept these terms.
        </label>
        <button onClick={onAccept} disabled={!checked}
          style={{ width: "100%", padding: "12px", borderRadius: 10, border: "none", background: "var(--accent)", color: "#14100a", fontSize: 15, fontWeight: 600, cursor: checked ? "pointer" : "not-allowed", opacity: checked ? 1 : 0.5 }}>
          Accept & continue
        </button>
      </div>
    </div>
  );
}

function MvpTierSelectScreen({ onSelect, preselected }) {
  const [loading, setLoading] = React.useState(preselected || null);
  // Marketing site CTA already told us which tier they picked — submit it
  // immediately instead of asking again.
  React.useEffect(() => {
    if (preselected) onSelect(preselected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (preselected) return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div style={{ fontSize: 15, color: "var(--text-muted)" }}>Setting up your {preselected === "portfolio_optimizer" ? "Portfolio Optimizer" : "Market Data"} account...</div>
    </div>
  );
  const tiers = [
    {
      id: "market_data", name: "Market Data", tagline: "Browse the market",
      accent: "var(--text-secondary)",
      features: ["Search any ticker for institutional-grade research", "Cue's daily pick, translated into plain English", "No account connection required"],
      cta: "Continue with Market Data",
    },
    {
      id: "portfolio_optimizer", name: "Portfolio Optimizer", tagline: "Get a full read on your holdings",
      accent: "var(--accent)",
      badge: "Most popular",
      features: ["Upload a CSV, Excel file, or a photo of your holdings", "Sector allocation, overlap & diversification score", "Tailored buy/sell recommendations", "No brokerage login needed"],
      cta: "Continue with Portfolio Optimizer",
    },
  ];
  const select = async (id) => {
    setLoading(id);
    await onSelect(id);
    setLoading(null);
  };
  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', system-ui, sans-serif", padding: "40px 16px" }}>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}><FlintLogo size="lg" /></div>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}><AIBadge /></div>
      <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 6, textAlign: "center" }}>Choose how you want to use Cue</div>
      <div style={{ fontSize: 15, color: "var(--text-muted)", marginBottom: 28, textAlign: "center" }}>Both are free to use, with no brokerage login required.</div>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", justifyContent: "center", maxWidth: 700, width: "100%" }}>
        {tiers.map(t => (
          <div key={t.id} style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 28, flex: "1 1 280px", maxWidth: 320, boxShadow: "var(--shadow-elevated)", border: `2px solid ${t.id === "portfolio_optimizer" ? t.accent : "var(--bg-border)"}`, position: "relative", display: "flex", flexDirection: "column" }}>
            {t.badge && <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: t.accent, color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 12px", borderRadius: 20, whiteSpace: "nowrap" }}>{t.badge}</div>}
            <div style={{ fontSize: 11, fontWeight: 600, color: t.accent, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 6 }}>{t.name}</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>{t.tagline}</div>
            <ul style={{ margin: "0 0 24px", padding: 0, listStyle: "none", flex: 1 }}>
              {t.features.map((f, i) => (
                <li key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 10, fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.4 }}>
                  <span style={{ color: t.accent, fontWeight: 700, marginTop: 1, flexShrink: 0 }}>✓</span>{f}
                </li>
              ))}
            </ul>
            <button onClick={() => select(t.id)} disabled={loading === t.id}
              style={{ width: "100%", padding: "11px", borderRadius: 10, border: t.id === "portfolio_optimizer" ? "none" : `1.5px solid ${t.accent}`, background: t.id === "portfolio_optimizer" ? t.accent : "transparent", color: t.id === "portfolio_optimizer" ? "#fff" : t.accent, fontSize: 15, fontWeight: 600, cursor: "pointer", opacity: loading === t.id ? 0.7 : 1 }}>
              {loading === t.id ? "..." : t.cta}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function TierSelectScreen({ onSelect }) {
  const [loading, setLoading] = React.useState(false);
  const [billing, setBilling] = React.useState("monthly");

  const tiers = [
    {
      id: "free", name: "Free", tagline: "Explore the market", monthlyPrice: 0,
      accent: "var(--text-secondary)",
      features: ["Daily market news & insights", "Sector & earnings commentary", "General watchlist, up to 5 tickers"],
      excluded: ["Personal portfolio recommendations", "Dollar-impact projections"],
      cta: "Start for free",
    },
    {
      id: "basic", name: "Plus", tagline: "Analyze your portfolio", monthlyPrice: 12,
      accent: "var(--accent)",
      badge: "Most popular",
      features: ["Personal portfolio recommendations", "Up to 3 connected accounts", "Daily recommendation refresh", "Dollar-impact projections", "AI Portfolio Agent, ask it anything"],
      excluded: ["Tax-aware suggestions"],
      cta: "Get Plus",
    },
    {
      id: "premium", name: "Pro", tagline: "Execute with confidence", monthlyPrice: 29,
      accent: "var(--gain)",
      badge: "Most powerful",
      features: ["Everything in Plus", "Connect your IBKR account live", "Real-time portfolio sync", "One-click Execute Portfolio Optimization", "All orders reviewed before execution", "Priority support"],
      cta: "Get Pro",
    },
  ];

  const priceLabel = (t) => {
    if (t.monthlyPrice === 0) return "Always free";
    if (billing === "annual") {
      const annualMonthly = (t.monthlyPrice * 0.9).toFixed(2).replace(/\.00$/, "");
      return `$${annualMonthly}/mo, billed annually (free during beta)`;
    }
    return `$${t.monthlyPrice}/mo (free during beta)`;
  };

  const select = async (tierId) => {
    setLoading(tierId);
    try { await axios.post(`${API}/api/auth/set-tier`, { tier: tierId }); } catch (e) {}
    onSelect(tierId);
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', system-ui, sans-serif", padding: "40px 16px" }}>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}><FlintLogo size="lg" /></div>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}><AIBadge /></div>
      <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 6, textAlign: "center" }}>Choose your plan</div>
      <div style={{ fontSize: 15, color: "var(--text-muted)", marginBottom: 24, textAlign: "center" }}>Start with your investor profile, then pick how you want to use Cue.</div>
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 10, marginBottom: 28 }}>
        <button onClick={() => setBilling("monthly")}
          style={{ fontSize: 13, fontWeight: 600, padding: "8px 18px", borderRadius: 20, border: "1px solid var(--bg-border)", background: billing === "monthly" ? "var(--accent)" : "var(--bg-surface)", color: billing === "monthly" ? "#14100a" : "var(--text-secondary)", cursor: "pointer" }}>
          Monthly
        </button>
        <button onClick={() => setBilling("annual")}
          style={{ fontSize: 13, fontWeight: 600, padding: "8px 18px", borderRadius: 20, border: "1px solid var(--bg-border)", background: billing === "annual" ? "var(--accent)" : "var(--bg-surface)", color: billing === "annual" ? "#14100a" : "var(--text-secondary)", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
          Annual
          <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 12, background: billing === "annual" ? "rgba(20,16,10,0.18)" : "var(--gain-bg)", color: billing === "annual" ? "#14100a" : "var(--gain)" }}>Save 10%</span>
        </button>
      </div>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", justifyContent: "center", maxWidth: 900, width: "100%" }}>
        {tiers.map(t => (
          <div key={t.id} style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 28, flex: "1 1 240px", maxWidth: 280, boxShadow: "var(--shadow-elevated)", border: `2px solid ${t.id === "premium" ? t.accent : "var(--bg-border)"}`, position: "relative", display: "flex", flexDirection: "column" }}>
            {t.badge && <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: t.accent, color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 12px", borderRadius: 20, whiteSpace: "nowrap" }}>{t.badge}</div>}
            <div style={{ fontSize: 11, fontWeight: 600, color: t.accent, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 6 }}>{t.name}</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 2 }}>{t.tagline}</div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 20 }}>{priceLabel(t)}</div>
            <ul style={{ margin: "0 0 24px", padding: 0, listStyle: "none", flex: 1 }}>
              {t.features.map((f, i) => (
                <li key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 10, fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.4 }}>
                  <span style={{ color: t.accent, fontWeight: 700, marginTop: 1, flexShrink: 0 }}>✓</span>{f}
                </li>
              ))}
              {(t.excluded || []).map((f, i) => (
                <li key={"x" + i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 10, fontSize: 14, color: "var(--text-muted)", lineHeight: 1.4 }}>
                  <span style={{ color: "var(--text-muted)", fontWeight: 700, marginTop: 1, flexShrink: 0 }}>–</span>{f}
                </li>
              ))}
            </ul>
            <button onClick={() => select(t.id)} disabled={loading === t.id}
              style={{ width: "100%", padding: "11px", borderRadius: 10, border: t.id === "premium" ? "none" : `1.5px solid ${t.accent}`, background: t.id === "premium" ? t.accent : "transparent", color: t.id === "premium" ? "#fff" : t.accent, fontSize: 15, fontWeight: 600, cursor: "pointer", opacity: loading === t.id ? 0.7 : 1 }}>
              {loading === t.id ? "..." : t.cta}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


function BrokerConnectScreen({ onConnect, onSkip, user }) {
  const [phase, setPhase] = React.useState("select"); // select | ibkr | upload
  const [selectedBroker, setSelectedBroker] = React.useState(null);
  const [ibkrPhase, setIbkrPhase] = React.useState("idle"); // idle | connecting | connected | error
  const [ibkrError, setIbkrError] = React.useState("");
  const [elapsed, setElapsed] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [uploadStatus, setUploadStatus] = React.useState("");

  React.useEffect(() => {
    let t;
    if (ibkrPhase === "connecting") t = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(t);
  }, [ibkrPhase]);

  const legacySync = async () => {
    try {
      const r = await axios.post(`${API}/api/ibkr/sync`, {}, { timeout: 90000 });
      if (r.data.connected && r.data.portfolio?.length > 0) {
        setIbkrPhase("connected");
        setTimeout(() => onConnect("ibkr", r.data), 1200);
      } else {
        setIbkrError(r.data.error || "Could not retrieve portfolio.");
        setIbkrPhase("error");
      }
    } catch { setIbkrError("Connection error. Please try again."); setIbkrPhase("error"); }
  };

  const connectIbkr = async () => {
    setIbkrPhase("connecting"); setIbkrError(""); setElapsed(0);

    // Skip the OAuth popup entirely if it isn't configured server-side yet —
    // avoids relying on a popup (which can be silently blocked) just to learn that.
    try {
      const status = await axios.get(`${API}/api/ibkr/oauth/status`, { timeout: 8000 });
      if (!status.data.configured) { await legacySync(); return; }
    } catch { /* status check failed — fall through to the popup flow */ }

    // Use the logged-in user's email as the stable user_id for token storage.
    // Fall back to a UUID stored in localStorage for unauthenticated sessions.
    const userId = user?.email || (() => {
      let id = localStorage.getItem("ibkr_user_id");
      if (!id) { id = crypto.randomUUID(); localStorage.setItem("ibkr_user_id", id); }
      return id;
    })();

    // Open IBKR OAuth in a popup so the user can authorize without leaving Flint.
    const popup = window.open(
      `${API}/api/ibkr/oauth/start?user_id=${encodeURIComponent(userId)}`,
      "ibkr_oauth",
      "width=520,height=700,left=200,top=100"
    );

    // Listen for the callback postMessage from the popup.
    const onMessage = async (event) => {
      if (event.data?.type !== "ibkr_oauth") return;
      window.removeEventListener("message", onMessage);

      if (!event.data.success) {
        const msg = event.data.error || "IBKR authorization failed.";
        // If OAuth app isn't configured yet, fall back to legacy sync endpoint.
        if (msg.includes("not configured")) { await legacySync(); return; }
        setIbkrError(msg);
        setIbkrPhase("error");
        return;
      }

      // OAuth succeeded — fetch portfolio using stored tokens.
      try {
        const r = await axios.post(`${API}/api/ibkr/oauth/sync`, { user_id: userId }, { timeout: 60000 });
        if (r.data.connected && r.data.portfolio?.length > 0) {
          localStorage.setItem("ibkr_user_id", userId);
          localStorage.setItem("ibkr_oauth_connected", "true");
          setIbkrPhase("connected");
          setTimeout(() => onConnect("ibkr", r.data), 1200);
        } else {
          setIbkrError(r.data.error || "Connected but could not retrieve portfolio.");
          setIbkrPhase("error");
        }
      } catch (e) {
        setIbkrError("Portfolio fetch failed after authorization. Please try again.");
        setIbkrPhase("error");
      }
    };

    window.addEventListener("message", onMessage);

    // If the popup was blocked or closed without posting, fall back after timeout.
    const pollClosed = setInterval(() => {
      if (popup?.closed) {
        clearInterval(pollClosed);
        window.removeEventListener("message", onMessage);
        if (ibkrPhase === "connecting") {
          setIbkrError("Authorization window was closed. Please try again.");
          setIbkrPhase("error");
        }
      }
    }, 500);
  };

  const handleScreenshot = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setLoading(true); setError(""); setUploadStatus("Analyzing portfolio screenshot with AI...");
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const b64 = ev.target.result.split(",")[1];
        const r = await axios.post(`${API}/api/portfolio/upload-screenshot`, { image: b64, media_type: file.type || "image/jpeg" }, { timeout: 60000 });
        if (r.data.connected && r.data.portfolio?.length > 0) onConnect(selectedBroker?.id || "upload", r.data);
        else setError(r.data.error || "Could not extract portfolio. Try a clearer screenshot.");
      } catch(e) { setError("Analysis failed. Please try again."); }
      setLoading(false); setUploadStatus("");
    };
    reader.readAsDataURL(file);
  };

  const handleCSV = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setLoading(true); setError(""); setUploadStatus("Parsing portfolio file...");
    try {
      const text = await file.text();
      const r = await axios.post(`${API}/api/portfolio/upload-csv`, { csv_content: text }, { timeout: 30000 });
      if (r.data.connected && r.data.portfolio?.length > 0) onConnect(selectedBroker?.id || "csv", r.data);
      else setError(r.data.error || "Could not parse file. Check the format.");
    } catch(e) { setError("Upload failed."); }
    setLoading(false); setUploadStatus("");
  };

  const otherBrokers = [
    { id: "schwab", name: "Charles Schwab", howto: "Accounts → Positions → Export" },
    { id: "fidelity", name: "Fidelity", howto: "Accounts → Positions → Download CSV" },
    { id: "robinhood", name: "Robinhood", howto: "Account → Statements → Download CSV" },
    { id: "etrade", name: "E*TRADE", howto: "Portfolio → Download" },
    { id: "td", name: "TD Ameritrade", howto: "My Account → Positions → Export" },
    { id: "other", name: "Other broker", howto: "Export your positions as CSV or take a screenshot" },
  ];

  if (phase === "ibkr") {
    const steps = [
      { label: "Connecting to IBKR", done: elapsed >= 3 },
      { label: "Fetching account positions", done: elapsed >= 12 },
      { label: "Loading real-time prices", done: elapsed >= 20 },
      { label: "Building portfolio snapshot", done: elapsed >= 28 },
    ];
    return (
      <div style={{ minHeight: "100vh", background: "var(--bg-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
        <div style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 36, width: 400, boxShadow: "var(--shadow-elevated)" }}>
          <button onClick={() => { setPhase("select"); setIbkrPhase("idle"); }} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: 14, marginBottom: 20, padding: 0 }}>← Back</button>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
            <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
              <rect width={20} height={20} rx={5} fill="#E8A030" opacity={0.15} />
              <path d="M10 4C10 4 13 7 13 9.5C13 11.2 11.8 12.5 10.5 13.2C10.8 11.8 10 11 9 10.7C9.3 11.8 9 13.2 8 14.2C7.3 13.2 7 11.8 7 10.5C7 7.5 10 4 10 4Z" fill="#E8A030"/>
            </svg>
            <span style={{ fontSize: 16, fontWeight: 600 }}>Connect Interactive Brokers</span>
          </div>
          {ibkrPhase === "idle" && (
            <>
              <div style={{ background: "var(--accent-dim)", border: "1px solid rgba(232,160,48,0.2)", borderRadius: 10, padding: 14, marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--accent)", marginBottom: 4 }}>Direct IBKR connection</div>
                <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>Cue syncs your portfolio directly from IBKR. No gateway or 2FA code required — happens in the background.</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
                {["Live portfolio sync", "Real-time position values", "One-click order execution"].map((f, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, fontSize: 13, color: "var(--text-secondary)" }}><span style={{ color: "var(--gain)" }}>✓</span>{f}</div>
                ))}
              </div>
              <button onClick={connectIbkr} style={{ width: "100%", padding: 12, borderRadius: 8, border: "none", background: "var(--accent)", color: "#000", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>Connect to IBKR</button>
            </>
          )}
          {ibkrPhase === "connecting" && (
            <div>
              <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 20 }}>Syncing with IBKR — usually takes 20–30 seconds.</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
                {steps.map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0, background: s.done ? "var(--gain-bg)" : "var(--bg-border)", color: s.done ? "var(--gain)" : "var(--text-muted)", border: s.done ? "1px solid var(--gain-border)" : "1px solid var(--bg-border)" }}>
                      {s.done ? "✓" : "·"}
                    </div>
                    <span style={{ fontSize: 13, color: s.done ? "var(--text-primary)" : "var(--text-muted)" }}>{s.label}</span>
                  </div>
                ))}
              </div>
              <div style={{ height: 3, background: "var(--bg-primary)", borderRadius: 2, overflow: "hidden" }}>
                <div style={{ height: 3, background: "var(--accent)", borderRadius: 2, transition: "width 1s linear", width: `${Math.min(95, (elapsed / 30) * 100)}%` }} />
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6, textAlign: "right" }}>{elapsed}s</div>
            </div>
          )}
          {ibkrPhase === "connected" && (
            <div style={{ textAlign: "center", padding: "16px 0" }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>✓</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "var(--gain)", marginBottom: 4 }}>Connected to IBKR</div>
              <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Fetching your portfolio...</div>
            </div>
          )}
          {ibkrPhase === "error" && (
            <>
              <div style={{ background: "var(--loss-bg)", border: "1px solid var(--loss-border)", borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13, color: "var(--loss)" }}>{ibkrError}</div>
              <button onClick={connectIbkr} style={{ width: "100%", padding: 11, borderRadius: 8, border: "none", background: "var(--accent)", color: "#000", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Try again</button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (phase === "upload") {
    const broker = selectedBroker;
    if (loading) return (
      <div style={{ minHeight: "100vh", background: "var(--bg-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 16, color: "var(--accent)", fontWeight: 500, marginBottom: 8 }}>{uploadStatus}</div>
          <div style={{ fontSize: 14, color: "var(--text-muted)" }}>Please wait...</div>
        </div>
      </div>
    );
    return (
      <div style={{ minHeight: "100vh", background: "var(--bg-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', system-ui, sans-serif", padding: 20 }}>
        <div style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 32, width: "100%", maxWidth: 480, boxShadow: "var(--shadow-elevated)" }}>
          <button onClick={() => { setPhase("select"); setError(""); }} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: 14, marginBottom: 20, padding: 0 }}>← Back</button>
          <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 6 }}>Connect {broker?.name || "your broker"}</div>
          <div style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 20, lineHeight: 1.6 }}>
            Export your portfolio from {broker?.name || "your broker"} and upload here. Cue will remember your portfolio — no re-uploads needed.
          </div>
          {broker?.howto && (
            <div style={{ background: "var(--bg-primary)", borderRadius: 8, padding: 12, marginBottom: 20, fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.7 }}>
              <strong>How to export:</strong><br />{broker.howto}
            </div>
          )}
          {error && <div style={{ background: "var(--loss-bg)", color: "var(--loss)", padding: "10px 14px", borderRadius: 8, fontSize: 14, marginBottom: 16 }}>{error}</div>}
          <div style={{ display: "flex", gap: 10 }}>
            <label style={{ flex: 1, display: "block", border: "2px dashed var(--bg-border)", borderRadius: 12, padding: 20, textAlign: "center", cursor: "pointer", background: "var(--bg-surface)" }}>
              <div style={{ fontSize: 24, marginBottom: 6 }}>📸</div>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>Screenshot</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>JPG, PNG, WebP</div>
              <input type="file" accept="image/*" onChange={handleScreenshot} style={{ display: "none" }} />
            </label>
            <label style={{ flex: 1, display: "block", border: "2px dashed var(--bg-border)", borderRadius: 12, padding: 20, textAlign: "center", cursor: "pointer", background: "var(--bg-surface)" }}>
              <div style={{ fontSize: 24, marginBottom: 6 }}>📄</div>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>CSV / Excel</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>CSV, XLS, XLSX</div>
              <input type="file" accept=".csv,.xls,.xlsx,text/csv" onChange={handleCSV} style={{ display: "none" }} />
            </label>
          </div>
        </div>
      </div>
    );
  }

  // Broker selection screen
  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', system-ui, sans-serif", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 560 }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}><FlintLogo size="lg" /></div>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}><AIBadge /></div>
          <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Connect your investment account</div>
          <div style={{ fontSize: 15, color: "var(--text-muted)", lineHeight: 1.6, maxWidth: 380, margin: "0 auto" }}>
            Link your brokerage to sync your portfolio. Once connected, Cue remembers everything — no re-uploads needed.
          </div>
        </div>

        {/* IBKR — full integration */}
        <button onClick={() => setPhase("ibkr")} style={{ width: "100%", background: "var(--accent-dim)", border: "1.5px solid var(--accent)", borderRadius: 12, padding: "18px 20px", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
              <path d="M10 3C10 3 14 6.5 14 10C14 12.5 12.3 14.2 10.8 15.2C11.2 12.8 10 12 8.5 11.3C9 13 8.5 15 7 16.5C5.8 15 5.5 13 5.5 11C5.5 6.5 10 3 10 3Z" fill="#000" opacity={0.85}/>
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: "var(--text-primary)" }}>Interactive Brokers</span>
              <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, background: "var(--accent)", color: "#000", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px" }}>Full integration</span>
            </div>
            <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>Auto-sync · Real-time positions · One-click order execution</div>
          </div>
          <span style={{ fontSize: 18, color: "var(--accent)" }}>→</span>
        </button>

        {/* Other brokers */}
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>Other brokers — connect via export</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 24 }}>
          {otherBrokers.map(b => (
            <button key={b.id} onClick={() => { setSelectedBroker(b); setPhase("upload"); }} style={{ background: "var(--bg-surface)", border: "1px solid var(--bg-border)", borderRadius: 10, padding: "13px 14px", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 10, transition: "border-color 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = "var(--accent)"}
              onMouseLeave={e => e.currentTarget.style.borderColor = "var(--bg-border)"}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: 1 }}>{b.name}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>CSV or screenshot</div>
              </div>
              <span style={{ fontSize: 14, color: "var(--text-muted)" }}>→</span>
            </button>
          ))}
        </div>

        <div style={{ textAlign: "center" }}>
          {(() => {
            const existing = localStorage.getItem("connectedBroker");
            const hasRealConnection = existing && existing !== "skipped" && existing !== "upload";
            return (
              <button onClick={onSkip} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "var(--text-muted)", padding: "6px 0" }}>
                {hasRealConnection ? "← Back to dashboard" : "Skip for now →"}
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  );
}


function DeleteAccountModal({ onClose, onDeleted }) {
  const [password, setPassword] = React.useState("");
  const [confirmText, setConfirmText] = React.useState("");
  const [error, setError] = React.useState("");
  const [deleting, setDeleting] = React.useState(false);

  const canDelete = confirmText.trim().toUpperCase() === "DELETE" && !deleting;

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      const r = await axios.post(`${API}/api/auth/delete-account`, { password });
      if (r.data.success) {
        onDeleted();
      } else {
        setError(r.data.error || "Could not delete account.");
      }
    } catch (e) {
      setError(e?.response?.data?.error || "Could not delete account.");
    }
    setDeleting(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 28, width: 420, boxShadow: "var(--shadow-elevated)" }}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6, color: "var(--loss)" }}>Delete account</div>
        <div style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 20, lineHeight: 1.6 }}>
          This permanently erases your portfolio data, watchlist, alerts, and account — it can't be undone.
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Password</div>
        <input type="password" value={password} onChange={e => setPassword(e.target.value)}
          placeholder="Leave blank if you signed in with Google"
          style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--bg-border)", background: "var(--bg-primary)", color: "var(--text-primary)", fontSize: 14, marginBottom: 16, boxSizing: "border-box" }} />
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Type DELETE to confirm</div>
        <input type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)}
          style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid var(--bg-border)", background: "var(--bg-primary)", color: "var(--text-primary)", fontSize: 14, marginBottom: 16, boxSizing: "border-box" }} />
        {error && <div style={{ fontSize: 13, color: "var(--loss)", marginBottom: 16 }}>{error}</div>}
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} disabled={deleting}
            style={{ flex: 1, padding: "11px", borderRadius: 10, border: "1px solid var(--bg-border)", background: "transparent", color: "var(--text-secondary)", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={handleDelete} disabled={!canDelete}
            style={{ flex: 1, padding: "11px", borderRadius: 10, border: "none", background: "var(--loss)", color: "#fff", fontSize: 14, fontWeight: 600, cursor: canDelete ? "pointer" : "not-allowed", opacity: canDelete ? 1 : 0.5 }}>
            {deleting ? "Deleting..." : "Delete account"}
          </button>
        </div>
      </div>
    </div>
  );
}


function ExecuteOptimizationModal({ strategy, onClose, onComplete }) {
  // Build dollar-amount orders directly from optimizer output.
  // Dollar amounts are the source of truth — the backend converts to shares at execution time.
  const buildOrders = () => {
    const positions = strategy?.recommended_portfolio?.recommended_positions || [];
    const portfolioMap = {};
    (strategy?.portfolio || []).forEach(p => {
      portfolioMap[p.ticker] = {
        market_value: p.market_value || 0,
        position: p.position || 0,
      };
    });

    return positions
      .filter(p => ["SELL", "REDUCE", "BUY", "INCREASE"].includes(p.action))
      .map(p => {
        const isSell = ["SELL", "REDUCE"].includes(p.action);
        const isFullSell = p.action === "SELL";
        const currentVal = portfolioMap[p.ticker]?.market_value || p.current_value || 0;
        const recommendedVal = p.recommended_value || 0;

        let dollar_amount;
        if (isSell) {
          // Amount to sell = current value minus what we want to keep
          dollar_amount = Math.max(0, currentVal - recommendedVal);
        } else {
          // Amount to buy = target value minus what we already have
          dollar_amount = Math.max(0, recommendedVal - currentVal);
        }

        // For full SELL orders, include exact share count so the backend doesn't undershoot
        // (floor(market_value / live_price) can miss 1 share if price rose since portfolio load)
        const current_shares = isFullSell ? Math.floor(portfolioMap[p.ticker]?.position || 0) : 0;

        return {
          ticker: p.ticker,
          action: isSell ? "SELL" : "BUY",
          dollar_amount: Math.round(dollar_amount),
          current_shares,
          market_price: p.market_price || 0,
          rationale: p.rationale,
          enabled: dollar_amount > 0,
          contract_id: p.contract_id || null,
        };
      })
      .filter(o => o.dollar_amount > 0);
  };

  const [orders, setOrders] = React.useState(() => buildOrders());
  const [sending, setSending] = React.useState(false);
  const [results, setResults] = React.useState(null);

  const updateDollars = (i, val) => setOrders(o => o.map((x, j) => j === i ? {
    ...x,
    dollar_amount: Math.max(1, parseInt(val.replace(/[^0-9]/g,"")) || 0),
    current_shares: 0,  // User edited amount → fall back to dollar_amount/price on backend
  } : x));
  const updateTicker = (i, val) => setOrders(o => o.map((x, j) => j === i ? { ...x, ticker: val.toUpperCase() } : x));
  const toggleOrder = (i) => setOrders(o => o.map((x, j) => j === i ? { ...x, enabled: !x.enabled } : x));

  const totalSell = orders.filter(o => o.action === "SELL" && o.enabled).reduce((s, o) => s + o.dollar_amount, 0);
  const totalBuy  = orders.filter(o => o.action === "BUY"  && o.enabled).reduce((s, o) => s + o.dollar_amount, 0);
  const availableCash = strategy?.performance?.available_cash || 0;

  const sells = orders.filter(o => o.action === "SELL");
  const buys  = orders.filter(o => o.action === "BUY");

  const execute = async () => {
    setSending(true);
    try {
      const enabled = orders.filter(o => o.enabled);
      const q = await axios.post(`${API}/api/ibkr/queue-orders`, { orders: enabled });
      if (!q.data.success) {
        setResults([{ success: false, error: q.data.error || "Failed to queue orders" }]);
        setSending(false);
        return;
      }
      const batchId = q.data.batch_id;
      // Poll for the background processor to complete the batch (runs automatically, no Claude Code needed)
      let attempts = 0;
      const poll = async () => {
        attempts++;
        try {
          const r = await axios.get(`${API}/api/ibkr/order-results/${batchId}`);
          if (r.data.status === "completed") {
            setResults(r.data.results || []);
            setSending(false);
          } else if (attempts < 60) {
            setTimeout(poll, 5000);  // poll every 5s for up to 5 minutes
          } else {
            // Still not processed after 5 minutes
            setResults([{ success: false, _isBatchReady: true, batchId }]);
            setSending(false);
          }
        } catch (e) {
          setResults([{ success: false, error: "Failed to check order status" }]);
          setSending(false);
        }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      setResults([{ success: false, error: "Failed to connect. Please try again." }]);
      setSending(false);
    }
  };

  const fmt = (n) => "$" + Math.round(n).toLocaleString("en-US");

  const inputStyle = {
    width: 90, padding: "5px 8px", borderRadius: 6, border: "1px solid var(--bg-border)",
    fontSize: 14, textAlign: "right", background: "var(--bg-elevated)",
    color: "var(--text-primary)", outline: "none", fontVariantNumeric: "tabular-nums",
  };
  const tickerInputStyle = {
    width: 68, padding: "5px 8px", borderRadius: 6, border: "1px solid var(--bg-border)",
    fontSize: 14, fontWeight: 700, background: "var(--bg-elevated)", color: "var(--text-primary)", outline: "none",
  };

  if (results) {
    const batchReadyResult = results.find(r => r._isBatchReady);
    const succeeded = results.filter(r => r.success);
    const failed = results.filter(r => !r.success && !r._isBatchReady);

    // Batch-ready state: Claude Code loop isn't running — show fallback with a simple message (no batch ID needed)
    if (batchReadyResult) {
      return (
        <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 28, width: 480, boxShadow: "var(--shadow-elevated)" }}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}>Orders still processing</div>
              <div style={{ fontSize: 14, color: "var(--text-muted)", lineHeight: 1.7 }}>
                The background processor is running but took longer than 5 minutes.<br />
                Refresh in a moment to check if orders completed.
              </div>
            </div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", background: "var(--bg-elevated)", borderRadius: 8, padding: "10px 12px", marginBottom: 16, lineHeight: 1.5 }}>
              If this keeps happening, the server may have lost its IBKR connection. Check that Claude Code is authenticated on the server.
            </div>
            <button onClick={onClose} style={{ width: "100%", padding: "11px", borderRadius: 10, border: "none", background: "var(--accent)", color: "#000", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>Got it</button>
          </div>
        </div>
      );
    }

    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 28, width: 480, maxHeight: "85vh", overflow: "auto", boxShadow: "var(--shadow-elevated)" }}>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>{succeeded.length} order instruction{succeeded.length !== 1 ? "s" : ""} created</div>
            <div style={{ fontSize: 14, color: "var(--text-muted)", lineHeight: 1.6 }}>Open each link in IBKR to confirm. Nothing executes until you approve it there.</div>
          </div>
          {succeeded.map((r, i) => (
            <div key={i} style={{ background: "var(--bg-elevated)", border: "1px solid var(--bg-border)", borderRadius: 10, padding: "12px 14px", marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>{r.ticker}</span>
                <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 4, background: r.action === "BUY" ? "var(--gain-bg)" : "var(--loss-bg)", color: r.action === "BUY" ? "var(--gain)" : "var(--loss)", fontWeight: 600 }}>{r.action}</span>
              </div>
              <div style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 8 }}>
                {r.mode === "cashQty" || r.cash_qty > 0
                  ? <><strong>{fmt(r.cash_qty || r.dollar_amount)}</strong> at market · IBKR fills exact shares (fractional allowed)</>
                  : <>{r.shares} shares{r.price > 0 ? <> @ ${r.price?.toFixed(2)} = <strong>{fmt(r.shares * (r.price || 0))}</strong></> : null}</>
                }
              </div>
              {r.confirm_url ? (
                <a href={r.confirm_url} target="_blank" rel="noopener noreferrer" style={{ display: "block", textAlign: "center", padding: "8px", borderRadius: 7, background: "var(--accent)", color: "#000", fontSize: 13, fontWeight: 600, textDecoration: "none" }}>Open in IBKR to confirm →</a>
              ) : (
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Instruction ID: {r.instruction_id}</div>
              )}
            </div>
          ))}
          {failed.map((r, i) => (
            <div key={i} style={{ background: "var(--loss-bg)", borderRadius: 10, padding: "10px 14px", marginBottom: 8, fontSize: 13, color: "var(--loss)" }}>
              {r.ticker}: {r.error}
            </div>
          ))}
          <button onClick={onComplete} style={{ width: "100%", marginTop: 8, padding: "11px", borderRadius: 10, border: "none", background: "var(--accent)", color: "#000", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
            Done — refresh portfolio
          </button>
        </div>
      </div>
    );
  }

  const OrderRow = ({ o, gi, isSell }) => {
    const estimatedShares = o.market_price > 0 ? Math.floor(o.dollar_amount / o.market_price) : null;
    const displayShares = isSell && o.current_shares > 0 ? o.current_shares : estimatedShares;
    return (
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 0", borderBottom: "1px solid var(--bg-border-subtle)", opacity: o.enabled ? 1 : 0.4 }}>
        <input type="checkbox" checked={o.enabled} onChange={() => toggleOrder(gi)}
          style={{ width: 15, height: 15, cursor: "pointer", flexShrink: 0, marginTop: 3 }} />
        {!isSell
          ? <input value={o.ticker} onChange={e => updateTicker(gi, e.target.value)} style={tickerInputStyle} disabled={!o.enabled} title="Edit ticker" />
          : <span style={{ fontWeight: 700, fontSize: 15, minWidth: 68 }}>{o.ticker}</span>
        }
        <div style={{ flex: 1, fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5 }}>{o.rationale}</div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, flexShrink: 0 }}>
          <input
            type="text"
            value={"$" + o.dollar_amount.toLocaleString("en-US")}
            onChange={e => updateDollars(gi, e.target.value)}
            style={inputStyle}
            disabled={!o.enabled}
          />
          {displayShares != null && displayShares > 0 && (
            <span style={{ fontSize: 11, color: "var(--text-muted)", fontVariantNumeric: "tabular-nums" }}>
              {displayShares} share{displayShares !== 1 ? "s" : ""}
              {o.market_price > 0 && (
                <span style={{ color: "var(--bg-border)" }}> @ ${o.market_price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              )}
            </span>
          )}
          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>to {isSell ? "sell" : "invest"}</span>
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "var(--bg-surface)", borderRadius: 16, padding: 28, width: 560, maxHeight: "90vh", overflow: "auto", boxShadow: "var(--shadow-elevated)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Execute Portfolio Optimization</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "var(--text-muted)", padding: "0 4px" }}>✕</button>
        </div>
        <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 20, lineHeight: 1.5 }}>
          Dollar amounts are from the optimizer. Edit any amount before sending. Sells execute first — proceeds fund the buys. Nothing runs until you confirm in IBKR.
        </div>

        {sells.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--loss)", textTransform: "uppercase", letterSpacing: "0.6px" }}>Sell / Reduce</span>
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>total {fmt(totalSell)}</span>
            </div>
            {sells.map((o) => <OrderRow key={o.ticker} o={o} gi={orders.indexOf(o)} isSell={true} />)}
          </div>
        )}

        {buys.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--gain)", textTransform: "uppercase", letterSpacing: "0.6px" }}>Buy / Increase</span>
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>total {fmt(totalBuy)}</span>
            </div>
            {buys.map((o) => <OrderRow key={o.ticker} o={o} gi={orders.indexOf(o)} isSell={false} />)}
          </div>
        )}

        {orders.length === 0 && (
          <div style={{ textAlign: "center", padding: "30px 0", color: "var(--text-muted)", fontSize: 15 }}>No actionable orders in the current plan.</div>
        )}

        {orders.length > 0 && (
          <div style={{ background: "var(--bg-elevated)", borderRadius: 10, padding: "12px 14px", marginBottom: 16, display: "flex", justifyContent: "space-between", fontSize: 13 }}>
            <span style={{ color: "var(--text-muted)" }}>Net cash flow{availableCash > 0 ? <span style={{ color: "var(--text-muted)", fontWeight: 400 }}> (incl. ${availableCash.toLocaleString("en-US", { maximumFractionDigits: 0 })} cash)</span> : ""}</span>
            <span style={{ fontWeight: 600, color: availableCash + totalSell >= totalBuy ? "var(--gain)" : "var(--loss)", fontVariantNumeric: "tabular-nums" }}>
              {availableCash + totalSell >= totalBuy ? "+" : ""}{fmt(availableCash + totalSell - totalBuy)}
            </span>
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "11px", borderRadius: 10, border: "1px solid var(--bg-border)", background: "transparent", fontSize: 14, cursor: "pointer", color: "var(--text-secondary)" }}>Cancel</button>
          <button onClick={execute} disabled={sending || !orders.some(o => o.enabled)}
            style={{ flex: 2, padding: "11px", borderRadius: 10, border: "none", background: sending || !orders.some(o => o.enabled) ? "var(--bg-border)" : "var(--accent)", color: sending || !orders.some(o => o.enabled) ? "var(--text-muted)" : "#000", fontSize: 15, fontWeight: 600, cursor: sending ? "default" : "pointer", fontFamily: "'DM Sans', sans-serif" }}>
            {sending ? "Sending to IBKR..." : `Send ${orders.filter(o => o.enabled).length} order${orders.filter(o => o.enabled).length !== 1 ? "s" : ""} to IBKR`}
          </button>
        </div>
      </div>
    </div>
  );
}


export default function App() {
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["market_flint", "optimization", "performance"].includes(t) ? t : "market_flint";
  });
  const [portfolioData, setPortfolioData] = React.useState(() => {
    try {
      const saved = localStorage.getItem("portfolioData");
      if (saved) {
        const parsed = JSON.parse(saved);
        // Persistent connections (broker-connected): 30-day TTL; otherwise 24-hour TTL
        const ttl = parsed.persistentConnection
          ? 30 * 24 * 60 * 60 * 1000
          : 24 * 60 * 60 * 1000;
        if (parsed.savedAt && Date.now() - parsed.savedAt < ttl) {
          return parsed;
        }
      }
    } catch(e) {}
    return null;
  });
  const [recommendations, setRecommendations] = useState(null);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState("portfolio");
  const [optimizationError, setOptimizationError] = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [strategyLoading, setStrategyLoading] = useState(false);
  const [performance, setPerformance] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [ibkr, setIbkr] = useState(false);
  const [portfolioValue, setPortfolioValue] = useState(null);
  const [diversificationScore, setDiversificationScore] = useState(null);
  const [overlapCount, setOverlapCount] = useState(null);
  const [signalCount, setSignalCount] = useState(null);
  const [hasPortfolioCache, setHasPortfolioCache] = useState(false);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [orderInstruction, setOrderInstruction] = useState(null);
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const now = new Date();

  const [user, setUser] = React.useState(null);
  const [userTier, setUserTier] = React.useState(null);
  // MVP two-tier launch (2026-08-28): separate from userTier (the dormant
  // Free/Plus/Pro paid concept) on purpose — see require_tier in app/main.py.
  const [userAccountType, setUserAccountType] = React.useState(null);
  const [userMvpTier, setUserMvpTier] = React.useState(null);
  const [betaAgreementAccepted, setBetaAgreementAccepted] = React.useState(false);
  // Marketing site CTAs link to /app?tier=market_data or ?tier=portfolio_optimizer
  // so a brand-new signup lands on their chosen tier without re-picking it.
  const [preselectedMvpTier] = React.useState(() => {
    const t = new URLSearchParams(window.location.search).get("tier");
    return t === "market_data" || t === "portfolio_optimizer" ? t : null;
  });
  const [screen, setScreen] = React.useState("loading"); // loading|auth|tier_select|questionnaire|profile_report|disclaimer|beta_agreement|mvp_tier_select|mvp_upload|main
  const [profileData, setProfileData] = React.useState(null);
  const [driftNotice, setDriftNotice] = React.useState(null);
  const [showIbkrConnect, setShowIbkrConnect] = React.useState(false);
  const [featureFlags, setFeatureFlags] = React.useState({});
  const [flintPick, setFlintPick] = React.useState(null);
  const [alerts, setAlerts] = React.useState([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [watchlistTickers, setWatchlistTickers] = React.useState([]);
  const [digestContent, setDigestContent] = React.useState(null);
  const [digestEmailEnabled, setDigestEmailEnabled] = React.useState(true);
  const [showFeatureOnboarding, setShowFeatureOnboarding] = React.useState(false);

  React.useEffect(() => {
    if (featureFlags.sentinel_alerts && localStorage.getItem("dismissedSentinelOnboarding") !== "true") {
      setShowFeatureOnboarding(true);
    }
  }, [featureFlags.sentinel_alerts]);

  const dismissFeatureOnboarding = (dontShowAgain) => {
    if (dontShowAgain) localStorage.setItem("dismissedSentinelOnboarding", "true");
    setShowFeatureOnboarding(false);
  };

  // Attach auth token to every axios request
  React.useEffect(() => {
    const id = axios.interceptors.request.use(cfg => {
      const token = localStorage.getItem("authToken");
      if (token) cfg.headers = { ...cfg.headers, Authorization: `Bearer ${token}` };
      return cfg;
    });
    return () => axios.interceptors.request.eject(id);
  }, []);

  const resolveScreen = (userData) => {
    // Francisco's operator account never goes through the MVP flow below —
    // exactly the pre-MVP behavior, untouched (Step 0: protect this account).
    if (userData.account_type !== "operator") {
      if (!userData.beta_agreement_accepted) return "beta_agreement";
      if (!userData.mvp_tier) return "mvp_tier_select";
      if (!userData.has_profile) return "questionnaire";
      if (userData.mvp_tier === "portfolio_optimizer" && !portfolioData) return "mvp_upload";
      return "main";
    }
    if (!userData.tier) return "tier_select";
    if (!userData.has_profile) return "questionnaire";
    if (!localStorage.getItem("disclaimerAccepted")) return "disclaimer";
    // Show connect screen for premium users who haven't linked a real broker yet
    const cb = localStorage.getItem("connectedBroker");
    const reallyConnected = cb && cb !== "skipped" && cb !== "upload";
    if (userData.tier === "premium" && !reallyConnected) return "connect_account";
    return "main";
  };

  const handleTierSelect = (tier) => {
    setUserTier(tier);
    const u = { ...user, tier };
    if (!u || !u.has_profile) { setScreen("questionnaire"); return; }
    if (!localStorage.getItem("disclaimerAccepted")) { setScreen("disclaimer"); return; }
    const _cb = localStorage.getItem("connectedBroker");
    if (tier === "premium" && (!_cb || _cb === "skipped" || _cb === "upload")) { setScreen("connect_account"); return; }
    setScreen("main");
  };

  React.useEffect(() => {
    if (screen !== "main") return;
    axios.get(`${API}/api/feature-flags`).then(r => setFeatureFlags(r.data)).catch(() => {});
  }, [screen]);

  React.useEffect(() => {
    if (!featureFlags.flint_of_day) return;
    axios.get(`${API}/api/flint-of-the-day/today`).then(r => {
      if (r.data.pick) setFlintPick(r.data.pick);
    }).catch(() => {});
  }, [featureFlags.flint_of_day]);

  const refreshAlerts = React.useCallback(() => {
    axios.get(`${API}/api/alerts`).then(r => {
      setAlerts(r.data.alerts || []);
      setUnreadCount(r.data.unread_count || 0);
    }).catch(() => {});
  }, []);

  const refreshWatchlist = React.useCallback(() => {
    axios.get(`${API}/api/watchlist`).then(r => setWatchlistTickers(r.data.tickers || [])).catch(() => {});
  }, []);

  React.useEffect(() => {
    if (!featureFlags.sentinel_alerts) return;
    refreshAlerts();
    refreshWatchlist();
  }, [featureFlags.sentinel_alerts, refreshAlerts, refreshWatchlist]);

  React.useEffect(() => {
    if (!featureFlags.daily_digest) return;
    axios.get(`${API}/api/digest/today`).then(r => {
      if (r.data.content) setDigestContent(r.data.content);
    }).catch(() => {});
    axios.get(`${API}/api/notification-preferences`).then(r => {
      if (typeof r.data.digest_email_enabled === "boolean") setDigestEmailEnabled(r.data.digest_email_enabled);
    }).catch(() => {});
  }, [featureFlags.daily_digest]);

  const todayViewLogged = React.useRef(false);
  React.useEffect(() => {
    if (todayViewLogged.current) return;
    if (!featureFlags.flint_of_day && !featureFlags.daily_digest) return;
    if (!flintPick && !digestContent) return;
    todayViewLogged.current = true;
    axios.post(`${API}/api/analytics/event`, { event_name: "today_tab_viewed" }).catch(() => {});
  }, [featureFlags.flint_of_day, featureFlags.daily_digest, flintPick, digestContent]);

  const handleToggleDigestEmail = () => {
    const next = !digestEmailEnabled;
    setDigestEmailEnabled(next);
    axios.put(`${API}/api/notification-preferences`, { digest_email_enabled: next }).catch(() => {});
  };

  const [sendingTestDigest, setSendingTestDigest] = React.useState(false);
  const handleSendTestDigest = () => {
    setSendingTestDigest(true);
    axios.post(`${API}/api/digest/send-test`).then(r => {
      const d = r.data.diagnostics || {};
      if (r.data.sent) {
        alert(`Sent to ${r.data.sent_to}. Check your inbox (and spam folder).`);
      } else {
        alert(
          "Not sent. Diagnostics:\n" +
          `SendGrid configured on server: ${d.sendgrid_configured}\n` +
          `daily_digest flag on for you: ${d.daily_digest_flag_enabled_for_you}\n` +
          `Email digest preference on: ${d.digest_email_preference_enabled}`
        );
      }
    }).catch(e => {
      alert(`Request failed: ${e.response?.data?.error || e.message}`);
    }).finally(() => setSendingTestDigest(false));
  };

  const handleMarkAlertRead = (id) => {
    axios.post(`${API}/api/alerts/${id}/read`).then(() => {
      setAlerts(prev => prev.map(a => a.id === id ? { ...a, read: true } : a));
      setUnreadCount(prev => Math.max(0, prev - 1));
      axios.post(`${API}/api/analytics/event`, { event_name: "notification_opened" }).catch(() => {});
    }).catch(() => {});
  };

  const handleAddWatchlistTicker = (ticker) => {
    axios.post(`${API}/api/watchlist`, { ticker }).then(() => refreshWatchlist()).catch(() => {});
  };

  const handleRemoveWatchlistTicker = (ticker) => {
    axios.delete(`${API}/api/watchlist/${ticker}`).then(() => refreshWatchlist()).catch(() => {});
  };

  // portfolioData/connectedBroker are cached in plain localStorage, which is shared by every
  // account that ever logs into this browser (not scoped per-user). Without this check, a new
  // account logging in on a browser that previously held another account's session would
  // silently inherit that stale portfolio/broker data instead of getting the upload/connect
  // intake screen. Call this right after we learn who the current user is (id or email), for
  // both the token-restore path and the fresh-login path.
  const syncPortfolioOwnership = (userId) => {
    if (!userId) return;
    const lastOwner = localStorage.getItem("lastPortfolioOwner");
    if (lastOwner && lastOwner !== userId) {
      localStorage.removeItem("portfolioData");
      localStorage.removeItem("connectedBroker");
      setPortfolioData(null);
    }
    localStorage.setItem("lastPortfolioOwner", userId);
  };

  useEffect(() => {
    axios.get(`${API}/api/health`).then(r => setIbkr(r.data.ibkr_connected)).catch(() => {});

    const token = localStorage.getItem("authToken");
    if (!token) { setScreen("auth"); return; }

    axios.get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (r.data.id) {
          syncPortfolioOwnership(r.data.id || r.data.email);
          setUser(r.data);
          if (r.data.tier) setUserTier(r.data.tier);
          setUserAccountType(r.data.account_type || null);
          setUserMvpTier(r.data.mvp_tier || null);
          setBetaAgreementAccepted(!!r.data.beta_agreement_accepted);
          if (r.data.profile) setProfileData(r.data.profile);
          setScreen(resolveScreen(r.data));
        } else {
          localStorage.removeItem("authToken");
          setScreen("auth");
        }
      })
      .catch(() => { localStorage.removeItem("authToken"); setScreen("auth"); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-fetch portfolio from IBKR when user has live connection but no cached data.
  // Prefers OAuth sync (stored tokens) if available; falls back to legacy sync endpoint.
  React.useEffect(() => {
    const connectedBroker = localStorage.getItem("connectedBroker");
    if (connectedBroker !== "ibkr" || portfolioData || screen !== "main") return;

    const syncPortfolio = async () => {
      // Try OAuth sync first (uses per-user stored tokens — works without Claude Code session)
      const userId = user?.email || localStorage.getItem("ibkr_user_id");
      if (userId && localStorage.getItem("ibkr_oauth_connected")) {
        try {
          const r = await axios.post(`${API}/api/ibkr/oauth/sync`, { user_id: userId }, { timeout: 60000 });
          if (r.data.connected && r.data.portfolio?.length > 0) {
            const d = { ...r.data, savedAt: Date.now(), persistentConnection: true };
            localStorage.setItem("portfolioData", JSON.stringify(d));
            setPortfolioData(d);
            if (d.net_liquidation) setPortfolioValue(d.net_liquidation);
            return;
          }
        } catch {}
      }
      // Fall back to legacy sync (push cache / Flex / gateway)
      try {
        const r = await axios.post(`${API}/api/ibkr/sync`, {}, { timeout: 90000 });
        if (r.data.connected && r.data.portfolio?.length > 0) {
          const d = { ...r.data, savedAt: Date.now(), persistentConnection: true };
          localStorage.setItem("portfolioData", JSON.stringify(d));
          setPortfolioData(d);
          if (d.net_liquidation) setPortfolioValue(d.net_liquidation);
        }
      } catch {}
    };

    syncPortfolio();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  // Recommendations don't change intraday, so the backend caches one run per user
  // per calendar day — this is nearly instant after the first login of the day.
  // Pass force=true (the "Re-run analysis" button) to bypass that and recompute.
  const fetchOptimization = async (force = false) => {
    setRecommendationsLoading(true);
    setStrategyLoading(true);
    setOptimizationError(null);
    setLoadingStage("portfolio");
    try {
      setLoadingStage("market");
      setTimeout(() => setLoadingStage("signals"), 15000);
      setTimeout(() => setLoadingStage("ai"), 40000);
      // Try POST with portfolioData first; fall back to GET (reads server-side push cache).
      // A cash-only "build from scratch" portfolio has zero stock positions but a real
      // available_cash amount — that's still explicit user data and must be POSTed, not
      // silently dropped into the GET fallback (which serves a different account's cache).
      let r;
      if (portfolioData && (portfolioData.portfolio?.length > 0 || portfolioData.available_cash > 0)) {
        const requestData = { portfolio: portfolioData, net_liquidation: portfolioData.net_liquidation, force_refresh: force };
        r = await axios.post(`${API}/api/portfolio/optimization`, requestData, { timeout: 300000 });
      }
      // If POST failed or no portfolioData, fall back to GET (uses push cache on server)
      if (!r || (!r.data.connected && !r.data.upgrade_required)) {
        r = await axios.get(`${API}/api/portfolio/optimization${force ? "?force_refresh=true" : ""}`, { timeout: 300000 });
      }
      // Free tier: server strips portfolio-specific analysis but still returns
      // general market picks/summary — surface the upgrade prompt for the
      // portfolio-analysis tab without blanking out the market tab's data.
      if (r.data.upgrade_required) {
        setOptimizationError(`upgrade:${r.data.upgrade_required}`);
      }
      // MVP two-tier launch: require_tier() in app/main.py returns
      // {success:false, error:"upgrade_required", message:"..."} for a
      // Market Data-tier user — surface that message directly.
      if (r.data.error === "upgrade_required") {
        setOptimizationError(r.data.message || "Upgrade to Portfolio Optimizer to unlock this.");
        setRecommendationsLoading(false);
        setStrategyLoading(false);
        return;
      }
      console.log('Optimization response:', r.data.connected, 'sells:', r.data.sells?.length, 'increases:', r.data.increases?.length);
      if (r.data.connected) {
        setLoadingStage("done");
        setRecommendations({
          stock_picks: r.data.stock_picks,
          etf_picks: r.data.etf_picks,
          market_summary: r.data.market_summary,
          portfolio_alignment: r.data.portfolio_alignment,
          sectors_to_watch: r.data.sectors_to_watch,
        });
        setStrategy({
          connected: true,
          portfolio: r.data.portfolio,
          performance: r.data.performance,
          recommended_portfolio: {
            recommended_positions: [...(Array.isArray(r.data.sells) ? r.data.sells : []), ...(Array.isArray(r.data.increases) ? r.data.increases : []), ...(Array.isArray(r.data.keeps) ? r.data.keeps : [])],
            performance_comparison: r.data.performance,
            execution_summary: r.data.executive_summary,
          },
          diversification_score: r.data.diversification_score,
          strengths: r.data.strengths || [],
          weaknesses: r.data.weaknesses || [],
          optimization_impact: r.data.optimization_impact || "",
          sector_rotation: r.data.sector_rotation || {},
          fully_optimized: r.data.fully_optimized || false,
          nice_to_have: r.data.nice_to_have || [],
          next_review_date: r.data.next_review_date || "",
          cache_meta: r.data.cache_meta || null,
          position_targets: r.data.position_targets || [],
          etf_outlook: r.data.etf_outlook || [],
        });
        setDiversificationScore(r.data.diversification_score);
        setOverlapCount((r.data.sells || []).filter(s => s.action === 'SELL' || s.action === 'REDUCE').length);
        setSignalCount((r.data.sells || []).filter(s => s.priority === 'high').length);
        if (r.data.net_liquidation) setPortfolioValue(r.data.net_liquidation);
        setHasPortfolioCache(true);
      }
    } catch (e) {
      console.error('Optimization error:', e);
      setOptimizationError(e?.response?.data?.error || e?.message || "Optimization failed. Please try again.");
    }
    setRecommendationsLoading(false);
    setStrategyLoading(false);
  };

  // Auto-run the optimizer once per app load instead of waiting for a button
  // click — the backend serves a cached result after the first run of the day,
  // so this is cheap on every login except the very first each day.
  const autoOptimizationRun = React.useRef(false);
  React.useEffect(() => {
    if (screen !== "main" || autoOptimizationRun.current) return;
    if (strategy || strategyLoading) return;
    autoOptimizationRun.current = true;
    fetchOptimization();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, strategy, strategyLoading]);

  const fetchStrategy = () => {
    setStrategyLoading(true);
    axios.get(`${API}/api/portfolio/strategy`, { timeout: 180000 })
      .then(r => {
        setStrategy(r.data);
        setStrategyLoading(false);
        setHasPortfolioCache(true);
        setDiversificationScore(r.data.diversification_score);
        setOverlapCount((r.data.overlaps || []).length);
        setSignalCount((r.data.deterioration_signals || []).length + (r.data.price_target_signals || []).length);
        if (r.data.net_liquidation) setPortfolioValue(r.data.net_liquidation);
      })
      .catch(() => setStrategyLoading(false));
  };

  const fetchPerformance = async () => {
    setPerformanceLoading(true);
    try {
      const r = portfolioData
        ? await axios.post(`${API}/api/performance`, { portfolio: portfolioData }, { timeout: 60000 })
        : await axios.get(`${API}/api/performance`, { timeout: 60000 });
      setPerformance(r.data);
    } catch(e) { console.error(e); }
    setPerformanceLoading(false);
  };;

  const previewOrder = async (ticker, action, targetValue, currentPrice) => {
    try {
      let price = currentPrice;
      if (!price || price <= 0) {
        const pick = recommendations?.stock_picks?.find(p => p.ticker === ticker);
        if (pick?.current_price) price = pick.current_price;
      }
      if (!price || price <= 0) {
        const pos = strategy?.portfolio?.find(p => p.ticker === ticker);
        if (pos) price = pos.position > 0 ? pos.market_value / pos.position : 0;
      }
      const r = await axios.post(`${API}/api/order/preview`, {
        ticker, action, target_value: targetValue, current_price: price || 0
      });
      if (r.data.success) {
        setPendingOrder(r.data);
      } else {
        alert(`Error: ${r.data.error}`);
      }
    } catch (e) {
      alert("Could not preview order. Please try again.");
    }
  };

  const confirmOrder = async () => {
    if (!pendingOrder) return;
    setPlacingOrder(true);
    try {
      const r = await axios.post(`${API}/api/order/place`, {
        ticker: pendingOrder.ticker,
        ibkr_action: pendingOrder.ibkr_action,
        dollar_amount: pendingOrder.estimated_value || 0,  // dollar-based (allows fractional)
        shares: pendingOrder.shares,                       // fallback for full sells
        contract_id: pendingOrder.contract_id || null,
      });
      setPlacingOrder(false);
      if (r.data.success) {
        if (r.data.mode === "instruction" && r.data.confirm_url) {
          // Instruction created — keep modal open, show the IBKR deep-link
          setOrderInstruction({ confirm_url: r.data.confirm_url, instruction_id: r.data.instruction_id });
        } else {
          // Direct TWS placement succeeded — close modal, show toast
          setPendingOrder(null);
          setOrderInstruction(null);
          setOrderResult({ success: true, message: r.data.message || "Order placed successfully" });
          setTimeout(() => setOrderResult(null), 5000);
        }
      } else {
        setPendingOrder(null);
        setOrderInstruction(null);
        setOrderResult({ success: false, message: r.data.error });
        setTimeout(() => setOrderResult(null), 5000);
      }
    } catch (e) {
      setPlacingOrder(false);
      setPendingOrder(null);
      setOrderInstruction(null);
      setOrderResult({ success: false, message: "Order failed. Please try again." });
      setTimeout(() => setOrderResult(null), 5000);
    }
  };

  const tabStyle = (t) => ({
    padding: "8px 20px", fontSize: 15, cursor: "pointer",
    color: tab === t ? "var(--accent)" : "var(--text-secondary)",
    background: "none", border: "none",
    borderBottom: tab === t ? "2px solid var(--accent)" : "2px solid transparent",
    fontWeight: tab === t ? 600 : 400
  });

  if (screen === "loading") {
    return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "'DM Sans', system-ui, sans-serif", color: "var(--text-muted)", fontSize: 16 }}>Loading...</div>;
  }
  if (screen === "auth") {
    return <AuthScreen onAuth={(userData) => {
      syncPortfolioOwnership(userData.id || userData.email);
      setUser(userData);
      localStorage.setItem("authToken", userData.token);
      if (userData.tier) setUserTier(userData.tier);
      setUserAccountType(userData.account_type || null);
      setUserMvpTier(userData.mvp_tier || null);
      setBetaAgreementAccepted(!!userData.beta_agreement_accepted);
      if (userData.profile) setProfileData(userData.profile);
      setScreen(resolveScreen(userData));
    }} />;
  }
  if (screen === "beta_agreement") {
    return <BetaAgreementScreen onAccept={async () => {
      try {
        await axios.post(`${API}/api/auth/accept-beta-agreement`, { version: BETA_AGREEMENT_VERSION });
      } catch (e) {}
      setBetaAgreementAccepted(true);
      setScreen("mvp_tier_select");
    }} />;
  }
  if (screen === "mvp_tier_select") {
    return <MvpTierSelectScreen preselected={preselectedMvpTier} onSelect={async (tier) => {
      try {
        await axios.post(`${API}/api/auth/set-mvp-tier`, { tier });
      } catch (e) {}
      setUserMvpTier(tier);
      setScreen("questionnaire");
    }} />;
  }
  if (screen === "mvp_upload") {
    return <PortfolioInputScreen onSkipToMarketData={async () => {
      try { await axios.post(`${API}/api/auth/set-mvp-tier`, { tier: "market_data" }); } catch (e) {}
      setUserMvpTier("market_data");
      setScreen("main");
    }} onPortfolioLoaded={(data) => {
      const dataWithTimestamp = { ...data, savedAt: Date.now(), persistentConnection: false };
      localStorage.setItem("portfolioData", JSON.stringify(dataWithTimestamp));
      setPortfolioData(dataWithTimestamp);
      setPortfolioValue(data.net_liquidation);
      setScreen("main");
    }} />;
  }
  if (screen === "tier_select") {
    return <TierSelectScreen onSelect={handleTierSelect} />;
  }
  if (screen === "questionnaire") {
    return <QuestionnaireScreen onComplete={(answers, profile) => {
      setProfileData(profile);
      setScreen("profile_report");
    }} />;
  }
  if (screen === "profile_report") {
    return <ProfileReportScreen profile={profileData} onAccept={() => {
      if (userAccountType === "mvp_beta") {
        setScreen(userMvpTier === "portfolio_optimizer" ? "mvp_upload" : "main");
        return;
      }
      setScreen("disclaimer");
    }} onRetake={() => setScreen("questionnaire")} />;
  }
  if (screen === "disclaimer") {
    return <DisclaimerScreen onAccept={() => {
      localStorage.setItem("disclaimerAccepted", "true");
      if (userTier === "premium" && !localStorage.getItem("connectedBroker")) {
        setScreen("connect_account");
      } else {
        setScreen("main");
      }
    }} />;
  }
  if (screen === "connect_account") {
    return <BrokerConnectScreen
      user={user}
      onConnect={(broker, data) => {
        localStorage.setItem("connectedBroker", broker);
        if (data) {
          const d = { ...data, savedAt: Date.now(), persistentConnection: true };
          localStorage.setItem("portfolioData", JSON.stringify(d));
          setPortfolioData(d);
          if (data.net_liquidation) setPortfolioValue(data.net_liquidation);
        }
        if (broker === "ibkr") setIbkr(true);
        setScreen("main");
      }}
      onSkip={() => {
        // Only mark as skipped if no real connection was made yet; if the user came from header, preserve existing connection
        const existing = localStorage.getItem("connectedBroker");
        const reallyConnected = existing && existing !== "skipped" && existing !== "upload";
        if (!reallyConnected) localStorage.setItem("connectedBroker", "skipped");
        setScreen("main");
      }}
    />;
  }
  // Free tier: skip portfolio input, go straight to market view
  // Premium IBKR-connected: auto-fetch in useEffect above, don't prompt
  const _connectedBroker = localStorage.getItem("connectedBroker");
  // Premium users without portfolio data → BrokerConnectScreen (has IBKR + upload options)
  // No connectedBroker exception here: localStorage from a different account on the same
  // browser can leave a stale "ibkr" value that shouldn't bypass this screen.
  // mvp_beta accounts never see this screen — even one with a stale legacy
  // tier value from before the two-tier launch stays on the new MVP flow.
  if (userAccountType !== "mvp_beta" && userTier === "premium" && !portfolioData) {
    return <BrokerConnectScreen
      user={user}
      onConnect={(broker, data) => {
        localStorage.setItem("connectedBroker", broker);
        if (data) {
          const d = { ...data, savedAt: Date.now(), persistentConnection: true };
          localStorage.setItem("portfolioData", JSON.stringify(d));
          setPortfolioData(d);
          if (data.net_liquidation) setPortfolioValue(data.net_liquidation);
        }
        if (broker === "ibkr") setIbkr(true);
      }}
      onSkip={() => {
        const existing = localStorage.getItem("connectedBroker");
        const reallyConnected = existing && existing !== "skipped" && existing !== "upload";
        if (!reallyConnected) localStorage.setItem("connectedBroker", "skipped");
      }}
    />;
  }
  // Basic users without portfolio → PortfolioInputScreen (mvp_beta accounts are
  // routed through their own mvp_upload screen by resolveScreen instead)
  if (userAccountType !== "mvp_beta" && userTier !== "free" && userTier !== "premium" && !portfolioData && _connectedBroker !== "ibkr") {
    return <PortfolioInputScreen onPortfolioLoaded={async (data) => {
      const dataWithTimestamp = { ...data, savedAt: Date.now(), persistentConnection: false };
      localStorage.setItem("portfolioData", JSON.stringify(dataWithTimestamp));
      setPortfolioData(dataWithTimestamp);
      setPortfolioValue(data.net_liquidation);
      try {
        const r = await axios.post(`${API}/api/investor-profile/drift-check`, { portfolio: data.portfolio || [] });
        if (r.data.drift_detected) setDriftNotice(r.data.message);
      } catch (e) {}
    }} />;
  }

  const tierBadgeColor = { free: "var(--text-muted)", basic: "var(--accent)", premium: "var(--gain)" };

  return (
    <div style={{ background: "var(--bg-primary)", minHeight: "100vh", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      {/* Header */}
      <div style={{ background: "var(--bg-surface)", borderBottom: "1px solid var(--bg-border)", padding: "0 20px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", rowGap: 10, paddingTop: 12, paddingBottom: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <FlintLogo size="md" />
              {userTier && <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: userTier === "premium" ? "var(--gain-bg)" : userTier === "basic" ? "var(--accent-dim)" : "var(--bg-elevated)", color: tierBadgeColor[userTier], textTransform: "uppercase", letterSpacing: "0.5px" }}>{userTier}</span>}
              {userAccountType === "mvp_beta" && userMvpTier && (
                <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: userMvpTier === "portfolio_optimizer" ? "var(--accent-dim)" : "var(--bg-elevated)", color: userMvpTier === "portfolio_optimizer" ? "var(--accent)" : "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {userMvpTier === "portfolio_optimizer" ? "Portfolio Optimizer" : "Market Data"} · Beta
                </span>
              )}
            </div>
            <AIBadge size="sm" />
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16, rowGap: 8 }}>
            {portfolioData && userTier !== "free" && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
                  {portfolioData.source === "screenshot" ? "📸" : portfolioData.source === "csv" ? "📄" : "✏️"}
                  {" "}Updated {portfolioData.savedAt ? new Date(portfolioData.savedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "recently"}
                </span>
                <button onClick={() => { localStorage.removeItem("portfolioData"); setPortfolioData(null); setIbkr(false); }}
                  style={{ fontSize: 13, padding: "3px 10px", borderRadius: 6, border: "1px solid var(--bg-border)", background: "var(--bg-surface)", cursor: "pointer", color: "var(--text-secondary)" }}>
                  Update portfolio
                </button>
              </div>
            )}
            {userTier === "premium" && (() => {
              const cb = localStorage.getItem("connectedBroker");
              const isReallyConnected = cb && cb !== "skipped" && cb !== "upload";
              const label = isReallyConnected
                ? (cb === "ibkr" ? (ibkr ? "IBKR live" : "IBKR offline") : `${cb} connected`)
                : "Connect broker";
              const color = isReallyConnected && ibkr ? "var(--gain)" : isReallyConnected ? "var(--text-secondary)" : "var(--amber)";
              const dot = isReallyConnected && ibkr ? "var(--gain)" : isReallyConnected ? "var(--loss)" : "var(--amber)";
              return (
                <button onClick={() => setScreen("connect_account")}
                  style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, padding: "4px 10px", borderRadius: 6, border: `1px solid ${color}`, background: isReallyConnected ? "var(--bg-surface)" : "rgba(245,158,11,0.08)", cursor: "pointer", color, fontWeight: isReallyConnected ? 400 : 600 }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: dot, display: "inline-block" }} />
                  {label}
                </button>
              );
            })()}
            {userTier !== "premium" && portfolioData && (
              <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "var(--text-muted)" }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: ibkr ? "var(--gain)" : "var(--loss)", display: "inline-block" }} />
                {ibkr ? "Portfolio connected" : "Portfolio loaded"}
              </div>
            )}
            {featureFlags.sentinel_alerts && (
              <>
                <WatchlistPanel tickers={watchlistTickers} onAdd={handleAddWatchlistTicker} onRemove={handleRemoveWatchlistTicker} />
                <NotificationBell alerts={alerts} unreadCount={unreadCount} onOpen={refreshAlerts} onMarkRead={handleMarkAlertRead} />
              </>
            )}
            {featureFlags.daily_digest && (
              <>
                <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "var(--text-muted)", cursor: "pointer" }}>
                  <input type="checkbox" checked={digestEmailEnabled} onChange={handleToggleDigestEmail} />
                  Email digest
                </label>
                <button onClick={handleSendTestDigest} disabled={sendingTestDigest}
                  style={{ fontSize: 12, padding: "3px 8px", borderRadius: 6, border: "1px solid var(--bg-border)", background: "var(--bg-surface)", cursor: sendingTestDigest ? "default" : "pointer", color: "var(--text-muted)" }}>
                  {sendingTestDigest ? "Sending…" : "Send test"}
                </button>
              </>
            )}
            {user && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{user.name || user.email}</span>
                <button onClick={() => { setUserTier(null); localStorage.removeItem("authToken"); localStorage.removeItem("portfolioData"); setUser(null); setPortfolioData(null); setScreen("auth"); }}
                  style={{ fontSize: 13, padding: "3px 8px", borderRadius: 6, border: "1px solid var(--bg-border)", background: "var(--bg-surface)", cursor: "pointer", color: "var(--text-muted)" }}>
                  Sign out
                </button>
                <button onClick={() => setShowDeleteAccountModal(true)}
                  style={{ fontSize: 13, padding: "3px 8px", borderRadius: 6, border: "none", background: "transparent", cursor: "pointer", color: "var(--text-muted)", textDecoration: "underline" }}>
                  Delete account
                </button>
              </div>
            )}
            <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{now.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {userTier === "free" ? (
            <>
              <button style={tabStyle("optimization")} onClick={() => setTab("optimization")}>Market</button>
              <button style={tabStyle("optimizer_locked")} onClick={() => setTab("optimizer_locked")}>Portfolio Optimizer</button>
              <button style={tabStyle("performance_locked")} onClick={() => setTab("performance_locked")}>Performance</button>
            </>
          ) : (
            <>
              <button style={tabStyle("market_flint")} onClick={() => setTab("market_flint")}>Market Outlook</button>
              <button style={tabStyle("optimization")} onClick={() => setTab("optimization")}>Portfolio Optimizer</button>
              <button style={tabStyle("performance")} onClick={() => { setTab("performance"); if (!performance) fetchPerformance(); }}>Performance</button>
            </>
          )}
        </div>
      </div>

      {driftNotice && (
        <div style={{ background: "var(--warning-bg)", borderBottom: "1px solid var(--bg-border)", padding: "10px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 15 }}>
          <span style={{ color: "var(--warning)" }}>{driftNotice}</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => { setDriftNotice(null); setScreen("questionnaire"); }}
              style={{ fontSize: 14, padding: "4px 10px", borderRadius: 6, border: "1px solid var(--bg-border)", background: "var(--bg-surface)", cursor: "pointer", color: "var(--warning)", fontWeight: 500 }}>Review now</button>
            <button onClick={() => setDriftNotice(null)}
              style={{ fontSize: 14, padding: "4px 10px", borderRadius: 6, border: "none", background: "transparent", cursor: "pointer", color: "var(--text-muted)" }}>Dismiss</button>
          </div>
        </div>
      )}

      <div style={{ padding: 16 }}>
        {/* Stats row — only for basic/premium with portfolio data */}
        {userTier !== "free" && portfolioData && (
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <div style={{ background: "var(--bg-surface)", borderRadius: 8, padding: 12, flex: 1 }}>
              <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 4 }}>Total value</div>
              <div style={{ fontSize: 22, fontWeight: 500 }}>{portfolioValue ? "$" + (portfolioValue + (portfolioData?.available_cash || 0)).toLocaleString("en-US", { maximumFractionDigits: 0 }) : "—"}</div>
              <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4, display: "flex", flexDirection: "column", gap: 1 }}>
                {portfolioData?.available_cash > 0 ? (
                  <><span>Invested: ${Number(portfolioValue || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}</span><span style={{ color: "var(--accent)" }}>+ Cash: ${Number(portfolioData.available_cash).toLocaleString("en-US", { maximumFractionDigits: 0 })}</span></>
                ) : <span>in positions</span>}
              </div>
            </div>
            <div style={{ background: "var(--bg-surface)", borderRadius: 8, padding: 12, flex: 1, position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
                <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Balance score</div>
                <div style={{ position: "relative", display: "inline-block" }}
                  onMouseEnter={e => e.currentTarget.querySelector(".tooltip").style.display = "block"}
                  onMouseLeave={e => e.currentTarget.querySelector(".tooltip").style.display = "none"}>
                  <span style={{ fontSize: 12, color: "var(--text-muted)", cursor: "help", background: "var(--bg-border)", borderRadius: "50%", width: 14, height: 14, display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 600 }}>?</span>
                  <div className="tooltip" style={{ display: "none", position: "absolute", bottom: "120%", left: "50%", transform: "translateX(-50%)", background: "var(--bg-elevated)", color: "var(--text-primary)", fontSize: 13, padding: "8px 12px", borderRadius: 8, border: "1px solid var(--bg-border)", width: 220, lineHeight: 1.5, zIndex: 100, whiteSpace: "normal" }}>
                    Measures how well your portfolio is spread across different sectors and asset classes. 100 = perfectly diversified across all target sectors.
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 22, fontWeight: 500, color: diversificationScore >= 70 ? "var(--gain)" : diversificationScore >= 50 ? "var(--warning)" : diversificationScore !== null ? "var(--loss)" : "#888" }}>
                {diversificationScore !== null && diversificationScore !== undefined ? `${diversificationScore} / 100` : "— / 100"}
              </div>
              <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2 }}>{diversificationScore >= 70 ? "well balanced" : diversificationScore >= 50 ? "moderately balanced" : diversificationScore !== null ? "room to improve" : "run analysis first"}</div>
            </div>
            <div style={{ background: "var(--bg-surface)", borderRadius: 8, padding: 12, flex: 1 }}>
              <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 4 }}>Deployable capital</div>
              {(() => {
                const p = strategy?.recommended_portfolio?.performance_comparison;
                const freed = p?.capital_freed || 0;
                const cash = p?.available_cash || portfolioData?.available_cash || 0;
                const total = freed + cash;
                return (
                  <>
                    <div style={{ fontSize: 22, fontWeight: 500, color: total > 0 ? "var(--accent)" : "#888" }}>{total > 0 ? "$" + Number(total).toLocaleString("en-US", { maximumFractionDigits: 0 }) : "—"}</div>
                    <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4, display: "flex", flexDirection: "column", gap: 1 }}>
                      {freed > 0 && <span>Freed: ${Number(freed).toLocaleString("en-US", { maximumFractionDigits: 0 })}</span>}
                      {cash > 0 && <span style={{ color: "var(--accent)" }}>Cash: ${Number(cash).toLocaleString("en-US", { maximumFractionDigits: 0 })}</span>}
                      {total === 0 && <span>run analysis first</span>}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Tab content */}
        {userTier === "free" && tab === "optimizer_locked" ? (
          <UpgradeLockedPanel
            headline="See exactly what to buy and sell — personalized to your portfolio"
            body="Cue's AI analyzes your actual holdings against 13 live data sources and tells you exactly what to trim, what to add, and why — with dollar-impact projections, not generic picks."
            bullets={["Personalized buy/sell recommendations, not generic picks", "Dollar-impact projections for every trade", "One-click push to your broker (Pro)"]}
            onUpgrade={() => setScreen("tier_select")}
          />
        ) : userTier === "free" && tab === "performance_locked" ? (
          <UpgradeLockedPanel
            headline="See exactly how much smarter investing is paying off"
            body="Track position-by-position P&L, momentum trends, and the real dollar difference between your current portfolio and Cue's optimized version."
            bullets={["Position-by-position P&L and momentum", "Projected return improvement from optimization", "Balance score tracking over time"]}
            onUpgrade={() => setScreen("tier_select")}
          />
        ) : userTier === "free" ? (
          <>
            <MarketTab data={recommendations} loading={recommendationsLoading} onRefresh={fetchOptimization} hasPortfolioCache={false} onBuy={null} />
            <div style={{ marginTop: 20, background: "var(--accent-glow)", borderRadius: 12, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>Want tailored recommendations?</div>
                <div style={{ fontSize: 14, color: "var(--text-muted)" }}>Upgrade to Plus to analyze your own portfolio, not just the general market.</div>
              </div>
              <button onClick={() => setScreen("tier_select")} style={{ padding: "9px 18px", borderRadius: 8, border: "none", background: "var(--accent)", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>Upgrade plan</button>
            </div>
          </>
        ) : (
          <>
            {tab === "market_flint" && (
              <MarketFlintTab
                featureFlags={featureFlags}
                flintPick={flintPick}
                digestContent={digestContent}
                onTestInOptimizer={() => { setTab("optimization"); axios.post(`${API}/api/analytics/event`, { event_name: "flint_test_in_optimizer_clicked" }).catch(() => {}); }}
                onFlintExpand={() => axios.post(`${API}/api/analytics/event`, { event_name: "flint_card_expanded" }).catch(() => {})}
              />
            )}
            {tab === "optimization" && (
              <OptimizationTab
                strategy={strategy}
                recommendations={recommendations}
                loading={recommendationsLoading || strategyLoading}
                loadingStage={loadingStage}
                onFetch={fetchOptimization}
                onExecuteAll={userTier === "premium" ? () => setShowExecuteModal(true) : null}
                portfolioData={portfolioData}
                error={optimizationError}
              />
            )}
            {tab === "performance" && <PerformanceTab performance={performance} loading={performanceLoading} onFetch={fetchPerformance} strategy={strategy} portfolioData={portfolioData} />}
            {userTier === "basic" && tab === "optimization" && strategy && (
              <div style={{ marginTop: 16, background: "var(--gain-bg)", borderRadius: 12, padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 600, color: "var(--gain)", marginBottom: 2 }}>Ready to execute?</div>
                  <div style={{ fontSize: 14, color: "var(--text-muted)" }}>Upgrade to Premium to connect IBKR and execute your optimization in one click.</div>
                </div>
                <button onClick={() => setScreen("tier_select")} style={{ padding: "9px 18px", borderRadius: 8, border: "none", background: "var(--gain)", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>Upgrade to Premium</button>
              </div>
            )}
          </>
        )}
      </div>

      {orderResult && (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 3000, padding: "12px 20px", borderRadius: 10, background: orderResult.success ? "var(--gain-bg)" : "var(--loss-bg)", color: orderResult.success ? "var(--gain)" : "var(--loss)", fontSize: 15, fontWeight: 500, boxShadow: "0 4px 16px rgba(0,0,0,0.15)" }}>
          {orderResult.success ? "✓" : "✗"} {orderResult.message}
        </div>
      )}

      <OrderModal order={pendingOrder} onConfirm={confirmOrder} onCancel={() => { setPendingOrder(null); setOrderInstruction(null); }} placing={placingOrder} instruction={orderInstruction} />

      {showExecuteModal && strategy && (
        <ExecuteOptimizationModal
          strategy={strategy}
          onClose={() => setShowExecuteModal(false)}
          onComplete={() => { setShowExecuteModal(false); fetchOptimization(); }}
        />
      )}

      {showDeleteAccountModal && (
        <DeleteAccountModal
          onClose={() => setShowDeleteAccountModal(false)}
          onDeleted={() => {
            setShowDeleteAccountModal(false);
            setUserTier(null);
            localStorage.removeItem("authToken");
            localStorage.removeItem("portfolioData");
            setUser(null);
            setPortfolioData(null);
            setScreen("auth");
          }}
        />
      )}

      {showIbkrConnect && (
        <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <IbkrConnectModal
            onConnect={(connected) => { setIbkr(!!connected); setShowIbkrConnect(false); }}
            onClose={() => setShowIbkrConnect(false)}
          />
        </div>
      )}

      {showFeatureOnboarding && <FeatureOnboardingModal onDismiss={dismissFeatureOnboarding} />}

      <AIChat portfolioData={strategy} recommendationsData={recommendations} strategyData={strategy} performanceData={performance} />
    </div>
  );
}